/* EpcLoginScene — Sorigin renewable-operations cinematic hero.
 *
 * ONE rich scene rendered on warm cream canvas. All infrastructure elements
 * visible simultaneously and animated: detailed wind turbines (rotor +
 * spinner + nacelle + anemometer + wind vane + aviation light + tapered
 * segmented tower + concrete foundation), detailed solar array (dark
 * mono-cell modules with busbars + frame + junction box + gleam sweep +
 * post-and-footing ground rack), detailed
 * tower crane (lattice mast + operator cab + jib + counter-jib + pulley
 * block + outriggers), detailed substation (3-phase transformer with
 * bushings + cooling fins + conservator + lightning arresters + fence),
 * power line poles with polymer insulators and bird diverters, GPS site
 * beacon on a hill, distant wind turbines, mountain layers, drifting clouds,
 * flying birds, distant city skyline with twinkling lights, plants, grass.
 *
 * Pure CSS animation. Respects prefers-reduced-motion.
 */
import './EpcLoginScene.css';
import type { ReactNode } from 'react';
import {
  BRAND_GREEN,
  CHAMPAGNE,
  RACING_GREEN,
  BOTTLE_GREEN,
  IVORY_PAPER,
  ESPRESSO_INK,
  CANDLELIGHT,
  SLATE_DARK,
  SLATE_MID,
  SLATE_LIGHT,
  SLATE_PANEL,
  GROUND_TAUPE,
  GROUND_SHADOW,
  HILL_SAGE,
  HILL_SAGE_DEEP,
  HILL_SAGE_SHADOW,
  MOUNTAIN_DISTANT,
  MOUNTAIN_MID,
  HI_VIS_ORANGE,
  RED_AVIATION,
  YELLOW_WARNING,
  SKIN_LIGHT,
  SKIN_MID,
  SKIN_DEEP,
  INK_DEEP,
  INK_MID,
  VIEW_W,
  VIEW_H,
  HORIZON,
} from './epcLoginPalette';
import { guilloche } from './epcParametricCurves';
import { useAuthSceneStore } from './authSceneStore';

interface EpcLoginSceneProps {
  className?: string;
}

// Aliases preserved for in-file references (Sovereign Emerald migration)
const BRAND_LIME = CHAMPAGNE;
const BRAND_GREEN_DEEP = RACING_GREEN;
const BRAND_GREEN_MID = BOTTLE_GREEN;
const BRAND_TEAL = BRAND_GREEN;
const BRAND_TEAL_DEEP = BOTTLE_GREEN;
const BRAND_LIME_DEEP = CHAMPAGNE;
const CREAM_BG = IVORY_PAPER;
const CREAM_WARM = IVORY_PAPER;
const CREAM_LIGHT = CANDLELIGHT;
const TOWER_WHITE = IVORY_PAPER;
const TOWER_TINT = IVORY_PAPER;
const TOWER_SHADE = GROUND_SHADOW;
const PANEL_GRID = SLATE_MID;
const SUN_ORANGE = CHAMPAGNE;
const HAT_ORANGE_DEEP = HI_VIS_ORANGE;

// ============================================================================
// DETAILED WIND TURBINE
// ============================================================================

interface TurbineProps {
  baseX: number;
  hubY: number;
  scale?: number;
  spinClass?: string;
  opacity?: number;
  tone?: string;
  shade?: string;
  detailLevel?: 'full' | 'medium' | 'simple';
}

/** Accurate 3-blade rotor star, hub-centred at (0,0), one blade pointing up.
 *  Real blade anatomy in profile: a narrow cylindrical ROOT, the max chord at
 *  a SHOULDER ~26% of span, then a smooth taper to a fine, slightly swept
 *  TIP. The profile is asymmetric: the LEADING edge (right side — the
 *  direction of travel for the scene's shared clockwise spin) runs almost
 *  straight, while the TRAILING edge (left) carries the curved chord bulge.
 *  Details: trailing-edge engraved shade, leading-edge highlight, spar line,
 *  red obstruction TIP CAP + inner band (standard aviation marking), and a
 *  slate pitch-bearing flange at the root. Everything is proportional to the
 *  blade length B, so all rotors in the scene share identical geometry. */
function RotorBlades({
  len: B,
  tone = TOWER_WHITE,
  shade = TOWER_SHADE,
  detail = true,
}: {
  len: number;
  tone?: string;
  shade?: string;
  detail?: boolean;
}) {
  const body = `M ${0.03 * B} ${-0.024 * B}
    C ${0.052 * B} ${-0.18 * B}, ${0.039 * B} ${-0.55 * B}, ${0.01 * B} ${-0.965 * B}
    Q ${0.004 * B} ${-0.995 * B}, ${-0.001 * B} ${-0.985 * B}
    C ${-0.03 * B} ${-0.88 * B}, ${-0.073 * B} ${-0.48 * B}, ${-0.078 * B} ${-0.26 * B}
    C ${-0.075 * B} ${-0.13 * B}, ${-0.048 * B} ${-0.045 * B}, ${-0.03 * B} ${-0.024 * B}
    Z`;
  const trailShade = `M ${-0.078 * B} ${-0.26 * B}
    C ${-0.073 * B} ${-0.48 * B}, ${-0.03 * B} ${-0.88 * B}, ${-0.001 * B} ${-0.985 * B}
    C ${-0.024 * B} ${-0.86 * B}, ${-0.06 * B} ${-0.47 * B}, ${-0.064 * B} ${-0.26 * B}
    C ${-0.062 * B} ${-0.14 * B}, ${-0.044 * B} ${-0.05 * B}, ${-0.03 * B} ${-0.024 * B}
    C ${-0.048 * B} ${-0.045 * B}, ${-0.075 * B} ${-0.13 * B}, ${-0.078 * B} ${-0.26 * B}
    Z`;
  const tipCap = `M ${0.013 * B} ${-0.94 * B}
    L ${0.01 * B} ${-0.965 * B}
    Q ${0.004 * B} ${-0.995 * B} ${-0.001 * B} ${-0.985 * B}
    L ${-0.012 * B} ${-0.94 * B}
    Q 0 ${-0.952 * B} ${0.013 * B} ${-0.94 * B}
    Z`;
  const tipBand = `M ${0.021 * B} ${-0.78 * B}
    L ${0.018 * B} ${-0.84 * B}
    Q 0 ${-0.855 * B} ${-0.036 * B} ${-0.84 * B}
    L ${-0.043 * B} ${-0.78 * B}
    Q 0 ${-0.795 * B} ${0.021 * B} ${-0.78 * B}
    Z`;
  const highlight = `M ${0.025 * B} ${-0.08 * B}
    C ${0.045 * B} ${-0.2 * B}, ${0.033 * B} ${-0.55 * B}, ${0.008 * B} ${-0.93 * B}`;
  return (
    <>
      {[0, 120, 240].map((angle) => (
        <g key={`blade-${angle}`} transform={`rotate(${angle})`}>
          <path d={body} fill={tone} stroke={ESPRESSO_INK} strokeOpacity="0.85" strokeWidth={0.008 * B} strokeLinejoin="round" />
          <path d={trailShade} fill={shade} opacity="0.55" />
          {detail && (
            <>
              <path d={highlight} stroke="#ffffff" strokeWidth={0.011 * B} strokeOpacity="0.85" fill="none" strokeLinecap="round" />
              <line x1={-0.006 * B} y1={-0.07 * B} x2={-0.004 * B} y2={-0.75 * B} stroke={ESPRESSO_INK} strokeWidth={0.005 * B} strokeOpacity="0.3" />
            </>
          )}
          <path d={tipCap} fill={RED_AVIATION} fillOpacity="0.92" />
          <path d={tipBand} fill={RED_AVIATION} fillOpacity="0.92" />
          {/* pitch-bearing flange at the root */}
          <ellipse cx="0" cy={-0.02 * B} rx={0.036 * B} ry={0.015 * B} fill={SLATE_DARK} opacity="0.8" />
        </g>
      ))}
    </>
  );
}

function Turbine({
  baseX,
  hubY,
  scale = 1,
  spinClass = 'login-blade-spin',
  opacity = 1,
  tone = TOWER_WHITE,
  shade = TOWER_SHADE,
  detailLevel = 'full',
}: TurbineProps) {
  const s = scale;
  const towerBottom = HORIZON;
  const towerTop = hubY;
  const baseHalf = 7 * s;
  const topHalf = 2.6 * s;
  const nacelleW = 30 * s;
  const nacelleH = 13 * s;
  const hubR = 3 * s;
  const bladeLen = 88 * s;
  const showFull = detailLevel === 'full';
  const showMedium = detailLevel !== 'simple';

  return (
    <g opacity={opacity}>
      {/* Foundation pad + anchor bolts */}
      {showMedium && (
        <g>
          <rect
            x={baseX - baseHalf - 6 * s}
            y={towerBottom - 5 * s}
            width={2 * (baseHalf + 6 * s)}
            height={5 * s}
            rx={1 * s}
            fill={SLATE_LIGHT}
          />
          <rect
            x={baseX - baseHalf - 6 * s}
            y={towerBottom - 7 * s}
            width={2 * (baseHalf + 6 * s)}
            height={2 * s}
            fill={SLATE_MID}
          />
          {showFull &&
            Array.from({ length: 5 }).map((_, i) => (
              <rect
                key={`bolt-${i}`}
                x={baseX - baseHalf - 4 * s + i * (baseHalf * 0.5)}
                y={towerBottom - 7.6 * s}
                width={1.4 * s}
                height={1.4 * s}
                fill={SLATE_DARK}
              />
            ))}
        </g>
      )}

      {/* Tower (tapered, 4 segments) — crisp engraved contour */}
      <polygon
        points={`
          ${baseX - baseHalf},${towerBottom - 7 * s}
          ${baseX + baseHalf},${towerBottom - 7 * s}
          ${baseX + topHalf},${towerTop}
          ${baseX - topHalf},${towerTop}
        `}
        fill={tone}
        stroke={ESPRESSO_INK}
        strokeWidth={0.8 * s}
      />
      {/* Tower shading (right side) */}
      <path
        d={`M ${baseX + topHalf * 0.35} ${towerTop}
            L ${baseX + baseHalf * 0.5} ${towerBottom - 7 * s}
            L ${baseX + baseHalf - 0.5 * s} ${towerBottom - 7 * s}
            L ${baseX + topHalf - 0.2 * s} ${towerTop} Z`}
        fill={shade}
        opacity="0.7"
      />
      {/* Tower segment welding rings */}
      {showMedium && (
        <>
          {[0.25, 0.5, 0.75].map((t, i) => {
            const y = towerBottom - 7 * s + (towerTop - (towerBottom - 7 * s)) * t;
            const half = baseHalf + (topHalf - baseHalf) * t;
            return (
              <g key={`ring-${i}`}>
                <line
                  x1={baseX - half}
                  y1={y}
                  x2={baseX + half}
                  y2={y}
                  stroke={ESPRESSO_INK}
                  strokeOpacity="0.55"
                  strokeWidth={0.6 * s}
                />
                <line
                  x1={baseX - half}
                  y1={y + 0.6 * s}
                  x2={baseX + half}
                  y2={y + 0.6 * s}
                  stroke={shade}
                  strokeWidth={0.4 * s}
                  opacity="0.6"
                />
              </g>
            );
          })}
        </>
      )}
      {/* Service door at base */}
      {showFull && (
        <rect
          x={baseX - 2 * s}
          y={towerBottom - 22 * s}
          width={4 * s}
          height={9 * s}
          rx={0.6 * s}
          fill={SLATE_MID}
          opacity="0.85"
        />
      )}

      {/* Nacelle assembly */}
      <g>
        {/* Yaw ring */}
        {showMedium && (
          <ellipse
            cx={baseX}
            cy={towerTop + nacelleH / 2 - 0.4 * s}
            rx={topHalf + 1.6 * s}
            ry={1.6 * s}
            fill={SLATE_DARK}
          />
        )}
        {/* Nacelle box body (rounded front toward rotor on right) */}
        <path
          d={`M ${baseX - nacelleW / 2} ${towerTop - nacelleH / 2}
              L ${baseX + nacelleW / 2 - 4 * s} ${towerTop - nacelleH / 2}
              Q ${baseX + nacelleW / 2 + 2 * s} ${towerTop - nacelleH / 2 + 1 * s} ${baseX + nacelleW / 2 + 2 * s} ${towerTop}
              Q ${baseX + nacelleW / 2 + 2 * s} ${towerTop + nacelleH / 2 - 1 * s} ${baseX + nacelleW / 2 - 4 * s} ${towerTop + nacelleH / 2}
              L ${baseX - nacelleW / 2} ${towerTop + nacelleH / 2}
              Z`}
          fill={SLATE_DARK}
          stroke={ESPRESSO_INK}
          strokeWidth={0.8 * s}
        />
        {/* Nacelle top highlight */}
        <rect
          x={baseX - nacelleW / 2 + 1 * s}
          y={towerTop - nacelleH / 2 + 1.4 * s}
          width={nacelleW - 4 * s}
          height={1.4 * s}
          fill={SLATE_MID}
        />
        {/* Cooling vents (3 slats) */}
        {showMedium && (
          <g fill={SLATE_PANEL}>
            <rect x={baseX - nacelleW / 2 + 4 * s} y={towerTop - 1 * s} width={1 * s} height={3.6 * s} />
            <rect x={baseX - nacelleW / 2 + 6.4 * s} y={towerTop - 1 * s} width={1 * s} height={3.6 * s} />
            <rect x={baseX - nacelleW / 2 + 8.8 * s} y={towerTop - 1 * s} width={1 * s} height={3.6 * s} />
          </g>
        )}
        {/* Brand accent stripe */}
        <rect
          x={baseX - nacelleW / 2 + 1 * s}
          y={towerTop + 4 * s}
          width={nacelleW - 4 * s}
          height={1.2 * s}
          fill={BRAND_LIME}
          opacity="0.9"
        />

        {/* Anemometer (cup wheel) on top */}
        {showFull && (
          <g transform={`translate(${baseX - 6 * s} ${towerTop - nacelleH / 2 - 6 * s})`}>
            <line x1="0" y1={5.5 * s} x2="0" y2={1 * s} stroke={SLATE_DARK} strokeWidth={0.7 * s} />
            <g className="login-anemometer">
              <circle r={2.2 * s} fill="none" stroke="none" />
              <circle cx={0} cy={-2.4 * s} r={0.9 * s} fill={SLATE_PANEL} />
              <circle cx={2 * s} cy={1 * s} r={0.9 * s} fill={SLATE_PANEL} />
              <circle cx={-2 * s} cy={1 * s} r={0.9 * s} fill={SLATE_PANEL} />
              <circle r={0.5 * s} fill={SLATE_DARK} />
            </g>
          </g>
        )}
        {/* Wind vane on top */}
        {showFull && (
          <g transform={`translate(${baseX + 4 * s} ${towerTop - nacelleH / 2 - 4 * s})`}>
            <line x1="0" y1={4.5 * s} x2="0" y2={0} stroke={SLATE_DARK} strokeWidth={0.7 * s} />
            <g className="login-vane">
              <rect x={-3 * s} y={-1.8 * s} width={0.8 * s} height={0.8 * s} fill="none" stroke="none" />
              <path d={`M 0 0 L ${4 * s} ${-1.2 * s} L ${4 * s} ${1.2 * s} Z`} fill={SLATE_PANEL} />
              <line x1="0" y1="0" x2={-2.6 * s} y2={0} stroke={SLATE_DARK} strokeWidth={0.6 * s} />
            </g>
          </g>
        )}
        {/* Aviation warning light */}
        {showFull && (
          <g>
            <rect
              x={baseX - 1 * s}
              y={towerTop - nacelleH / 2 - 2.2 * s}
              width={2 * s}
              height={1.5 * s}
              fill={SLATE_DARK}
            />
            <circle
              cx={baseX}
              cy={towerTop - nacelleH / 2 - 2.5 * s}
              r={1.4 * s}
              fill={RED_AVIATION}
              className="login-aviation-light"
            />
          </g>
        )}
      </g>

      {/* Rotor: spinner cap + 3 accurate blades — rotates as a single group */}
      <g transform={`translate(${baseX + nacelleW / 2 + 3 * s} ${towerTop})`}>
        <g className={spinClass}>
          {/* Invisible bbox balancer for stable rotation pivot */}
          <circle r={bladeLen * 0.95} fill="none" stroke="none" pointerEvents="none" />
          <RotorBlades len={bladeLen} tone={tone} shade={shade} detail={showMedium} />
          {/* Spinner cone (rounded hub cap) */}
          <circle r={hubR + 1.6 * s} fill={SLATE_PANEL} stroke={ESPRESSO_INK} strokeWidth={0.7 * s} />
          <circle r={hubR * 0.55} fill={BRAND_LIME} stroke={ESPRESSO_INK} strokeWidth={0.4 * s} />
        </g>
      </g>
    </g>
  );
}

// ============================================================================
// DETAILED SOLAR PANEL
// ============================================================================

interface SolarPanelProps {
  cx: number;
  cy: number;
  width: number;
  height: number;
  tilt?: number;
  gleamClass?: string;
}

function SolarPanel({
  cx,
  cy,
  width,
  height,
  tilt = -16,
  gleamClass = 'login-gleam',
}: SolarPanelProps) {
  // Pseudo-3D: the module leans back, so the top edge shifts away AND
  // foreshortens (slightly narrower) — a trapezoid, not a parallelogram.
  const shift = (height / 2) * Math.tan((tilt * Math.PI) / 180);
  const yTop = cy - height / 2;
  const yBot = cy + height / 2;
  const topW = width * 0.9;
  const faceTL = { x: cx + shift - topW / 2, y: yTop };
  const faceTR = { x: cx + shift + topW / 2, y: yTop };
  const faceBL = { x: cx - shift - width / 2, y: yBot };
  const faceBR = { x: cx - shift + width / 2, y: yBot };
  const points = `${faceBL.x},${faceBL.y} ${faceBR.x},${faceBR.y} ${faceTR.x},${faceTR.y} ${faceTL.x},${faceTL.y}`;
  // Bilinear point on the face: u 0→1 left→right, v 0→1 top→bottom
  const facePt = (u: number, v: number) => {
    const xT = faceTL.x + (faceTR.x - faceTL.x) * u;
    const xB = faceBL.x + (faceBR.x - faceBL.x) * u;
    return { x: xT + (xB - xT) * v, y: yTop + (yBot - yTop) * v };
  };
  const quad = (u0: number, v0: number, u1: number, v1: number) => {
    const a = facePt(u0, v0);
    const b = facePt(u1, v0);
    const c = facePt(u1, v1);
    const d = facePt(u0, v1);
    return `${a.x.toFixed(1)},${a.y.toFixed(1)} ${b.x.toFixed(1)},${b.y.toFixed(1)} ${c.x.toFixed(1)},${c.y.toFixed(1)} ${d.x.toFixed(1)},${d.y.toFixed(1)}`;
  };
  const cols = 6;
  const rows = 4;
  const fU = 0.045; // frame border as fraction of width
  const fV = 0.085; // frame border as fraction of height
  const gU = 0.011; // half-gap between cells (white backsheet showing)
  const gV = 0.022;
  const clipId = `login-panel-clip-${cx}-${cy}`;
  const frameDepth = 2.4;
  const groundY = yBot + 16;
  const legs = [0.2, 0.8].map((u) => ({ topX: faceBL.x + (faceBR.x - faceBL.x) * u, u }));

  return (
    <g>
      <defs>
        <clipPath id={clipId}>
          <polygon points={points} />
        </clipPath>
      </defs>

      {/* Mounting rack BEHIND the module: galvanized posts on concrete
          footings + cross member + diagonal kicker brace */}
      <g stroke={ESPRESSO_INK} strokeLinecap="round">
        {legs.map((leg) => (
          <line key={`post-${leg.u}`} x1={leg.topX} y1={yBot} x2={leg.topX} y2={groundY} strokeWidth="1.7" />
        ))}
        {/* cross member tying the posts */}
        <line x1={legs[0].topX} y1={yBot + 8.5} x2={legs[1].topX} y2={yBot + 8.5} strokeWidth="0.8" />
        {/* diagonal kicker brace */}
        <line x1={legs[0].topX} y1={groundY - 1} x2={legs[1].topX} y2={yBot + 3.5} strokeWidth="0.6" strokeOpacity="0.8" />
      </g>
      {/* concrete footings */}
      {legs.map((leg) => (
        <rect
          key={`foot-${leg.u}`}
          x={leg.topX - 2.8}
          y={groundY - 1.2}
          width={5.6}
          height={3}
          rx={0.6}
          fill={GROUND_SHADOW}
          stroke={ESPRESSO_INK}
          strokeWidth="0.55"
        />
      ))}

      {/* Frame side profile — extruded thickness under the bottom rail */}
      <polygon
        points={`${faceBL.x},${faceBL.y} ${faceBR.x},${faceBR.y} ${faceBR.x + 0.6},${faceBR.y + frameDepth} ${faceBL.x + 0.6},${faceBL.y + frameDepth}`}
        fill="url(#intaglio-h)"
        stroke={ESPRESSO_INK}
        strokeWidth="0.7"
      />

      {/* Aluminium frame face — the ivory border around the laminate */}
      <polygon points={points} fill={IVORY_PAPER} stroke={ESPRESSO_INK} strokeWidth="1" />

      {/* Module clamps — champagne fittings where the rail grips the frame */}
      {legs.map((leg) => (
        <rect
          key={`clamp-${leg.u}`}
          x={leg.topX - 1.3}
          y={yBot - 1}
          width={2.6}
          height={2}
          rx={0.4}
          fill={CHAMPAGNE}
          stroke={ESPRESSO_INK}
          strokeWidth="0.45"
          vectorEffect="non-scaling-stroke"
        />
      ))}

      {/* Laminate: white backsheet visible in the gaps between dark cells */}
      <polygon points={quad(fU, fV, 1 - fU, 1 - fV)} fill={CANDLELIGHT} stroke={ESPRESSO_INK} strokeWidth="0.45" strokeOpacity="0.7" />

      {/* Mono-crystalline cell matrix — dark cells, each with twin busbars */}
      {Array.from({ length: rows }).map((_, r) =>
        Array.from({ length: cols }).map((_, c) => {
          const u0 = fU + (1 - 2 * fU) * (c / cols) + gU;
          const u1 = fU + (1 - 2 * fU) * ((c + 1) / cols) - gU;
          const v0 = fV + (1 - 2 * fV) * (r / rows) + gV;
          const v1 = fV + (1 - 2 * fV) * ((r + 1) / rows) - gV;
          return (
            <g key={`cell-${r}-${c}`}>
              <polygon points={quad(u0, v0, u1, v1)} fill="url(#intaglio-cell)" stroke={RACING_GREEN} strokeWidth="0.35" strokeOpacity="0.7" />
              {/* twin vertical busbars per cell — fine metallic conductors */}
              {[1 / 3, 2 / 3].map((f) => {
                const bu = u0 + (u1 - u0) * f;
                const p0 = facePt(bu, v0 + 0.008);
                const p1 = facePt(bu, v1 - 0.008);
                return (
                  <line
                    key={`bb-${f}`}
                    x1={p0.x}
                    y1={p0.y}
                    x2={p1.x}
                    y2={p1.y}
                    stroke={CHAMPAGNE}
                    strokeOpacity="0.75"
                    strokeWidth="0.35"
                  />
                );
              })}
            </g>
          );
        }),
      )}

      {/* Anti-reflective glass sheen + gleam sweep across the laminate */}
      <g clipPath={`url(#${clipId})`}>
        <polygon points={quad(fU, fV, 0.4, 1 - fV)} fill={CANDLELIGHT} opacity="0.08" />
        <rect
          className={gleamClass}
          x={faceBL.x - width * 0.3}
          y={yTop - 6}
          width={width * 0.35}
          height={height + 12}
          fill="url(#login-gleam-grad)"
          transform="skewX(-22)"
        />
      </g>

      {/* Junction box on the top (rear) edge + MC4 leads dropping behind */}
      <rect
        x={faceTR.x - 13}
        y={faceTR.y - 2.6}
        width={9}
        height={4.6}
        rx={1}
        fill={IVORY_PAPER}
        stroke={ESPRESSO_INK}
        strokeWidth="0.6"
      />
      <path
        d={`M ${faceTR.x - 10.5} ${faceTR.y + 2}
            Q ${faceTR.x - 6} ${faceTR.y + 9} ${faceTR.x + 2} ${faceTR.y + 15}`}
        stroke={ESPRESSO_INK}
        strokeOpacity="0.8"
        strokeWidth="0.7"
        fill="none"
      />
      <path
        d={`M ${faceTR.x - 6.5} ${faceTR.y + 2}
            Q ${faceTR.x - 2} ${faceTR.y + 9} ${faceTR.x + 6} ${faceTR.y + 15}`}
        stroke={ESPRESSO_INK}
        strokeOpacity="0.8"
        strokeWidth="0.7"
        fill="none"
      />

      {/* Ground contact + shadow */}
      <ellipse cx={cx} cy={groundY + 2.5} rx={width * 0.45} ry={3.2} fill={GROUND_SHADOW} opacity="0.55" />
    </g>
  );
}

// ============================================================================
// DETAILED TOWER CRANE
// ============================================================================

interface CraneProps {
  baseX: number;
  groundY: number;
  scale?: number;
}

function Crane({ baseX, groundY, scale = 1 }: CraneProps) {
  const s = scale;
  const mastTopY = groundY - 280 * s;
  const jibY = mastTopY + 14 * s;
  const jibLeftX = baseX - 220 * s;
  const counterJibRightX = baseX + 84 * s;
  const hookCableX = baseX - 120 * s;
  const hookY = jibY + 100 * s;

  return (
    <g>
      {/* Concrete base pad */}
      <rect
        x={baseX - 26 * s}
        y={groundY - 8 * s}
        width={52 * s}
        height={10 * s}
        rx={1 * s}
        fill={SLATE_LIGHT}
      />
      <line
        x1={baseX - 26 * s}
        y1={groundY - 5 * s}
        x2={baseX + 26 * s}
        y2={groundY - 5 * s}
        stroke={SLATE_MID}
        strokeWidth="0.7"
      />

      {/* Outrigger pads */}
      <g fill={SLATE_DARK}>
        <rect x={baseX - 40 * s} y={groundY - 4 * s} width={10 * s} height={4 * s} rx={1 * s} />
        <rect x={baseX + 30 * s} y={groundY - 4 * s} width={10 * s} height={4 * s} rx={1 * s} />
      </g>

      {/* Tower mast — lattice with X braces (slight taper) */}
      <g>
        {/* Outer vertical edges */}
        <line x1={baseX - 11 * s} y1={groundY - 8 * s} x2={baseX - 9 * s} y2={mastTopY} stroke={SUN_ORANGE} strokeWidth={3 * s} strokeLinecap="round" />
        <line x1={baseX + 11 * s} y1={groundY - 8 * s} x2={baseX + 9 * s} y2={mastTopY} stroke={SUN_ORANGE} strokeWidth={3 * s} strokeLinecap="round" />
        {/* Horizontal rungs */}
        {Array.from({ length: 13 }).map((_, i) => {
          const t = i / 12;
          const y = groundY - 8 * s + ((mastTopY) - (groundY - 8 * s)) * t;
          const halfW = 11 * s - 2 * s * t;
          return (
            <line
              key={`rung-${i}`}
              x1={baseX - halfW}
              y1={y}
              x2={baseX + halfW}
              y2={y}
              stroke={SUN_ORANGE}
              strokeWidth={1.6 * s}
            />
          );
        })}
        {/* X-braces */}
        {Array.from({ length: 12 }).map((_, i) => {
          const t1 = i / 12;
          const t2 = (i + 1) / 12;
          const y1 = groundY - 8 * s + ((mastTopY) - (groundY - 8 * s)) * t1;
          const y2 = groundY - 8 * s + ((mastTopY) - (groundY - 8 * s)) * t2;
          const half1 = 11 * s - 2 * s * t1;
          const half2 = 11 * s - 2 * s * t2;
          return (
            <g key={`x-${i}`} stroke={SLATE_DARK} strokeWidth={0.9 * s}>
              <line x1={baseX - half1} y1={y1} x2={baseX + half2} y2={y2} />
              <line x1={baseX + half1} y1={y1} x2={baseX - half2} y2={y2} />
            </g>
          );
        })}
      </g>

      {/* Slewing platform (rotates the upper section) */}
      <g>
        <rect
          x={baseX - 14 * s}
          y={mastTopY - 6 * s}
          width={28 * s}
          height={8 * s}
          rx={1 * s}
          fill={SLATE_DARK}
        />
        <ellipse cx={baseX} cy={mastTopY - 6 * s} rx={14 * s} ry={2 * s} fill={SLATE_MID} />
      </g>

      {/* Operator cab */}
      <g>
        <rect
          x={baseX - 16 * s}
          y={mastTopY - 26 * s}
          width={28 * s}
          height={20 * s}
          rx={2 * s}
          fill={SLATE_DARK}
        />
        {/* Windows */}
        <rect x={baseX - 14 * s} y={mastTopY - 24 * s} width={10 * s} height={9 * s} fill={BRAND_TEAL} opacity="0.7" />
        <rect x={baseX - 3 * s} y={mastTopY - 24 * s} width={6 * s} height={9 * s} fill={BRAND_TEAL} opacity="0.7" />
        <rect x={baseX + 4 * s} y={mastTopY - 24 * s} width={6 * s} height={9 * s} fill={BRAND_TEAL} opacity="0.7" />
        {/* Door */}
        <rect x={baseX + 8 * s} y={mastTopY - 13 * s} width={3 * s} height={6 * s} fill={SLATE_PANEL} />
        {/* Light */}
        <circle cx={baseX + 11 * s} cy={mastTopY - 22 * s} r={1 * s} fill={YELLOW_WARNING} className="login-pulse-fast" />
      </g>

      {/* Main jib — long lattice arm to the left */}
      <g>
        {/* Top + bottom chords */}
        <line x1={jibLeftX} y1={jibY - 4 * s} x2={baseX - 10 * s} y2={jibY - 4 * s} stroke={SUN_ORANGE} strokeWidth={2.4 * s} strokeLinecap="round" />
        <line x1={jibLeftX} y1={jibY + 8 * s} x2={baseX - 10 * s} y2={jibY + 8 * s} stroke={SUN_ORANGE} strokeWidth={2.4 * s} strokeLinecap="round" />
        {/* Lattice X-braces */}
        {Array.from({ length: 13 }).map((_, i) => {
          const t1 = i / 13;
          const t2 = (i + 1) / 13;
          const xA = jibLeftX + (baseX - 10 * s - jibLeftX) * t1;
          const xB = jibLeftX + (baseX - 10 * s - jibLeftX) * t2;
          return (
            <g key={`jib-x-${i}`} stroke={SLATE_DARK} strokeWidth={0.7 * s}>
              <line x1={xA} y1={jibY - 4 * s} x2={xB} y2={jibY + 8 * s} />
              <line x1={xB} y1={jibY - 4 * s} x2={xA} y2={jibY + 8 * s} />
            </g>
          );
        })}
        {/* Trolley running on jib */}
        <rect x={hookCableX - 5 * s} y={jibY + 2 * s} width={10 * s} height={6 * s} rx={1 * s} fill={SLATE_DARK} />
        <circle cx={hookCableX - 3 * s} cy={jibY + 8 * s} r={1 * s} fill={SLATE_MID} />
        <circle cx={hookCableX + 3 * s} cy={jibY + 8 * s} r={1 * s} fill={SLATE_MID} />
      </g>

      {/* Counter-jib — short arm right side with counterweights */}
      <g>
        <line x1={baseX + 10 * s} y1={jibY - 4 * s} x2={counterJibRightX} y2={jibY - 4 * s} stroke={SUN_ORANGE} strokeWidth={2.2 * s} strokeLinecap="round" />
        <line x1={baseX + 10 * s} y1={jibY + 6 * s} x2={counterJibRightX} y2={jibY + 6 * s} stroke={SUN_ORANGE} strokeWidth={2.2 * s} strokeLinecap="round" />
        {/* Counterweight concrete blocks */}
        <rect x={counterJibRightX - 28 * s} y={jibY + 6 * s} width={28 * s} height={14 * s} rx={1 * s} fill={SLATE_LIGHT} stroke={SLATE_DARK} strokeWidth={0.5 * s} />
        {/* Warning stripes */}
        <rect x={counterJibRightX - 26 * s} y={jibY + 14 * s} width={6 * s} height={3 * s} fill={SUN_ORANGE} />
        <rect x={counterJibRightX - 16 * s} y={jibY + 14 * s} width={6 * s} height={3 * s} fill={SLATE_DARK} />
        <rect x={counterJibRightX - 6 * s} y={jibY + 14 * s} width={6 * s} height={3 * s} fill={SUN_ORANGE} />
      </g>

      {/* A-frame / pendant cables from top of mast to jib tip + counter-jib tip */}
      <g stroke={SLATE_DARK} strokeWidth={0.8 * s} strokeLinecap="round" fill="none">
        <line x1={baseX} y1={mastTopY - 28 * s} x2={jibLeftX + 6 * s} y2={jibY - 4 * s} />
        <line x1={baseX} y1={mastTopY - 28 * s} x2={counterJibRightX - 4 * s} y2={jibY - 4 * s} />
        <line x1={baseX - 4 * s} y1={mastTopY - 28 * s} x2={baseX - 4 * s} y2={mastTopY - 6 * s} />
        <line x1={baseX + 4 * s} y1={mastTopY - 28 * s} x2={baseX + 4 * s} y2={mastTopY - 6 * s} />
      </g>
      {/* Mast tip + aviation light */}
      <rect x={baseX - 1.4 * s} y={mastTopY - 28 * s} width={2.8 * s} height={3 * s} fill={SLATE_DARK} />
      <circle cx={baseX} cy={mastTopY - 30 * s} r={1.4 * s} fill={RED_AVIATION} className="login-aviation-light" />

      {/* Hoist cable down to hook */}
      <line x1={hookCableX} y1={jibY + 8 * s} x2={hookCableX} y2={hookY} stroke={SLATE_DARK} strokeWidth={1 * s} />

      {/* Hook block with pulley sheaves */}
      <g className="login-crane-hook">
        <g transform={`translate(${hookCableX} ${hookY})`}>
          {/* Block housing */}
          <rect x={-9 * s} y={0} width={18 * s} height={11 * s} rx={1.4 * s} fill={SLATE_DARK} />
          {/* Sheaves visible */}
          <circle cx={-5 * s} cy={5.5 * s} r={2 * s} fill={SLATE_MID} stroke={SLATE_LIGHT} strokeWidth={0.4 * s} />
          <circle cx={0} cy={5.5 * s} r={2 * s} fill={SLATE_MID} stroke={SLATE_LIGHT} strokeWidth={0.4 * s} />
          <circle cx={5 * s} cy={5.5 * s} r={2 * s} fill={SLATE_MID} stroke={SLATE_LIGHT} strokeWidth={0.4 * s} />
          {/* Hook */}
          <path
            d={`M 0 ${11 * s}
                L 0 ${17 * s}
                Q 0 ${22 * s} ${5 * s} ${22 * s}
                Q ${7 * s} ${22 * s} ${7 * s} ${20 * s}`}
            stroke={SLATE_DARK}
            strokeWidth={2 * s}
            fill="none"
            strokeLinecap="round"
          />
          {/* Load: turbine blade strapped horizontal */}
          <g transform={`translate(0 ${30 * s})`}>
            <path
              d={`M ${-90 * s} 0
                  Q ${-55 * s} ${-4 * s} 0 ${-4 * s}
                  Q ${55 * s} ${-3 * s} ${90 * s} 0
                  Q ${55 * s} ${5 * s} 0 ${5 * s}
                  Q ${-55 * s} ${5 * s} ${-90 * s} 0 Z`}
              fill={TOWER_WHITE}
              stroke={SLATE_LIGHT}
              strokeWidth={0.6 * s}
            />
            {/* Slings */}
            <rect x={-30 * s} y={-7 * s} width={4 * s} height={14 * s} fill={HAT_ORANGE_DEEP} />
            <rect x={26 * s} y={-7 * s} width={4 * s} height={14 * s} fill={HAT_ORANGE_DEEP} />
            <line x1={-28 * s} y1={-7 * s} x2={hookCableX < baseX ? 0 : 0} y2={-22 * s} stroke={SLATE_DARK} strokeWidth={0.7 * s} transform="translate(0 0)" />
          </g>
        </g>
      </g>
    </g>
  );
}

// ============================================================================
// DETAILED SUBSTATION
// ============================================================================

interface SubstationProps {
  cx: number;
  groundY: number;
  scale?: number;
}

function Substation({ cx, groundY, scale = 1 }: SubstationProps) {
  const s = scale;
  return (
    <g>
      {/* Energy halo */}
      <ellipse
        cx={cx}
        cy={groundY - 40 * s}
        rx={60 * s}
        ry={80 * s}
        fill={BRAND_LIME}
        opacity="0.2"
        className="login-substation-glow"
      />

      {/* Perimeter fence — chain-link rendered with light hatching */}
      <g stroke={SLATE_MID} strokeWidth={0.6 * s}>
        <line x1={cx - 60 * s} y1={groundY - 24 * s} x2={cx + 60 * s} y2={groundY - 24 * s} strokeWidth={1.2 * s} />
        <line x1={cx - 60 * s} y1={groundY - 2 * s} x2={cx + 60 * s} y2={groundY - 2 * s} strokeWidth={1.2 * s} />
        {Array.from({ length: 12 }).map((_, i) => (
          <line
            key={`fp-${i}`}
            x1={cx - 60 * s + i * 10 * s}
            y1={groundY - 2 * s}
            x2={cx - 60 * s + i * 10 * s}
            y2={groundY - 26 * s}
            strokeWidth={1.2 * s}
          />
        ))}
        {/* Chain-link X pattern */}
        {Array.from({ length: 24 }).map((_, i) => (
          <line
            key={`fxA-${i}`}
            x1={cx - 60 * s + i * 5 * s}
            y1={groundY - 24 * s}
            x2={cx - 60 * s + (i + 5) * 5 * s}
            y2={groundY - 4 * s}
            strokeOpacity="0.4"
          />
        ))}
        {Array.from({ length: 24 }).map((_, i) => (
          <line
            key={`fxB-${i}`}
            x1={cx - 60 * s + (i + 5) * 5 * s}
            y1={groundY - 24 * s}
            x2={cx - 60 * s + i * 5 * s}
            y2={groundY - 4 * s}
            strokeOpacity="0.4"
          />
        ))}
        {/* Gate (slight gap) */}
        <line x1={cx + 18 * s} y1={groundY - 26 * s} x2={cx + 18 * s} y2={groundY - 2 * s} stroke={SLATE_DARK} strokeWidth={1.4 * s} />
        <line x1={cx + 30 * s} y1={groundY - 26 * s} x2={cx + 30 * s} y2={groundY - 2 * s} stroke={SLATE_DARK} strokeWidth={1.4 * s} />
      </g>

      {/* Control building behind */}
      <g>
        <rect
          x={cx - 56 * s}
          y={groundY - 76 * s}
          width={40 * s}
          height={52 * s}
          rx={1 * s}
          fill={CREAM_WARM}
          stroke={SLATE_MID}
          strokeWidth={0.6 * s}
        />
        {/* Roof line */}
        <rect x={cx - 56 * s} y={groundY - 76 * s} width={40 * s} height={3 * s} fill={SLATE_MID} />
        {/* Window grid */}
        <g fill={BRAND_TEAL} opacity="0.5">
          <rect x={cx - 52 * s} y={groundY - 68 * s} width={6 * s} height={6 * s} />
          <rect x={cx - 42 * s} y={groundY - 68 * s} width={6 * s} height={6 * s} />
          <rect x={cx - 32 * s} y={groundY - 68 * s} width={6 * s} height={6 * s} />
          <rect x={cx - 52 * s} y={groundY - 58 * s} width={6 * s} height={6 * s} />
          <rect x={cx - 42 * s} y={groundY - 58 * s} width={6 * s} height={6 * s} />
          <rect x={cx - 32 * s} y={groundY - 58 * s} width={6 * s} height={6 * s} />
        </g>
        {/* Door */}
        <rect x={cx - 24 * s} y={groundY - 44 * s} width={6 * s} height={20 * s} fill={SLATE_MID} />
        {/* Sign on building */}
        <rect x={cx - 52 * s} y={groundY - 78 * s} width={16 * s} height={3 * s} fill={BRAND_GREEN_DEEP} />
      </g>

      {/* Main transformer */}
      <g transform={`translate(${cx + 4 * s} ${groundY - 4 * s})`}>
        {/* Main tank */}
        <rect x={-22 * s} y={-46 * s} width={44 * s} height={42 * s} rx={2 * s} fill={SLATE_LIGHT} stroke={SLATE_DARK} strokeWidth={0.8 * s} />
        {/* Cooling fins (radiator on the left) */}
        {Array.from({ length: 5 }).map((_, i) => (
          <rect
            key={`finL-${i}`}
            x={-26 * s}
            y={-42 * s + i * 7 * s}
            width={4 * s}
            height={5 * s}
            rx={0.6 * s}
            fill={SLATE_MID}
            stroke={SLATE_DARK}
            strokeWidth={0.3 * s}
          />
        ))}
        {Array.from({ length: 5 }).map((_, i) => (
          <rect
            key={`finR-${i}`}
            x={22 * s}
            y={-42 * s + i * 7 * s}
            width={4 * s}
            height={5 * s}
            rx={0.6 * s}
            fill={SLATE_MID}
            stroke={SLATE_DARK}
            strokeWidth={0.3 * s}
          />
        ))}
        {/* Conservator tank on top (cylinder) */}
        <rect x={-14 * s} y={-54 * s} width={28 * s} height={8 * s} rx={4 * s} fill={SLATE_LIGHT} stroke={SLATE_DARK} strokeWidth={0.6 * s} />
        {/* Buchholz relay pipe */}
        <line x1={6 * s} y1={-46 * s} x2={6 * s} y2={-50 * s} stroke={SLATE_DARK} strokeWidth={1 * s} />
        {/* 3 bushings (porcelain ribbed insulators) */}
        {[-12, 0, 12].map((dx, i) => (
          <g key={`bushing-${i}`} transform={`translate(${dx * s} ${-46 * s})`}>
            {/* Stalk */}
            <rect x={-1.6 * s} y={-12 * s} width={3.2 * s} height={12 * s} fill={CREAM_WARM} stroke={SLATE_DARK} strokeWidth={0.4 * s} />
            {/* Ribs */}
            {[2, 5, 8].map((ry, j) => (
              <ellipse key={`rib-${j}`} cx={0} cy={-12 * s + ry * s} rx={3 * s} ry={1 * s} fill={CREAM_WARM} stroke={SLATE_DARK} strokeWidth={0.3 * s} />
            ))}
            {/* Top cap */}
            <ellipse cx={0} cy={-12 * s} rx={2.4 * s} ry={1 * s} fill={SLATE_DARK} />
          </g>
        ))}
        {/* Operating mechanism box (small) */}
        <rect x={6 * s} y={-18 * s} width={8 * s} height={10 * s} fill={SUN_ORANGE} />
        {/* Wheels at base */}
        <circle cx={-16 * s} cy={-2 * s} r={2 * s} fill={SLATE_DARK} />
        <circle cx={16 * s} cy={-2 * s} r={2 * s} fill={SLATE_DARK} />
        {/* HV warning triangle */}
        <g transform={`translate(${-18 * s} ${-24 * s})`}>
          <path d={`M 0 ${-5 * s} L ${5 * s} ${4 * s} L ${-5 * s} ${4 * s} Z`} fill={YELLOW_WARNING} stroke={SLATE_DARK} strokeWidth={0.5 * s} />
          <path d={`M ${-1.4 * s} ${-1 * s} L ${1.4 * s} ${0.6 * s} L 0 ${0.6 * s} L ${1.4 * s} ${2 * s}`} stroke={SLATE_DARK} strokeWidth={0.6 * s} fill="none" />
        </g>
      </g>

      {/* Lightning arresters (3 mushroom-shapes in a row) */}
      <g>
        {[-30, -20, -10].map((dx, i) => (
          <g key={`arr-${i}`} transform={`translate(${cx + dx * s} ${groundY - 24 * s})`}>
            <rect x={-0.6 * s} y={0} width={1.2 * s} height={6 * s} fill={SLATE_MID} />
            <g>
              {[1, 3, 5].map((dy, j) => (
                <ellipse key={`arr-rib-${j}`} cx={0} cy={dy * s - 6 * s} rx={2.2 * s} ry={0.8 * s} fill={CREAM_WARM} stroke={SLATE_DARK} strokeWidth={0.3 * s} />
              ))}
            </g>
            <ellipse cx={0} cy={-8 * s} rx={2.4 * s} ry={1 * s} fill={SLATE_DARK} />
          </g>
        ))}
      </g>

      {/* Pulsing live indicator at front */}
      <circle cx={cx} cy={groundY - 40 * s} r={3 * s} fill={BRAND_LIME} className="login-pulse-fast" />
      <circle cx={cx} cy={groundY - 40 * s} r={6 * s} fill={BRAND_LIME} opacity="0.3" className="login-pulse" />
    </g>
  );
}

// ============================================================================
// DETAILED POWER POLE
// ============================================================================

interface PowerPoleProps {
  x: number;
  baseY: number;
  height: number;
}

function PowerPole({ x, baseY, height }: PowerPoleProps) {
  const topY = baseY - height;
  return (
    <g>
      {/* Pole (slight taper) */}
      <polygon
        points={`${x - 2.4},${baseY} ${x + 2.4},${baseY} ${x + 1.6},${topY} ${x - 1.6},${topY}`}
        fill={SLATE_DARK}
      />
      {/* Pole shading */}
      <line x1={x + 0.6} y1={topY} x2={x + 1.4} y2={baseY} stroke={SLATE_MID} strokeWidth="0.9" />

      {/* Crossarms */}
      <rect x={x - 22} y={topY + 4} width={44} height={2.6} fill={SLATE_DARK} />
      <rect x={x - 17} y={topY + 14} width={34} height={2.4} fill={SLATE_DARK} />

      {/* Polymer insulators (ribbed) — 3 on top crossarm */}
      {[-18, 0, 18].map((dx, i) => (
        <g key={`ins-top-${i}`} transform={`translate(${x + dx} ${topY + 4})`}>
          <line x1={0} y1={0} x2={0} y2={-7} stroke={SLATE_DARK} strokeWidth="1.4" />
          {[-5, -3.5, -2, -0.5].map((ry, j) => (
            <ellipse key={`ins-rib-${j}`} cx={0} cy={ry} rx={1.6} ry={0.5} fill={CREAM_WARM} stroke={SLATE_DARK} strokeWidth="0.3" />
          ))}
          <circle cx={0} cy={-7.5} r={1} fill={SLATE_DARK} />
        </g>
      ))}
      {/* Smaller insulators on bottom crossarm — 2 */}
      {[-14, 14].map((dx, i) => (
        <g key={`ins-bot-${i}`} transform={`translate(${x + dx} ${topY + 14})`}>
          <line x1={0} y1={0} x2={0} y2={-4} stroke={SLATE_DARK} strokeWidth="1" />
          <ellipse cx={0} cy={-3} rx={1.2} ry={0.4} fill={CREAM_WARM} stroke={SLATE_DARK} strokeWidth="0.3" />
          <circle cx={0} cy={-4.4} r={0.8} fill={SLATE_DARK} />
        </g>
      ))}

      {/* Ground stake / grounding wire */}
      <line x1={x - 1.4} y1={topY + 4} x2={x - 1.4} y2={baseY - 4} stroke={SLATE_DARK} strokeWidth="0.4" />

      {/* Contact shadow */}
      <ellipse cx={x} cy={baseY + 1} rx={7} ry={1.6} fill={GROUND_SHADOW} opacity="0.65" />
    </g>
  );
}

// ============================================================================
// DETAILED CLOUD
// ============================================================================

function Cloud({ x, y, scale = 1, opacity = 0.95 }: { x: number; y: number; scale?: number; opacity?: number }) {
  return (
    <g transform={`translate(${x} ${y}) scale(${scale})`} opacity={opacity}>
      <ellipse cx={0} cy={0} rx={52} ry={12} fill="#ffffff" />
      <ellipse cx={-30} cy={-6} rx={24} ry={12} fill="#ffffff" />
      <ellipse cx={28} cy={-5} rx={30} ry={13} fill="#ffffff" />
      <ellipse cx={-12} cy={-10} rx={16} ry={8} fill="#ffffff" />
      <ellipse cx={14} cy={-9} rx={18} ry={8} fill="#ffffff" />
      {/* Underside shadow */}
      <ellipse cx={0} cy={8} rx={50} ry={3} fill={SLATE_LIGHT} opacity="0.18" />
    </g>
  );
}

// ============================================================================
// DETAILED BEACON PIN
// ============================================================================

function BeaconPin({ x, y }: { x: number; y: number }) {
  return (
    <g transform={`translate(${x} ${y})`}>
      <circle r={14} fill="none" stroke={BRAND_LIME} strokeWidth="2.4" className="login-beacon-ring" />
      <circle r={14} fill="none" stroke={BRAND_LIME} strokeWidth="2.4" className="login-beacon-ring-2" />
      <circle r={14} fill="none" stroke={BRAND_LIME} strokeWidth="2.4" className="login-beacon-ring-3" />
      <path
        d="M 0 -38 C -12 -38 -19 -29 -19 -21 C -19 -11 -10 -3 0 12 C 10 -3 19 -11 19 -21 C 19 -29 12 -38 0 -38 Z"
        fill="url(#login-pin-grad)"
        stroke={BRAND_GREEN_DEEP}
        strokeWidth="1.2"
      />
      <path d="M -10 -32 Q -14 -28 -16 -22" stroke="#ffffff" strokeWidth="1.6" strokeLinecap="round" fill="none" opacity="0.65" />
      <circle cx={0} cy={-22} r={7} fill="#ffffff" />
      <circle cx={0} cy={-22} r={4.5} fill={BRAND_GREEN_DEEP} />
      <circle cx={-1.4} cy={-23} r={1.4} fill="#ffffff" opacity="0.85" />
      <ellipse cx={0} cy={14} rx={11} ry={3} fill={GROUND_SHADOW} opacity="0.7" />
    </g>
  );
}

// ============================================================================
// REFINED ENGINEER (clean silhouette, no cartoon face)
// ============================================================================

type EngineerAction = 'tablet' | 'clipboard' | 'walking' | 'point' | 'complete';

interface EngineerProps {
  x: number;
  y: number; // feet position
  scale?: number;
  action?: EngineerAction;
  facing?: 'left' | 'right';
  bobClass?: string;
}

function Engineer({
  x,
  y,
  scale = 1,
  action = 'tablet',
  facing = 'right',
  bobClass = 'login-bob',
}: EngineerProps) {
  const s = scale;
  const f = facing === 'left' ? -1 : 1;
  // Anchor: feet at (x, y)
  // Heights (in s units): boot 4, leg 24, torso 28, neck 4, head 13, hat 5 → total ~78
  const bootH = 4 * s;
  const legH = 24 * s;
  const torsoH = 28 * s;
  const headR = 6.5 * s;

  const legTopY = y - bootH - legH;
  const torsoTopY = legTopY - torsoH;
  const neckY = torsoTopY - 1 * s;
  const headCY = neckY - headR + 2 * s;

  // Skin / hair / hi-vis colours (kept neutral / corporate)
  const skin = '#D89870';
  const jacket = '#F4C24D';
  const reflective = '#F0EDE4';
  const trousers = SLATE_DARK;
  const boot = '#1A1A22';
  const hatOrange = '#F08A3E';

  return (
    <g transform={`translate(${x} 0) scale(${f}, 1) translate(${-x} 0)`}>
      <g className={bobClass}>
      {/* Contact shadow */}
      <ellipse cx={x} cy={y + 2 * s} rx={12 * s} ry={2 * s} fill={SLATE_DARK} opacity="0.22" />

      {/* Legs */}
      {action === 'walking' ? (
        <>
          <g className="login-leg-a">
            <rect x={x - 5 * s} y={legTopY} width={4 * s} height={legH} rx={1 * s} fill={trousers} />
          </g>
          <g className="login-leg-b">
            <rect x={x + 1 * s} y={legTopY} width={4 * s} height={legH} rx={1 * s} fill={trousers} />
          </g>
          {/* Boots that travel with each leg */}
          <g className="login-leg-a">
            <rect x={x - 6 * s} y={y - bootH} width={6 * s} height={bootH} rx={0.8 * s} fill={boot} />
          </g>
          <g className="login-leg-b">
            <rect x={x} y={y - bootH} width={6 * s} height={bootH} rx={0.8 * s} fill={boot} />
          </g>
        </>
      ) : (
        <>
          <rect x={x - 5 * s} y={legTopY} width={4 * s} height={legH} rx={1 * s} fill={trousers} />
          <rect x={x + 1 * s} y={legTopY} width={4 * s} height={legH} rx={1 * s} fill={trousers} />
          <rect x={x - 6 * s} y={y - bootH} width={6 * s} height={bootH} rx={0.8 * s} fill={boot} />
          <rect x={x} y={y - bootH} width={6 * s} height={bootH} rx={0.8 * s} fill={boot} />
        </>
      )}

      {/* Belt */}
      <rect x={x - 9 * s} y={legTopY - 2 * s} width={18 * s} height={2 * s} fill="#5A3A2C" />
      <rect x={x - 1 * s} y={legTopY - 2 * s} width={2 * s} height={2 * s} fill="#3D2820" />

      {/* Hi-vis jacket (torso) */}
      <path
        d={`M ${x - 10 * s} ${torsoTopY}
            Q ${x - 11 * s} ${torsoTopY + 4 * s} ${x - 10 * s} ${torsoTopY + 6 * s}
            L ${x - 11 * s} ${legTopY - 2 * s}
            L ${x + 11 * s} ${legTopY - 2 * s}
            L ${x + 10 * s} ${torsoTopY + 6 * s}
            Q ${x + 11 * s} ${torsoTopY + 4 * s} ${x + 10 * s} ${torsoTopY}
            Z`}
        fill={jacket}
      />
      {/* Reflective stripes (horizontal) */}
      <rect x={x - 11 * s} y={torsoTopY + 10 * s} width={22 * s} height={1.8 * s} fill={reflective} opacity="0.95" />
      <rect x={x - 11 * s} y={torsoTopY + 18 * s} width={22 * s} height={1.4 * s} fill={reflective} opacity="0.7" />
      {/* Vertical zip */}
      <line x1={x} y1={torsoTopY} x2={x} y2={legTopY - 2 * s} stroke={HAT_ORANGE_DEEP} strokeWidth={0.6 * s} />

      {/* Arms — pose per action */}
      {action === 'tablet' || action === 'complete' ? (
        <>
          {/* Both arms forward */}
          <path d={`M ${x - 10 * s} ${torsoTopY + 3 * s} L ${x - 4 * s} ${torsoTopY + 18 * s} L ${x - 2 * s} ${torsoTopY + 20 * s} L ${x - 8 * s} ${torsoTopY + 3 * s} Z`} fill={jacket} />
          <path d={`M ${x + 10 * s} ${torsoTopY + 3 * s} L ${x + 4 * s} ${torsoTopY + 18 * s} L ${x + 2 * s} ${torsoTopY + 20 * s} L ${x + 8 * s} ${torsoTopY + 3 * s} Z`} fill={jacket} />
          <circle cx={x - 4 * s} cy={torsoTopY + 19 * s} r={1.8 * s} fill={skin} />
          <circle cx={x + 4 * s} cy={torsoTopY + 19 * s} r={1.8 * s} fill={skin} />
          {/* Tablet */}
          <g transform={`translate(${x - 8 * s} ${torsoTopY + 13 * s})`}>
            <rect width={16 * s} height={10 * s} rx={1.2 * s} fill={SLATE_DARK} />
            <rect x={1 * s} y={1 * s} width={14 * s} height={8 * s} rx={0.8 * s} fill={action === 'complete' ? BRAND_LIME : BRAND_TEAL} opacity={action === 'complete' ? 1 : 0.85} />
            {action === 'complete' ? (
              <g transform={`translate(${8 * s} ${5 * s})`} className="login-check-pop">
                <path d={`M ${-3 * s} 0 L ${-1 * s} ${1.6 * s} L ${3 * s} ${-2 * s}`} stroke="#ffffff" strokeWidth={1.6 * s} strokeLinecap="round" strokeLinejoin="round" fill="none" />
              </g>
            ) : (
              <>
                <line x1={2 * s} y1={3 * s} x2={14 * s} y2={3 * s} stroke="#ffffff" strokeOpacity="0.7" strokeWidth={0.5 * s} />
                <line x1={2 * s} y1={5 * s} x2={14 * s} y2={5 * s} stroke="#ffffff" strokeOpacity="0.7" strokeWidth={0.5 * s} />
                <line x1={2 * s} y1={7 * s} x2={10 * s} y2={7 * s} stroke="#ffffff" strokeOpacity="0.7" strokeWidth={0.5 * s} />
              </>
            )}
          </g>
        </>
      ) : action === 'clipboard' ? (
        <>
          {/* Left arm down, right arm holding clipboard out */}
          <path d={`M ${x - 10 * s} ${torsoTopY + 3 * s} L ${x - 13 * s} ${torsoTopY + 22 * s} L ${x - 10 * s} ${torsoTopY + 23 * s} L ${x - 7 * s} ${torsoTopY + 3 * s} Z`} fill={jacket} />
          <path d={`M ${x + 10 * s} ${torsoTopY + 3 * s} L ${x + 16 * s} ${torsoTopY + 14 * s} L ${x + 14 * s} ${torsoTopY + 16 * s} L ${x + 8 * s} ${torsoTopY + 5 * s} Z`} fill={jacket} />
          <circle cx={x - 11 * s} cy={torsoTopY + 22 * s} r={1.8 * s} fill={skin} />
          <circle cx={x + 15 * s} cy={torsoTopY + 15 * s} r={1.8 * s} fill={skin} />
          <g transform={`translate(${x + 13 * s} ${torsoTopY + 4 * s})`}>
            <rect width={10 * s} height={14 * s} rx={1 * s} fill={CREAM_WARM} stroke={SLATE_DARK} strokeWidth={0.4 * s} />
            <rect x={3 * s} y={-1.4 * s} width={4 * s} height={2.2 * s} rx={0.4 * s} fill={SLATE_DARK} />
            <line x1={1.5 * s} y1={4 * s} x2={8.5 * s} y2={4 * s} stroke={SLATE_LIGHT} strokeWidth={0.4 * s} />
            <line x1={1.5 * s} y1={7 * s} x2={8.5 * s} y2={7 * s} stroke={SLATE_LIGHT} strokeWidth={0.4 * s} />
            <line x1={1.5 * s} y1={10 * s} x2={6 * s} y2={10 * s} stroke={SLATE_LIGHT} strokeWidth={0.4 * s} />
          </g>
        </>
      ) : action === 'point' ? (
        <>
          {/* Left arm down */}
          <path d={`M ${x - 10 * s} ${torsoTopY + 3 * s} L ${x - 13 * s} ${torsoTopY + 22 * s} L ${x - 10 * s} ${torsoTopY + 23 * s} L ${x - 7 * s} ${torsoTopY + 3 * s} Z`} fill={jacket} />
          {/* Right arm pointing upward */}
          <path d={`M ${x + 10 * s} ${torsoTopY + 3 * s} L ${x + 20 * s} ${torsoTopY - 6 * s} L ${x + 19 * s} ${torsoTopY - 4 * s} L ${x + 8 * s} ${torsoTopY + 5 * s} Z`} fill={jacket} />
          <circle cx={x - 11 * s} cy={torsoTopY + 22 * s} r={1.8 * s} fill={skin} />
          <circle cx={x + 20 * s} cy={torsoTopY - 6 * s} r={1.8 * s} fill={skin} />
        </>
      ) : (
        <>
          {/* Walking — arms swing opposite to legs (simple) */}
          <g className="login-leg-b">
            <path d={`M ${x - 10 * s} ${torsoTopY + 3 * s} L ${x - 13 * s} ${torsoTopY + 22 * s} L ${x - 10 * s} ${torsoTopY + 23 * s} L ${x - 7 * s} ${torsoTopY + 3 * s} Z`} fill={jacket} />
          </g>
          <g className="login-leg-a">
            <path d={`M ${x + 10 * s} ${torsoTopY + 3 * s} L ${x + 13 * s} ${torsoTopY + 22 * s} L ${x + 10 * s} ${torsoTopY + 23 * s} L ${x + 7 * s} ${torsoTopY + 3 * s} Z`} fill={jacket} />
          </g>
          <circle cx={x - 11 * s} cy={torsoTopY + 22 * s} r={1.8 * s} fill={skin} />
          <circle cx={x + 11 * s} cy={torsoTopY + 22 * s} r={1.8 * s} fill={skin} />
        </>
      )}

      {/* Neck */}
      <rect x={x - 2.4 * s} y={neckY} width={4.8 * s} height={4 * s} fill={skin} />

      {/* Head — no facial features (silhouette) */}
      <circle cx={x} cy={headCY} r={headR} fill={skin} />
      {/* Subtle ear edge */}
      <circle cx={x - headR + 0.6 * s} cy={headCY + 0.4 * s} r={0.9 * s} fill={skin} />

      {/* Hard hat */}
      <path
        d={`M ${x - headR - 1 * s} ${headCY - headR + 2 * s}
            Q ${x} ${headCY - headR - 7 * s} ${x + headR + 1 * s} ${headCY - headR + 2 * s}
            L ${x + headR + 1 * s} ${headCY - headR + 3.5 * s}
            L ${x - headR - 1 * s} ${headCY - headR + 3.5 * s}
            Z`}
        fill={hatOrange}
      />
      <ellipse cx={x} cy={headCY - headR + 3.6 * s} rx={headR + 2.6 * s} ry={1.5 * s} fill={hatOrange} />
      {/* Hat highlight crescent */}
      <path d={`M ${x - headR + 1.4 * s} ${headCY - headR - 1 * s} Q ${x - headR + 4 * s} ${headCY - headR - 5 * s} ${x - headR + 8 * s} ${headCY - headR - 5 * s}`} stroke="#ffffff" strokeWidth={0.9 * s} strokeLinecap="round" fill="none" opacity="0.6" />
      {/* Hat band */}
      <rect x={x - headR - 1 * s} y={headCY - headR + 2.6 * s} width={(headR + 1) * 2 * s} height={1 * s} fill={SLATE_DARK} opacity="0.45" />

      {/* Wave overlay for Scene 5 complete */}
      {action === 'complete' && (
        <g transform={`translate(${x + 10 * s} ${torsoTopY + 3 * s})`} className="login-wave">
          {/* (no extra arm — wave is implied via the bob + checkmark) */}
        </g>
      )}
      </g>
    </g>
  );
}

// ============================================================================
// DEPOT — small Sorigin warehouse building (Scene 1 anchor)
// ============================================================================

function Depot({ x, y, scale = 1 }: { x: number; y: number; scale?: number }) {
  const s = scale;
  return (
    <g>
      {/* Ground shadow */}
      <ellipse cx={x} cy={y + 3 * s} rx={75 * s} ry={3 * s} fill={SLATE_DARK} opacity="0.18" />
      {/* Main building */}
      <rect x={x - 62 * s} y={y - 78 * s} width={124 * s} height={78 * s} fill={CREAM_WARM} stroke={SLATE_MID} strokeWidth={1.2 * s} />
      {/* Roof (flat with slight slope) */}
      <polygon
        points={`${x - 68 * s},${y - 78 * s} ${x + 68 * s},${y - 78 * s} ${x + 62 * s},${y - 86 * s} ${x - 62 * s},${y - 86 * s}`}
        fill={SLATE_DARK}
      />
      {/* Brand signage band */}
      <rect x={x - 36 * s} y={y - 84 * s} width={72 * s} height={6 * s} fill={BRAND_GREEN} />
      <text
        x={x}
        y={y - 79 * s}
        textAnchor="middle"
        fontFamily="system-ui, sans-serif"
        fontSize={4 * s}
        fontWeight="700"
        fill="#ffffff"
        letterSpacing="1"
      >
        SORIGIN · EPC
      </text>
      {/* Sliding cargo door */}
      <rect x={x - 24 * s} y={y - 56 * s} width={48 * s} height={56 * s} fill={BRAND_GREEN} stroke={SLATE_DARK} strokeWidth={0.8 * s} />
      <rect x={x - 24 * s} y={y - 58 * s} width={48 * s} height={3 * s} fill={SLATE_DARK} />
      {/* Door panels */}
      <line x1={x} y1={y - 56 * s} x2={x} y2={y} stroke={SLATE_DARK} strokeOpacity="0.6" strokeWidth={0.8 * s} />
      <line x1={x - 12 * s} y1={y - 56 * s} x2={x - 12 * s} y2={y} stroke={SLATE_DARK} strokeOpacity="0.3" strokeWidth={0.6 * s} />
      <line x1={x + 12 * s} y1={y - 56 * s} x2={x + 12 * s} y2={y} stroke={SLATE_DARK} strokeOpacity="0.3" strokeWidth={0.6 * s} />
      {/* Sorigin lime accent stripe across door */}
      <rect x={x - 24 * s} y={y - 28 * s} width={48 * s} height={2 * s} fill={BRAND_LIME} />
      {/* Side windows */}
      <rect x={x - 54 * s} y={y - 66 * s} width={22 * s} height={14 * s} fill={BRAND_TEAL} opacity="0.6" stroke={SLATE_MID} strokeWidth={0.5 * s} />
      <rect x={x + 32 * s} y={y - 66 * s} width={22 * s} height={14 * s} fill={BRAND_TEAL} opacity="0.6" stroke={SLATE_MID} strokeWidth={0.5 * s} />
      <line x1={x - 43 * s} y1={y - 66 * s} x2={x - 43 * s} y2={y - 52 * s} stroke={SLATE_MID} strokeWidth={0.5 * s} />
      <line x1={x - 54 * s} y1={y - 59 * s} x2={x - 32 * s} y2={y - 59 * s} stroke={SLATE_MID} strokeWidth={0.5 * s} />
      <line x1={x + 43 * s} y1={y - 66 * s} x2={x + 43 * s} y2={y - 52 * s} stroke={SLATE_MID} strokeWidth={0.5 * s} />
      <line x1={x + 32 * s} y1={y - 59 * s} x2={x + 54 * s} y2={y - 59 * s} stroke={SLATE_MID} strokeWidth={0.5 * s} />
      {/* Loading bay light */}
      <rect x={x - 4 * s} y={y - 62 * s} width={8 * s} height={4 * s} rx={1 * s} fill={SLATE_DARK} />
      <circle cx={x} cy={y - 60 * s} r={1.6 * s} fill={YELLOW_WARNING} className="login-pulse-fast" />
      {/* Antenna with aviation light */}
      <line x1={x + 54 * s} y1={y - 86 * s} x2={x + 54 * s} y2={y - 100 * s} stroke={SLATE_DARK} strokeWidth={1 * s} />
      <circle cx={x + 54 * s} cy={y - 102 * s} r={1.6 * s} fill={RED_AVIATION} className="login-aviation-light" />
      {/* Brand monogram circle on side */}
      <circle cx={x - 50 * s} cy={y - 38 * s} r={6 * s} fill={BRAND_GREEN} />
      <text
        x={x - 50 * s}
        y={y - 35 * s}
        textAnchor="middle"
        fontFamily="system-ui, sans-serif"
        fontSize={6 * s}
        fontWeight="800"
        fill="#ffffff"
      >
        S
      </text>
    </g>
  );
}

// ============================================================================
// VEHICLE — small Sorigin field service van (Scenes 1, 2, 4)
// ============================================================================

interface VehicleProps {
  x: number;
  y: number; // wheel-ground line
  scale?: number;
  facing?: 'left' | 'right';
  moving?: boolean;
}

function Vehicle({ x, y, scale = 1, facing = 'right', moving = false }: VehicleProps) {
  const s = scale;
  const f = facing === 'left' ? -1 : 1;
  const wheelCls = moving ? 'login-wheel' : undefined;

  return (
    <g transform={`translate(${x} 0) scale(${f}, 1) translate(${-x} 0)`}>
      {/* Ground shadow */}
      <ellipse cx={x} cy={y + 2 * s} rx={36 * s} ry={2.6 * s} fill={SLATE_DARK} opacity="0.22" />
      {/* Lower body */}
      <rect x={x - 32 * s} y={y - 18 * s} width={64 * s} height={14 * s} rx={3 * s} fill={BRAND_GREEN} />
      {/* Upper cabin (curved top) */}
      <path
        d={`M ${x - 28 * s} ${y - 18 * s}
            L ${x - 22 * s} ${y - 34 * s}
            Q ${x - 18 * s} ${y - 38 * s} ${x - 12 * s} ${y - 38 * s}
            L ${x + 22 * s} ${y - 38 * s}
            Q ${x + 26 * s} ${y - 36 * s} ${x + 30 * s} ${y - 30 * s}
            L ${x + 32 * s} ${y - 18 * s}
            Z`}
        fill={BRAND_GREEN}
      />
      {/* Front windshield */}
      <path
        d={`M ${x + 10 * s} ${y - 36 * s}
            L ${x + 20 * s} ${y - 36 * s}
            Q ${x + 24 * s} ${y - 34 * s} ${x + 28 * s} ${y - 28 * s}
            L ${x + 28 * s} ${y - 22 * s}
            L ${x + 10 * s} ${y - 22 * s}
            Z`}
        fill={BRAND_TEAL}
        opacity="0.7"
      />
      {/* Side window */}
      <path
        d={`M ${x - 18 * s} ${y - 36 * s}
            L ${x + 8 * s} ${y - 36 * s}
            L ${x + 8 * s} ${y - 22 * s}
            L ${x - 18 * s} ${y - 22 * s}
            Z`}
        fill={BRAND_TEAL}
        opacity="0.65"
      />
      {/* Engineer head visible in cabin */}
      <circle cx={x + 17 * s} cy={y - 29 * s} r={2.8 * s} fill="#D89870" />
      <path
        d={`M ${x + 14 * s} ${y - 31 * s} Q ${x + 17 * s} ${y - 34 * s} ${x + 20 * s} ${y - 31 * s} L ${x + 20 * s} ${y - 30 * s} L ${x + 14 * s} ${y - 30 * s} Z`}
        fill="#F08A3E"
      />
      {/* Headlight */}
      <ellipse cx={x + 30 * s} cy={y - 14 * s} rx={2 * s} ry={1.4 * s} fill={YELLOW_WARNING} />
      <ellipse cx={x + 32 * s} cy={y - 14 * s} rx={6 * s} ry={1.4 * s} fill={YELLOW_WARNING} opacity="0.3" />
      {/* Side door split line */}
      <line x1={x - 4 * s} y1={y - 18 * s} x2={x - 4 * s} y2={y - 6 * s} stroke={SLATE_DARK} strokeOpacity="0.4" strokeWidth={0.8 * s} />
      {/* Brand lime stripe */}
      <rect x={x - 32 * s} y={y - 9 * s} width={64 * s} height={2.4 * s} fill={BRAND_LIME} />
      {/* Brand "S" monogram on door */}
      <circle cx={x - 12 * s} cy={y - 13 * s} r={4 * s} fill="#ffffff" />
      <text
        x={x - 12 * s}
        y={y - 10.6 * s}
        textAnchor="middle"
        fontFamily="system-ui, sans-serif"
        fontSize={5 * s}
        fontWeight="800"
        fill={BRAND_GREEN}
      >
        S
      </text>
      {/* Side mirror */}
      <rect x={x + 24 * s} y={y - 30 * s} width={2 * s} height={3 * s} fill={SLATE_DARK} />
      {/* Wheels */}
      <g>
        <circle cx={x - 18 * s} cy={y - 4 * s} r={6 * s} fill={SLATE_DARK} />
        <g className={wheelCls}>
          <circle cx={x - 18 * s} cy={y - 4 * s} r={3.6 * s} fill="#cbd5e1" />
          <circle cx={x - 18 * s} cy={y - 4 * s} r={3.6 * s} fill="none" stroke={SLATE_DARK} strokeWidth={0.4 * s} />
          <line x1={x - 18 * s} y1={(y - 4 * s) - 3 * s} x2={x - 18 * s} y2={(y - 4 * s) + 3 * s} stroke={SLATE_DARK} strokeWidth={0.7 * s} />
          <line x1={(x - 18 * s) - 3 * s} y1={y - 4 * s} x2={(x - 18 * s) + 3 * s} y2={y - 4 * s} stroke={SLATE_DARK} strokeWidth={0.7 * s} />
        </g>
        <circle cx={x + 18 * s} cy={y - 4 * s} r={6 * s} fill={SLATE_DARK} />
        <g className={wheelCls}>
          <circle cx={x + 18 * s} cy={y - 4 * s} r={3.6 * s} fill="#cbd5e1" />
          <circle cx={x + 18 * s} cy={y - 4 * s} r={3.6 * s} fill="none" stroke={SLATE_DARK} strokeWidth={0.4 * s} />
          <line x1={x + 18 * s} y1={(y - 4 * s) - 3 * s} x2={x + 18 * s} y2={(y - 4 * s) + 3 * s} stroke={SLATE_DARK} strokeWidth={0.7 * s} />
          <line x1={(x + 18 * s) - 3 * s} y1={y - 4 * s} x2={(x + 18 * s) + 3 * s} y2={y - 4 * s} stroke={SLATE_DARK} strokeWidth={0.7 * s} />
        </g>
      </g>
    </g>
  );
}

// ============================================================================
// MOTORCYCLE — Royal Enfield Bullet ridden by the site engineer
// ============================================================================

interface MotorcycleProps {
  x: number;
  y: number; // wheel-ground line
  scale?: number;
  facing?: 'left' | 'right';
  riding?: boolean;
}

function Motorcycle({ x, y, scale = 1, facing = 'right', riding = false }: MotorcycleProps) {
  const s = scale;
  const f = facing === 'left' ? -1 : 1;
  const wheelR = 10 * s;
  const halfGap = 24 * s;
  const rearX = x - halfGap;
  const frontX = x + halfGap;
  const wheelY = y - wheelR;
  const wheelCls = riding ? 'login-wheel' : undefined;

  return (
    <g transform={`translate(${x} 0) scale(${f}, 1) translate(${-x} 0)`}>
      {/* Ground shadow */}
      <ellipse cx={x} cy={y + 2 * s} rx={32 * s} ry={2.4 * s} fill={SLATE_DARK} opacity="0.25" />

      {/* Rear wheel */}
      <g>
        <circle cx={rearX} cy={wheelY} r={wheelR} fill="#1A1A22" />
        <circle cx={rearX} cy={wheelY} r={wheelR - 1.4 * s} fill="#0a0a0e" />
        <g className={wheelCls}>
          <circle cx={rearX} cy={wheelY} r={wheelR - 2.5 * s} fill="none" stroke="#cbd5e1" strokeWidth={0.5 * s} />
          {[0, 30, 60, 90, 120, 150].map((angle) => (
            <line
              key={`rs-${angle}`}
              x1={rearX}
              y1={wheelY}
              x2={rearX + (wheelR - 3 * s) * Math.cos((angle * Math.PI) / 180)}
              y2={wheelY + (wheelR - 3 * s) * Math.sin((angle * Math.PI) / 180)}
              stroke="#cbd5e1"
              strokeWidth={0.4 * s}
            />
          ))}
          <circle cx={rearX} cy={wheelY} r={1.4 * s} fill="#94A3B8" />
        </g>
      </g>

      {/* Front wheel */}
      <g>
        <circle cx={frontX} cy={wheelY} r={wheelR} fill="#1A1A22" />
        <circle cx={frontX} cy={wheelY} r={wheelR - 1.4 * s} fill="#0a0a0e" />
        <g className={wheelCls}>
          <circle cx={frontX} cy={wheelY} r={wheelR - 2.5 * s} fill="none" stroke="#cbd5e1" strokeWidth={0.5 * s} />
          {[0, 30, 60, 90, 120, 150].map((angle) => (
            <line
              key={`fs-${angle}`}
              x1={frontX}
              y1={wheelY}
              x2={frontX + (wheelR - 3 * s) * Math.cos((angle * Math.PI) / 180)}
              y2={wheelY + (wheelR - 3 * s) * Math.sin((angle * Math.PI) / 180)}
              stroke="#cbd5e1"
              strokeWidth={0.4 * s}
            />
          ))}
          <circle cx={frontX} cy={wheelY} r={1.4 * s} fill="#94A3B8" />
        </g>
      </g>

      {/* Engine block (centred, with cooling fins — Royal Enfield thumper) */}
      <path
        d={`M ${x - 8 * s} ${wheelY - 1 * s}
            L ${x + 8 * s} ${wheelY - 1 * s}
            L ${x + 10 * s} ${wheelY - 12 * s}
            L ${x - 10 * s} ${wheelY - 12 * s} Z`}
        fill={SLATE_MID}
        stroke={SLATE_DARK}
        strokeWidth={0.5 * s}
      />
      {[3, 5.5, 8, 10.5].map((dy, i) => (
        <line
          key={`fin-${i}`}
          x1={x - 9 * s}
          y1={wheelY - dy * s}
          x2={x + 9 * s}
          y2={wheelY - dy * s}
          stroke={SLATE_DARK}
          strokeWidth={0.35 * s}
        />
      ))}
      {/* Engine cap / spark plug */}
      <rect x={x - 1.2 * s} y={wheelY - 14 * s} width={2.4 * s} height={2.5 * s} fill={SLATE_DARK} />

      {/* Chrome exhaust pipe (along the right/back) */}
      <path
        d={`M ${rearX + 2 * s} ${wheelY - 3 * s}
            L ${rearX + 22 * s} ${wheelY - 3 * s}
            L ${rearX + 22 * s} ${wheelY - 0.5 * s}
            L ${rearX + 2 * s} ${wheelY - 0.5 * s} Z`}
        fill="#E5E7EB"
        stroke={SLATE_MID}
        strokeWidth={0.4 * s}
      />
      <ellipse cx={rearX + 2 * s} cy={wheelY - 1.7 * s} rx={1.2 * s} ry={1.7 * s} fill="#0a0a0e" />
      {/* Exhaust highlight */}
      <line x1={rearX + 4 * s} y1={wheelY - 2.4 * s} x2={rearX + 20 * s} y2={wheelY - 2.4 * s} stroke="#ffffff" strokeWidth={0.4 * s} opacity="0.7" />

      {/* Frame downtubes */}
      <line x1={x - 11 * s} y1={wheelY - 14 * s} x2={x - 6 * s} y2={wheelY - 1 * s} stroke={SLATE_DARK} strokeWidth={1.4 * s} strokeLinecap="round" />
      <line x1={x + 6 * s} y1={wheelY - 1 * s} x2={x + 12 * s} y2={wheelY - 14 * s} stroke={SLATE_DARK} strokeWidth={1.2 * s} strokeLinecap="round" />
      {/* Swingarm / rear frame to wheel */}
      <line x1={x - 6 * s} y1={wheelY - 1 * s} x2={rearX + 4 * s} y2={wheelY - 0.5 * s} stroke={SLATE_DARK} strokeWidth={1.4 * s} strokeLinecap="round" />
      {/* Front fork */}
      <line x1={x + 13 * s} y1={wheelY - 14 * s} x2={frontX - 1 * s} y2={wheelY - 1 * s} stroke={SLATE_DARK} strokeWidth={1.6 * s} strokeLinecap="round" />

      {/* Fuel tank — iconic teardrop in BRAND_GREEN */}
      <path
        d={`M ${x - 14 * s} ${wheelY - 14 * s}
            Q ${x - 15 * s} ${wheelY - 24 * s} ${x - 4 * s} ${wheelY - 25 * s}
            L ${x + 8 * s} ${wheelY - 24 * s}
            Q ${x + 15 * s} ${wheelY - 22 * s} ${x + 14 * s} ${wheelY - 16 * s}
            L ${x + 12 * s} ${wheelY - 14 * s}
            Z`}
        fill={BRAND_GREEN}
        stroke="#0a3d2a"
        strokeWidth={0.6 * s}
      />
      {/* Tank chrome highlight */}
      <path
        d={`M ${x - 10 * s} ${wheelY - 21 * s} Q ${x - 4 * s} ${wheelY - 23 * s} ${x + 4 * s} ${wheelY - 22 * s}`}
        stroke="#ffffff"
        strokeWidth={0.9 * s}
        fill="none"
        opacity="0.55"
        strokeLinecap="round"
      />
      {/* Sorigin "S" badge on the tank */}
      <circle cx={x + 2 * s} cy={wheelY - 18 * s} r={2.6 * s} fill="#ffffff" />
      <text
        x={x + 2 * s}
        y={wheelY - 16.3 * s}
        textAnchor="middle"
        fontFamily="system-ui, sans-serif"
        fontSize={3.4 * s}
        fontWeight="800"
        fill={BRAND_GREEN}
      >
        S
      </text>
      {/* Bullet model script line under badge */}
      <rect x={x - 6 * s} y={wheelY - 14.4 * s} width={12 * s} height={0.6 * s} fill="#ffffff" opacity="0.7" />

      {/* Bench seat (rearward) */}
      <path
        d={`M ${rearX - 2 * s} ${wheelY - 14 * s}
            Q ${rearX - 3 * s} ${wheelY - 17 * s} ${rearX + 3 * s} ${wheelY - 17 * s}
            L ${x - 14 * s} ${wheelY - 16 * s}
            L ${x - 14 * s} ${wheelY - 13 * s}
            L ${rearX + 1 * s} ${wheelY - 13 * s}
            Z`}
        fill="#0a0a0e"
      />
      {/* Seat stitch */}
      <line x1={rearX} y1={wheelY - 15 * s} x2={x - 13 * s} y2={wheelY - 14 * s} stroke="#3D4452" strokeWidth={0.4 * s} />

      {/* Rear fender / mudguard */}
      <path
        d={`M ${rearX - 7 * s} ${wheelY - 6 * s}
            Q ${rearX - 2 * s} ${wheelY - 12 * s} ${rearX + 6 * s} ${wheelY - 10 * s}`}
        stroke="#0a0a0e"
        strokeWidth={1.6 * s}
        fill="none"
      />
      {/* Tail light */}
      <ellipse cx={rearX - 7 * s} cy={wheelY - 9 * s} rx={1.6 * s} ry={1.2 * s} fill="#E63946" />
      <circle cx={rearX - 7 * s} cy={wheelY - 9.4 * s} r={0.6 * s} fill="#ffffff" opacity="0.8" />
      {/* Number-plate holder */}
      <rect x={rearX - 11 * s} y={wheelY - 7 * s} width={5 * s} height={3 * s} rx={0.4 * s} fill="#ffffff" stroke={SLATE_DARK} strokeWidth={0.4 * s} />

      {/* Front fender */}
      <path
        d={`M ${frontX - 7 * s} ${wheelY - 5 * s}
            Q ${frontX} ${wheelY - 11 * s} ${frontX + 7 * s} ${wheelY - 5 * s}`}
        stroke="#0a0a0e"
        strokeWidth={1.6 * s}
        fill="none"
      />

      {/* Handlebar stem + bars */}
      <line x1={x + 14 * s} y1={wheelY - 16 * s} x2={x + 16 * s} y2={wheelY - 26 * s} stroke={SLATE_DARK} strokeWidth={1.6 * s} strokeLinecap="round" />
      <rect x={x + 11 * s} y={wheelY - 27 * s} width={11 * s} height={1.8 * s} rx={0.8 * s} fill="#0a0a0e" />
      {/* Grip ends */}
      <circle cx={x + 11.5 * s} cy={wheelY - 26.1 * s} r={1.2 * s} fill={SLATE_DARK} />
      <circle cx={x + 21.5 * s} cy={wheelY - 26.1 * s} r={1.2 * s} fill={SLATE_DARK} />
      {/* Speedometer */}
      <circle cx={x + 16.5 * s} cy={wheelY - 25 * s} r={2.6 * s} fill="#cbd5e1" stroke={SLATE_DARK} strokeWidth={0.5 * s} />
      <circle cx={x + 16.5 * s} cy={wheelY - 25 * s} r={1.4 * s} fill={SLATE_DARK} />
      <circle cx={x + 16.5 * s} cy={wheelY - 25 * s} r={0.4 * s} fill={BRAND_LIME} />

      {/* Iconic round headlight — chrome rim + amber bulb */}
      <circle cx={x + 19 * s} cy={wheelY - 19 * s} r={4 * s} fill="#0a0a0e" />
      <circle cx={x + 19 * s} cy={wheelY - 19 * s} r={3.2 * s} fill="#cbd5e1" />
      <circle cx={x + 19 * s} cy={wheelY - 19 * s} r={2.4 * s} fill={YELLOW_WARNING} />
      <circle cx={x + 18 * s} cy={wheelY - 19.8 * s} r={0.9 * s} fill="#FFE5A0" />

      {/* Rider — torso (slightly hunched forward) */}
      <path
        d={`M ${x - 15 * s} ${wheelY - 30 * s}
            L ${x - 14 * s} ${wheelY - 16 * s}
            L ${x - 4 * s} ${wheelY - 16 * s}
            L ${x - 2 * s} ${wheelY - 30 * s}
            Z`}
        fill="#F4C24D"
      />
      {/* Reflective stripe across torso */}
      <rect x={x - 15 * s} y={wheelY - 24 * s} width={13 * s} height={1.4 * s} fill="#F0EDE4" opacity="0.95" />
      {/* Backpack visible behind torso */}
      <rect x={x - 19 * s} y={wheelY - 28 * s} width={4.5 * s} height={12 * s} rx={1 * s} fill="#2D3340" />
      <rect x={x - 17.5 * s} y={wheelY - 23 * s} width={2 * s} height={1.4 * s} fill={BRAND_LIME} />
      <line x1={x - 19 * s} y1={wheelY - 18 * s} x2={x - 14.5 * s} y2={wheelY - 18 * s} stroke={SLATE_LIGHT} strokeOpacity="0.4" strokeWidth={0.4 * s} />

      {/* Arms forward to handlebar */}
      <path
        d={`M ${x - 3 * s} ${wheelY - 29 * s}
            L ${x + 12 * s} ${wheelY - 25 * s}
            L ${x + 13 * s} ${wheelY - 23 * s}
            L ${x - 2 * s} ${wheelY - 27 * s}
            Z`}
        fill="#F4C24D"
      />
      {/* Gloved hand on grip */}
      <circle cx={x + 13 * s} cy={wheelY - 25 * s} r={1.8 * s} fill="#1A1A22" />

      {/* Riding pants (visible thigh) */}
      <path
        d={`M ${x - 10 * s} ${wheelY - 16 * s}
            L ${x - 6 * s} ${wheelY - 6 * s}
            L ${x + 1 * s} ${wheelY - 6 * s}
            L ${x - 2 * s} ${wheelY - 16 * s}
            Z`}
        fill={SLATE_DARK}
      />
      {/* Boot */}
      <ellipse cx={x - 4 * s} cy={wheelY - 4 * s} rx={4 * s} ry={2 * s} fill="#0a0a0e" />

      {/* Head + helmet */}
      <circle cx={x - 9 * s} cy={wheelY - 34 * s} r={4.2 * s} fill="#D89870" />
      {/* Full-face helmet shell */}
      <path
        d={`M ${x - 14.5 * s} ${wheelY - 34 * s}
            Q ${x - 9 * s} ${wheelY - 42 * s} ${x - 3 * s} ${wheelY - 34 * s}
            L ${x - 3 * s} ${wheelY - 30 * s}
            L ${x - 14.5 * s} ${wheelY - 30 * s}
            Z`}
        fill={BRAND_GREEN}
        stroke="#0a3d2a"
        strokeWidth={0.5 * s}
      />
      {/* Visor */}
      <rect
        x={x - 13.5 * s}
        y={wheelY - 35 * s}
        width={10 * s}
        height={3 * s}
        rx={0.8 * s}
        fill="#0a0a0e"
        opacity="0.9"
      />
      {/* Visor reflection */}
      <line x1={x - 12 * s} y1={wheelY - 33.5 * s} x2={x - 8 * s} y2={wheelY - 33.5 * s} stroke="#ffffff" strokeWidth={0.5 * s} opacity="0.5" />
      {/* Helmet top highlight */}
      <path d={`M ${x - 11 * s} ${wheelY - 39 * s} Q ${x - 8 * s} ${wheelY - 41 * s} ${x - 5 * s} ${wheelY - 39 * s}`} stroke="#ffffff" strokeWidth={0.7 * s} fill="none" opacity="0.5" strokeLinecap="round" />
      {/* Helmet trim stripe */}
      <rect x={x - 14 * s} y={wheelY - 32 * s} width={11 * s} height={0.6 * s} fill={BRAND_LIME} />
    </g>
  );
}

// ============================================================================
// ROUTE LINE — dashed curving travel road
// ============================================================================

function RouteLine({
  x1,
  y1,
  x2,
  y2,
  curveAmount = 30,
}: {
  x1: number;
  y1: number;
  x2: number;
  y2: number;
  curveAmount?: number;
}) {
  const d = `M ${x1} ${y1} Q ${(x1 + x2) / 2} ${(y1 + y2) / 2 + curveAmount} ${x2} ${y2}`;
  return (
    <g>
      <path d={d} stroke="#C9B27C" strokeWidth="24" strokeLinecap="round" fill="none" opacity="0.85" />
      <path d={d} stroke="#FFE5A0" strokeWidth="2.2" strokeDasharray="12 14" fill="none" opacity="0.85" />
    </g>
  );
}

// ============================================================================
// CINEMATIC HELPERS — dust motes, color grade
// ============================================================================

function DustMotes() {
  // 12 motes hand-placed across the canvas with different sizes + drift classes
  const motes: { x: number; y: number; r: number; cls: string }[] = [
    { x: 110, y: 200, r: 1.6, cls: 'login-mote' },
    { x: 220, y: 320, r: 1.2, cls: 'login-mote-2' },
    { x: 330, y: 250, r: 1.8, cls: 'login-mote-3' },
    { x: 440, y: 350, r: 1.2, cls: 'login-mote-4' },
    { x: 550, y: 220, r: 1.4, cls: 'login-mote-5' },
    { x: 660, y: 300, r: 1.6, cls: 'login-mote' },
    { x: 770, y: 380, r: 1.4, cls: 'login-mote-2' },
    { x: 170, y: 410, r: 1.2, cls: 'login-mote-3' },
    { x: 380, y: 420, r: 1.8, cls: 'login-mote-4' },
    { x: 580, y: 410, r: 1.4, cls: 'login-mote-5' },
    { x: 720, y: 240, r: 1.2, cls: 'login-mote' },
    { x: 300, y: 180, r: 1.4, cls: 'login-mote-3' },
  ];
  return (
    <g fill="#FFF8DC" opacity="0.8" pointerEvents="none">
      {motes.map((m, i) => (
        <circle key={`m-${i}`} cx={m.x} cy={m.y} r={m.r} className={m.cls} />
      ))}
    </g>
  );
}

interface ColorGradeProps {
  tint: string;
  opacity: number;
}

function ColorGrade({ tint, opacity }: ColorGradeProps) {
  return (
    <rect x={0} y={0} width={VIEW_W} height={VIEW_H} fill={tint} opacity={opacity} pointerEvents="none" />
  );
}

// ============================================================================
// SURVEY FLAG (Scene 1 markers)
// ============================================================================

function SurveyFlag({ x, y, color = BRAND_LIME }: { x: number; y: number; color?: string }) {
  return (
    <g>
      <line x1={x} y1={y} x2={x} y2={y - 32} stroke={SLATE_DARK} strokeWidth="1.4" strokeLinecap="round" />
      <path d={`M ${x} ${y - 32} L ${x + 14} ${y - 28} L ${x} ${y - 22} Z`} fill={color} />
      <ellipse cx={x} cy={y + 1} rx="4" ry="1.4" fill={SLATE_DARK} opacity="0.25" />
    </g>
  );
}

// ============================================================================
// CONSTRUCTION SIGN (Scene 2 marker)
// ============================================================================

function ConstructionSign({ x, y, scale = 1 }: { x: number; y: number; scale?: number }) {
  const s = scale;
  return (
    <g transform={`translate(${x} ${y})`}>
      <line x1={0} y1={0} x2={0} y2={-22 * s} stroke={SLATE_DARK} strokeWidth={1.6 * s} strokeLinecap="round" />
      <path d={`M ${-12 * s} ${-32 * s} L ${12 * s} ${-32 * s} L ${10 * s} ${-22 * s} L ${-10 * s} ${-22 * s} Z`} fill="#F08A3E" />
      <text x={0} y={-25 * s} textAnchor="middle" fontFamily="system-ui, sans-serif" fontSize={6 * s} fontWeight="700" fill="#ffffff">
        EPC
      </text>
      <ellipse cx={0} cy={1 * s} rx={4 * s} ry={1.4 * s} fill={SLATE_DARK} opacity="0.25" />
    </g>
  );
}

// ============================================================================
// BIRDS, GRASS, FOLIAGE
// ============================================================================

function Bird({ x, y, scale = 1, className = '' }: { x: number; y: number; scale?: number; className?: string }) {
  const s = scale;
  return (
    <g transform={`translate(${x} ${y})`} className={className}>
      <path
        d={`M ${-6 * s} 0 Q ${-3 * s} ${-4 * s} 0 0 Q ${3 * s} ${-4 * s} ${6 * s} 0`}
        stroke={SLATE_DARK}
        strokeWidth={1.4 * s}
        fill="none"
        strokeLinecap="round"
      />
    </g>
  );
}

function GrassTuft({ x, y, scale = 1, swayClass = 'login-grass' }: { x: number; y: number; scale?: number; swayClass?: string }) {
  const s = scale;
  return (
    <g transform={`translate(${x} ${y})`} className={swayClass}>
      <path d={`M 0 0 Q ${-2 * s} ${-7 * s} ${-1 * s} ${-12 * s}`} stroke={HILL_SAGE_SHADOW} strokeWidth={1.2 * s} fill="none" strokeLinecap="round" />
      <path d={`M 0 0 Q 0 ${-9 * s} ${1 * s} ${-14 * s}`} stroke={HILL_SAGE_DEEP} strokeWidth={1.2 * s} fill="none" strokeLinecap="round" />
      <path d={`M 0 0 Q ${2 * s} ${-7 * s} ${3 * s} ${-11 * s}`} stroke={HILL_SAGE_SHADOW} strokeWidth={1.2 * s} fill="none" strokeLinecap="round" />
    </g>
  );
}

function Plant({ x, y, scale = 1, flip = false }: { x: number; y: number; scale?: number; flip?: boolean }) {
  const s = scale;
  const f = flip ? -1 : 1;
  return (
    <g transform={`translate(${x} ${y}) scale(${f * s}, ${s})`}>
      <g className="login-grass">
        <path d="M 0 0 Q -4 -18 -2 -32 Q 6 -22 4 0 Z" fill={HILL_SAGE_DEEP} />
        <path d="M 8 0 Q 12 -22 18 -18 Q 14 -8 12 0 Z" fill={HILL_SAGE_SHADOW} />
        <path d="M -10 0 Q -14 -16 -8 -10 Q -5 -4 -6 0 Z" fill={HILL_SAGE_SHADOW} />
        <path d="M 14 0 Q 18 -10 22 -2 Q 18 0 16 0 Z" fill={HILL_SAGE_DEEP} />
        {/* Flower dots */}
        <circle cx={-2} cy={-30} r={1.4} fill={BRAND_LIME} />
        <circle cx={18} cy={-18} r={1.2} fill={SUN_ORANGE} />
      </g>
    </g>
  );
}

// ============================================================================
// GUILLOCHE PATH (concentric polar ornament rings)
// ============================================================================

interface GuillochePathProps {
  cx: number;
  cy: number;
  iterations?: number;
  steps?: number;
  a?: number;
  b?: number;
  c?: number;
  outerPhase?: number;
  innerPhase?: number;
  stroke?: string;
  strokeWidth?: number;
  strokeOpacity?: number;
}

function GuillochePath({
  cx, cy,
  iterations = 60,
  steps = 1200,
  a = 4, b = 8, c = 20,
  outerPhase = 4.7,
  innerPhase = 1.8,
  stroke = ESPRESSO_INK,
  strokeWidth = 0.35,
  strokeOpacity = 0.55,
}: GuillochePathProps) {
  const rings = guilloche({ cx, cy, iterations, steps, a, b, c, outerPhase, innerPhase });
  return (
    <g fill="none" stroke={stroke} strokeWidth={strokeWidth} strokeOpacity={strokeOpacity} vectorEffect="non-scaling-stroke" strokeLinecap="round" strokeLinejoin="round">
      {rings.map((d, i) => (
        <path key={i} d={d} />
      ))}
    </g>
  );
}

interface MicroprintRingProps {
  cx: number;
  cy: number;
  r: number;
  phrase: string;
  fontSize?: number;
  fill?: string;
  /** Unique ID to give the path-id; required so multiple rings on the page don't collide. */
  pathId: string;
}

function MicroprintRing({ cx, cy, r, phrase, fontSize = 2.4, fill = ESPRESSO_INK, pathId }: MicroprintRingProps) {
  // The text repeats along the path; pad it well past the circumference.
  const circumference = 2 * Math.PI * r;
  const charsNeeded = Math.ceil(circumference / (fontSize * 0.55));
  const padded = (phrase + ' · ').repeat(Math.ceil(charsNeeded / phrase.length) + 1);
  return (
    <g>
      <defs>
        <path id={pathId} d={`M ${cx - r},${cy} a ${r},${r} 0 1,1 ${r * 2},0 a ${r},${r} 0 1,1 ${-r * 2},0`} fill="none" />
      </defs>
      <text fontFamily="Inter, system-ui, sans-serif" fontSize={fontSize} letterSpacing="0.3" fill={fill} fillOpacity="0.85">
        <textPath href={`#${pathId}`} startOffset="0">{padded}</textPath>
      </text>
    </g>
  );
}

interface PearlBorderProps {
  cx: number;
  cy: number;
  r: number;
  count?: number;
  pearlR?: number;
  fill?: string;
}

function PearlBorder({ cx, cy, r, count = 48, pearlR = 0.9, fill = ESPRESSO_INK }: PearlBorderProps) {
  const pearls = Array.from({ length: count }, (_, i) => {
    const a = (i / count) * Math.PI * 2;
    return { x: cx + r * Math.cos(a), y: cy + r * Math.sin(a) };
  });
  return (
    <g fill={fill} fillOpacity="0.9">
      {pearls.map((p, i) => (
        <circle key={i} cx={p.x} cy={p.y} r={pearlR} />
      ))}
    </g>
  );
}

interface Rosette16Props {
  cx: number;
  cy: number;
  innerR: number;
  outerR: number;
  microprintPhrase?: string;
  counter?: string;
  /** Unique id stem; used for the path id of the microprint ring. */
  idStem: string;
}

function Rosette16({ cx, cy, innerR, outerR, microprintPhrase = 'SORIGIN WIND ASSETS · SITE INTEGRITY · ', counter = 'EPC', idStem }: Rosette16Props) {
  const petals = Array.from({ length: 16 }, (_, i) => i * (360 / 16));
  return (
    <g>
      {/* Outer pearl border */}
      <PearlBorder cx={cx} cy={cy} r={outerR} count={48} pearlR={0.9} />
      {/* Inner pearl border */}
      <PearlBorder cx={cx} cy={cy} r={innerR + (outerR - innerR) * 0.55} count={32} pearlR={0.7} />
      {/* Guilloche annulus between the two pearl rings */}
      <GuillochePath cx={cx} cy={cy} iterations={28} a={4} b={8} c={innerR + (outerR - innerR) * 0.25} steps={400} strokeWidth={0.32} strokeOpacity={0.6} stroke={ESPRESSO_INK} />
      {/* 16-fold inner motif: champagne petals */}
      <g>
        {petals.map((deg) => (
          <g key={deg} transform={`rotate(${deg} ${cx} ${cy})`}>
            <path
              d={`M ${cx} ${cy - innerR * 0.45}
                  Q ${cx + innerR * 0.12} ${cy - innerR * 0.6} ${cx} ${cy - innerR * 0.8}
                  Q ${cx - innerR * 0.12} ${cy - innerR * 0.6} ${cx} ${cy - innerR * 0.45}
                  Z`}
              fill={CHAMPAGNE}
              fillOpacity="0.85"
              stroke={ESPRESSO_INK}
              strokeWidth="0.3"
            />
          </g>
        ))}
      </g>
      {/* Microprint ring just inside the outer pearls */}
      <MicroprintRing cx={cx} cy={cy} r={outerR - 4} phrase={microprintPhrase} pathId={`${idStem}-microring`} fontSize={2.2} />
      {/* Counter at centre */}
      <text x={cx} y={cy + innerR * 0.18} textAnchor="middle" fontFamily="'Georgia', 'Times New Roman', serif" fontSize={innerR * 0.55} fontWeight="800" fill={ESPRESSO_INK}>
        {counter}
      </text>
    </g>
  );
}

interface LatentHatchProps {
  carrierD: string;
  motifD: string;
  carrierAngle?: number;
  motifAngle?: number;
  pitch?: number;
  idStem: string;
}

function LatentHatch({ carrierD, motifD, carrierAngle = 0, motifAngle = 90, pitch = 1.4, idStem }: LatentHatchProps) {
  return (
    <g>
      <defs>
        <pattern id={`${idStem}-carrier`} x="0" y="0" width="4" height="4" patternUnits="userSpaceOnUse" patternTransform={`rotate(${carrierAngle})`}>
          <line x1="0" y1="0" x2="0" y2="4" stroke={ESPRESSO_INK} strokeWidth="0.4" strokeOpacity="0.6" />
        </pattern>
        <pattern id={`${idStem}-motif`} x="0" y="0" width={pitch} height={pitch} patternUnits="userSpaceOnUse" patternTransform={`rotate(${motifAngle})`}>
          <line x1="0" y1="0" x2="0" y2={pitch} stroke={ESPRESSO_INK} strokeWidth="0.4" strokeOpacity="0.6" />
        </pattern>
        <clipPath id={`${idStem}-clip`}>
          <path d={carrierD} />
        </clipPath>
      </defs>
      <g clipPath={`url(#${idStem}-clip)`}>
        <path d={carrierD} fill={`url(#${idStem}-carrier)`} />
        <path d={motifD} fill={`url(#${idStem}-motif)`} />
      </g>
    </g>
  );
}

// === Phase 4 EPC scene components ===

/** The erection site at x=583: a persistent foundation pedestal plus the
 *  GROWING tower. Each built piece is geometry-identical to its flying twin
 *  frozen at the seated position, so the crossfade handoff (flying grip-N out,
 *  built-N in at the same instant on the 40s clock) is invisible. Taper is
 *  continuous: half-width 11 at the base → 9.2 at joint 1-2 (y422) → 7.5 at
 *  joint 2-3 (y376) → 6 at the tower top (y334). All pieces rest at inline
 *  opacity 0; the CSS erection clock (or reduced-motion / auth-success
 *  overrides) drives visibility. The rotor attaches PARKED — it only spins in
 *  the auth-success state (turbines under construction have locked blades). */
function ErectionSite() {
  return (
    <g>
      {/* Foundation pedestal — always there */}
      <rect x={564} y={470} width={38} height={6} rx={1} fill={SLATE_LIGHT} stroke={ESPRESSO_INK} strokeWidth={0.6} />
      <rect x={564} y={470} width={38} height={1.4} fill={SLATE_MID} />

      {/* Section 1/3 — lands on the foundation in phase 1 */}
      <g className="login-built-piece login-built-s1" opacity={0}>
        <polygon points="572,470 594,470 592.2,422 573.8,422" fill="url(#intaglio-v)" stroke={ESPRESSO_INK} strokeWidth="0.7" />
        <path d="M 588.9 422 L 590.9 470 L 594 470 L 592.2 422 Z" fill={GROUND_SHADOW} opacity="0.25" />
        <line x1={573.2} y1={438} x2={592.8} y2={438} stroke={ESPRESSO_INK} strokeWidth="0.4" strokeOpacity="0.45" />
        <line x1={572.6} y1={454} x2={593.4} y2={454} stroke={ESPRESSO_INK} strokeWidth="0.4" strokeOpacity="0.45" />
        {/* cutaway door opening — internal ladder access; a second crew member
            is just visible inside (the flange work happens from within) */}
        <rect x={580.6} y={460} width={4.8} height={10} rx={2} fill={ESPRESSO_INK} fillOpacity="0.82" stroke={ESPRESSO_INK} strokeWidth="0.5" />
        <line x1={582.2} y1={461.5} x2={583.8} y2={461.5} stroke={IVORY_PAPER} strokeWidth="0.35" strokeOpacity="0.75" />
        <line x1={582.2} y1={463} x2={583.8} y2={463} stroke={IVORY_PAPER} strokeWidth="0.35" strokeOpacity="0.75" />
        <line x1={582.2} y1={464.5} x2={583.8} y2={464.5} stroke={IVORY_PAPER} strokeWidth="0.35" strokeOpacity="0.75" />
        <rect x={581.8} y={466.9} width={2.4} height={3.1} rx={0.9} fill={HI_VIS_ORANGE} stroke={ESPRESSO_INK} strokeWidth="0.3" />
        <circle cx={583} cy={466.2} r={0.9} fill={IVORY_PAPER} stroke={ESPRESSO_INK} strokeWidth="0.3" />
        <text x={583} y={450} textAnchor="middle" fontFamily="Inter, sans-serif" fontSize={4.5} fontWeight="700" fill={ESPRESSO_INK}>1/3</text>
        {/* torqued base anchor bolts */}
        {[[573.6, 471], [577.6, 471.5], [583, 471.7], [588.4, 471.5], [592.4, 471]].map(([bx, by]) => (
          <circle key={`s1-bolt-${bx}`} cx={bx} cy={by} r={0.6} fill={ESPRESSO_INK} fillOpacity="0.85" />
        ))}
        <ellipse cx={583} cy={422} rx={9.4} ry={1.9} fill={SLATE_LIGHT} stroke={ESPRESSO_INK} strokeWidth="0.5" />
      </g>

      {/* Section 2/3 — lands on 1/3 in phase 2 */}
      <g className="login-built-piece login-built-s2" opacity={0}>
        <polygon points="573.8,422 592.2,422 590.5,376 575.5,376" fill="url(#intaglio-v)" stroke={ESPRESSO_INK} strokeWidth="0.7" />
        <path d="M 587.6 376 L 589.3 422 L 592.2 422 L 590.5 376 Z" fill={GROUND_SHADOW} opacity="0.25" />
        <line x1={574.7} y1={394} x2={591.3} y2={394} stroke={ESPRESSO_INK} strokeWidth="0.4" strokeOpacity="0.45" />
        <line x1={574.2} y1={408} x2={591.8} y2={408} stroke={ESPRESSO_INK} strokeWidth="0.4" strokeOpacity="0.45" />
        <text x={583} y={402} textAnchor="middle" fontFamily="Inter, sans-serif" fontSize={4.5} fontWeight="700" fill={ESPRESSO_INK}>2/3</text>
        {[[575.6, 423], [579, 423.5], [583, 423.7], [587, 423.5], [590.4, 423]].map(([bx, by]) => (
          <circle key={`s2-bolt-${bx}`} cx={bx} cy={by} r={0.6} fill={ESPRESSO_INK} fillOpacity="0.85" />
        ))}
        <ellipse cx={583} cy={376} rx={7.7} ry={1.8} fill={SLATE_LIGHT} stroke={ESPRESSO_INK} strokeWidth="0.5" />
      </g>

      {/* Section 3/3 — lands on 2/3 in phase 3 */}
      <g className="login-built-piece login-built-s3" opacity={0}>
        <polygon points="577,334 589,334 590.5,376 575.5,376" fill="url(#intaglio-v)" stroke={ESPRESSO_INK} strokeWidth="0.7" />
        <path d="M 586.6 334 L 587.9 376 L 590.5 376 L 589 334 Z" fill={GROUND_SHADOW} opacity="0.25" />
        <line x1={576.4} y1={355} x2={589.6} y2={355} stroke={ESPRESSO_INK} strokeWidth="0.4" strokeOpacity="0.45" />
        <text x={583} y={362} textAnchor="middle" fontFamily="Inter, sans-serif" fontSize={4.5} fontWeight="700" fill={ESPRESSO_INK}>3/3</text>
        {[[577, 377], [579.6, 377.5], [583, 377.7], [586.4, 377.5], [589, 377]].map(([bx, by]) => (
          <circle key={`s3-bolt-${bx}`} cx={bx} cy={by} r={0.6} fill={ESPRESSO_INK} fillOpacity="0.85" />
        ))}
        <ellipse cx={583} cy={334} rx={6.2} ry={1.5} fill={SLATE_LIGHT} stroke={ESPRESSO_INK} strokeWidth="0.5" />
      </g>

      {/* Nacelle — flown to the tower top in phase 4 */}
      <g className="login-built-piece login-built-nac" opacity={0}>
        <rect x={572} y={326} width={22} height={8} rx={2} fill="url(#intaglio-h)" stroke={ESPRESSO_INK} strokeWidth="0.7" />
        <line x1={573.5} y1={327.6} x2={592.5} y2={327.6} stroke={CANDLELIGHT} strokeWidth="0.6" strokeOpacity="0.8" />
        <circle cx={571.5} cy={330} r={2.6} fill={SLATE_DARK} stroke={ESPRESSO_INK} strokeWidth="0.6" />
        <circle cx={571.5} cy={330} r={0.8} fill={ESPRESSO_INK} />
        <circle cx={592} cy={324.9} r={0.8} fill={RED_AVIATION} className="login-aviation-light" />
      </g>

      {/* Rotor — attaches PARKED once the nacelle is landed; spins only on
          auth success. The translate wrapper stays OUTSIDE login-rotor-hub so
          the success spin (a CSS transform) doesn't evict the positioning;
          the balance circle squares the bbox so the pivot = hub centre. */}
      <g className="login-built-piece login-built-rotor" opacity={0}>
        <g transform="translate(571.5 330)">
          <g className="login-rotor-hub">
            <circle r={39} fill="none" stroke="none" />
            <RotorBlades len={37} />
            <circle r={2.4} fill={SLATE_PANEL} stroke={ESPRESSO_INK} strokeWidth="0.55" />
            <circle r={1} fill={ESPRESSO_INK} />
          </g>
        </g>
      </g>
    </g>
  );
}

/** The ground rigger guiding the suspended load with a TAG LINE — standard
 *  rigging practice: nobody rides or climbs under a suspended lift; the load
 *  is steered from the ground. The rope is pinned at his hands (527,457) and
 *  rotate+scaled so its far end tracks the hook block through rise + carry
 *  (CSS .login-tagline). On auth error he shifts on his feet (loginBob). */
function TaglineRigger() {
  return (
    <g>
      {/* tag line — drawn at the carry pose (hands → hook bottom over the stack) */}
      <g className="login-tagline" opacity={0} style={{ transformOrigin: '527px 457px' }}>
        <line x1={527} y1={457} x2={583} y2={287} stroke={ESPRESSO_INK} strokeWidth="0.55" strokeLinecap="round" />
      </g>
      <g className="login-tagline-holder">
        <ellipse cx={524} cy={471} rx={3.2} ry={0.8} fill={ESPRESSO_INK} opacity="0.22" />
        {/* boots + legs */}
        <rect x={522.2} y={468.9} width={1.7} height={1.1} rx={0.3} fill={INK_DEEP} />
        <rect x={524.4} y={468.9} width={1.7} height={1.1} rx={0.3} fill={INK_DEEP} />
        <path d="M 522.9 464.6 L 522.5 469 L 523.7 469 L 523.9 464.6 Z" fill={SLATE_DARK} stroke={ESPRESSO_INK} strokeWidth="0.3" />
        <path d="M 524.3 464.6 L 524.7 469 L 525.9 469 L 525.5 464.6 Z" fill={SLATE_DARK} stroke={ESPRESSO_INK} strokeWidth="0.3" />
        {/* torso + reflective bands */}
        <path d="M 522.5 459.6 Q 522.2 460.4 522.35 461.2 L 522.8 464.9 L 525.7 464.9 L 526 461.2 Q 526.15 460.4 525.9 459.6 Z" fill={HI_VIS_ORANGE} stroke={ESPRESSO_INK} strokeWidth="0.35" />
        <rect x={522.6} y={461.6} width={3.2} height={0.7} fill={IVORY_PAPER} opacity="0.95" />
        <rect x={522.7} y={463.4} width={3} height={0.55} fill={IVORY_PAPER} opacity="0.7" />
        {/* both hands up on the line */}
        <path d="M 525.5 460.2 L 527.2 457.6 L 526.4 456.9 L 524.7 459.6 Z" fill={HI_VIS_ORANGE} stroke={ESPRESSO_INK} strokeWidth="0.3" />
        <circle cx={527} cy={457.2} r={0.65} fill={SKIN_MID} />
        <path d="M 523 460.2 L 526 458.6 L 525.6 457.7 L 522.6 459.4 Z" fill={HI_VIS_ORANGE} stroke={ESPRESSO_INK} strokeWidth="0.3" />
        <circle cx={526} cy={458.1} r={0.65} fill={SKIN_MID} />
        {/* head + helmet */}
        <circle cx={524.1} cy={458.3} r={1.05} fill={SKIN_MID} />
        <path d="M 522.9 457.9 Q 524.1 456.2 525.3 457.9 Z" fill={IVORY_PAPER} stroke={ESPRESSO_INK} strokeWidth="0.3" />
        <ellipse cx={524.1} cy={457.95} rx={1.55} ry={0.35} fill={IVORY_PAPER} stroke={ESPRESSO_INK} strokeWidth="0.25" />
      </g>
    </g>
  );
}

function CompletedTurbine({ x, y, scale = 1 }: { x: number; y: number; scale?: number }) {
  const s = scale;
  return (
    <g opacity="0.7">
      {/* Tower */}
      <polygon points={`${x - 2 * s},${y} ${x + 2 * s},${y} ${x + 1 * s},${y - 90 * s} ${x - 1 * s},${y - 90 * s}`} fill="url(#intaglio-v)" stroke={ESPRESSO_INK} strokeWidth={0.5 * s} />
      {/* Nacelle */}
      <rect x={x - 6 * s} y={y - 95 * s} width={12 * s} height={4 * s} rx={1 * s} fill={ESPRESSO_INK} />
      {/* Rotor — this turbine is COMPLETE, so it turns: slow, same direction
          as every other running machine. The invisible balance circle squares
          the bbox so the fill-box pivot lands exactly on the hub centre. */}
      <g transform={`translate(${x + 6 * s} ${y - 93 * s})`}>
        <g className="login-blade-spin-slow">
          <circle r={31 * s} fill="none" stroke="none" />
          <RotorBlades len={30 * s} detail={false} />
          <circle r={2 * s} fill={SLATE_PANEL} stroke={ESPRESSO_INK} strokeWidth={0.5 * s} />
          <circle r={0.9 * s} fill={ESPRESSO_INK} />
        </g>
      </g>
    </g>
  );
}

function BladeOnCradles({ x, y, scale = 1 }: { x: number; y: number; scale?: number }) {
  const s = scale;
  return (
    <g>
      {/* Cradle 1 */}
      <path d={`M ${x - 40 * s} ${y} L ${x - 36 * s} ${y - 8 * s} L ${x - 28 * s} ${y - 8 * s} L ${x - 24 * s} ${y}`} fill={BRAND_GREEN} stroke={ESPRESSO_INK} strokeWidth={0.5 * s} />
      {/* Cradle 2 */}
      <path d={`M ${x + 24 * s} ${y} L ${x + 28 * s} ${y - 8 * s} L ${x + 36 * s} ${y - 8 * s} L ${x + 40 * s} ${y}`} fill={BRAND_GREEN} stroke={ESPRESSO_INK} strokeWidth={0.5 * s} />
      {/* Blade — long teardrop horizontal */}
      <path
        d={`M ${x - 50 * s} ${y - 10 * s}
            Q ${x - 20 * s} ${y - 14 * s} ${x + 40 * s} ${y - 11 * s}
            L ${x + 48 * s} ${y - 9 * s}
            Q ${x + 0 * s} ${y - 7 * s} ${x - 50 * s} ${y - 9 * s} Z`}
        fill="url(#intaglio-h)"
        stroke={ESPRESSO_INK}
        strokeWidth={0.6 * s}
      />
      {/* red tip bands (tip = narrow left end) */}
      <rect x={x - 49.8 * s} y={y - 10.2 * s} width={5 * s} height={1.25 * s} fill={RED_AVIATION} fillOpacity="0.95" />
      <rect x={x - 42.5 * s} y={y - 10.55 * s} width={2.6 * s} height={1.65 * s} fill={RED_AVIATION} fillOpacity="0.95" />
      {/* Strap */}
      <rect x={x - 32 * s} y={y - 14 * s} width={2 * s} height={8 * s} fill={ESPRESSO_INK} />
      <rect x={x + 30 * s} y={y - 14 * s} width={2 * s} height={8 * s} fill={ESPRESSO_INK} />
    </g>
  );
}

function FoundationPad({ x, y, scale = 1 }: { x: number; y: number; scale?: number }) {
  const s = scale;
  const bolts = Array.from({ length: 12 }, (_, i) => {
    const a = (i / 12) * Math.PI * 2;
    return { x: x + 16 * s * Math.cos(a), y: y - 3 * s + 1.5 * s * Math.sin(a) };
  });
  return (
    <g>
      {/* Ellipse pad (top-down perspective) */}
      <ellipse cx={x} cy={y} rx={22 * s} ry={5 * s} fill="url(#intaglio-cross)" stroke={ESPRESSO_INK} strokeWidth={0.6 * s} />
      <ellipse cx={x} cy={y - 2 * s} rx={22 * s} ry={5 * s} fill={IVORY_PAPER} stroke={ESPRESSO_INK} strokeWidth={0.6 * s} />
      {/* Anchor-bolt cage with red caps */}
      {bolts.map((b, i) => (
        <g key={i}>
          <rect x={b.x - 0.6 * s} y={b.y - 1.6 * s} width={1.2 * s} height={2 * s} fill={ESPRESSO_INK} />
          <circle cx={b.x} cy={b.y - 2.2 * s} r={1 * s} fill={RED_AVIATION} />
        </g>
      ))}
      {/* Rebar tails poking out */}
      {[-18, -12, -6, 6, 12, 18].map((dx) => (
        <line key={dx} x1={x + dx * s} y1={y - 5 * s} x2={x + dx * s} y2={y - 9 * s} stroke={ESPRESSO_INK} strokeWidth={0.4 * s} />
      ))}
    </g>
  );
}

function MetMast({ x, y, scale = 1 }: { x: number; y: number; scale?: number }) {
  const s = scale;
  return (
    <g opacity="0.85">
      {/* Mast */}
      <line x1={x} y1={y} x2={x} y2={y - 60 * s} stroke={ESPRESSO_INK} strokeWidth={1.2 * s} />
      {/* Cross bracing — 6 X's */}
      {Array.from({ length: 6 }).map((_, i) => {
        const yT = y - (i + 1) * 10 * s;
        const yB = y - i * 10 * s;
        return (
          <g key={i}>
            <line x1={x - 1.4 * s} y1={yB} x2={x + 1.4 * s} y2={yT} stroke={ESPRESSO_INK} strokeWidth={0.3 * s} />
            <line x1={x + 1.4 * s} y1={yB} x2={x - 1.4 * s} y2={yT} stroke={ESPRESSO_INK} strokeWidth={0.3 * s} />
          </g>
        );
      })}
      {/* Guy wires */}
      <line x1={x} y1={y - 50 * s} x2={x - 24 * s} y2={y} stroke={ESPRESSO_INK} strokeWidth={0.3 * s} />
      <line x1={x} y1={y - 50 * s} x2={x + 24 * s} y2={y} stroke={ESPRESSO_INK} strokeWidth={0.3 * s} />
      {/* Two anemometer cups */}
      <g className="login-anemometer" transform={`translate(${x - 4 * s} ${y - 64 * s})`}>
        <circle r={0.6 * s} fill="none" stroke="none" />
        <circle cx={1.6 * s} cy={0} r={0.8 * s} fill={ESPRESSO_INK} />
        <circle cx={-1.6 * s} cy={0} r={0.8 * s} fill={ESPRESSO_INK} />
      </g>
      {/* Wind vane */}
      <g className="login-vane" transform={`translate(${x + 4 * s} ${y - 64 * s})`}>
        <rect x={-2 * s} y={-1 * s} width={4 * s} height={0.4 * s} fill="none" stroke="none" />
        <path d={`M 0 0 L ${3 * s} ${-1 * s} L ${3 * s} ${1 * s} Z`} fill={ESPRESSO_INK} />
      </g>
      {/* Solar + datalogger at base */}
      <rect x={x - 6 * s} y={y - 1 * s} width={6 * s} height={2 * s} fill={SLATE_DARK} stroke={ESPRESSO_INK} strokeWidth={0.3 * s} />
      <rect x={x + 2 * s} y={y - 2 * s} width={4 * s} height={2 * s} fill={CHAMPAGNE} stroke={ESPRESSO_INK} strokeWidth={0.3 * s} />
    </g>
  );
}

/** Shared small site-worker body: proper legs + boots, fitted jacket with
 *  sloped shoulders and real arms, reflective bands, neck, head, helmet with
 *  dome + brim. Feet anchored at (x, y); ~28·s tall to match prior footprint. */
function SiteWorker({
  x, y, scale = 1, suit, trousers, helmet, rightArm = 'down',
}: {
  x: number; y: number; scale?: number; suit: string; trousers?: string; helmet: string;
  rightArm?: 'down' | 'raised' | 'reach';
}) {
  const s = scale;
  const legs = trousers ?? suit;
  const hipY = y - 12 * s;
  const shoY = y - 21 * s;
  const headCY = y - 25 * s;
  return (
    <g>
      <ellipse cx={x} cy={y + 1 * s} rx={5.5 * s} ry={1.2 * s} fill={ESPRESSO_INK} opacity="0.24" />
      {/* boots */}
      <rect x={x - 3.4 * s} y={y - 2 * s} width={3 * s} height={2 * s} rx={0.5 * s} fill={INK_DEEP} />
      <rect x={x + 0.4 * s} y={y - 2 * s} width={3 * s} height={2 * s} rx={0.5 * s} fill={INK_DEEP} />
      {/* tapered trouser legs with ankle reflective cuffs */}
      <path d={`M ${x - 2.8 * s} ${hipY} L ${x - 3 * s} ${y - 2 * s} L ${x - 0.8 * s} ${y - 2 * s} L ${x - 0.4 * s} ${hipY} Z`} fill={legs} stroke={ESPRESSO_INK} strokeWidth={0.35 * s} />
      <path d={`M ${x + 0.4 * s} ${hipY} L ${x + 0.8 * s} ${y - 2 * s} L ${x + 3 * s} ${y - 2 * s} L ${x + 2.8 * s} ${hipY} Z`} fill={legs} stroke={ESPRESSO_INK} strokeWidth={0.35 * s} />
      <rect x={x - 3.1 * s} y={y - 5 * s} width={2.5 * s} height={0.8 * s} fill={IVORY_PAPER} opacity="0.9" />
      <rect x={x + 0.7 * s} y={y - 5 * s} width={2.5 * s} height={0.8 * s} fill={IVORY_PAPER} opacity="0.9" />
      {/* torso with sloped shoulders */}
      <path d={`M ${x - 3.6 * s} ${shoY} Q ${x - 4.1 * s} ${shoY + 1.2 * s} ${x - 3.8 * s} ${shoY + 3 * s} L ${x - 3.2 * s} ${hipY} L ${x + 3.2 * s} ${hipY} L ${x + 3.8 * s} ${shoY + 3 * s} Q ${x + 4.1 * s} ${shoY + 1.2 * s} ${x + 3.6 * s} ${shoY} Z`} fill={suit} stroke={ESPRESSO_INK} strokeWidth={0.35 * s} />
      {/* reflective bands */}
      <rect x={x - 3.7 * s} y={shoY + 3.4 * s} width={7.4 * s} height={0.9 * s} fill={IVORY_PAPER} opacity="0.95" />
      <rect x={x - 3.4 * s} y={shoY + 6.4 * s} width={6.8 * s} height={0.7 * s} fill={IVORY_PAPER} opacity="0.7" />
      {/* belt + chest radio */}
      <rect x={x - 3.2 * s} y={hipY - 1 * s} width={6.4 * s} height={1 * s} fill={INK_DEEP} opacity="0.85" />
      <rect x={x + 1.6 * s} y={shoY + 1.4 * s} width={1.4 * s} height={2.4 * s} rx={0.3 * s} fill={INK_DEEP} />
      <line x1={x + 2.3 * s} y1={shoY + 1.4 * s} x2={x + 2.3 * s} y2={shoY + 0.2 * s} stroke={INK_DEEP} strokeWidth={0.35 * s} />
      {/* left arm down */}
      <path d={`M ${x - 3.6 * s} ${shoY + 0.6 * s} L ${x - 4.6 * s} ${shoY + 7.6 * s} L ${x - 3.4 * s} ${shoY + 7.9 * s} L ${x - 2.5 * s} ${shoY + 1.2 * s} Z`} fill={suit} stroke={ESPRESSO_INK} strokeWidth={0.3 * s} />
      <circle cx={x - 4 * s} cy={shoY + 8.6 * s} r={1 * s} fill={SKIN_MID} />
      {/* right arm per pose — raised/reach arms swing about the shoulder so the figure reads as working */}
      {rightArm === 'raised' ? (
        <g className="login-signal-arm" style={{ transformOrigin: `${x + 3 * s}px ${shoY + 0.8 * s}px` }}>
          <path d={`M ${x + 3.4 * s} ${shoY + 0.8 * s} L ${x + 7 * s} ${shoY - 6.4 * s} L ${x + 5.9 * s} ${shoY - 7 * s} L ${x + 2.4 * s} ${shoY + 0.2 * s} Z`} fill={suit} stroke={ESPRESSO_INK} strokeWidth={0.3 * s} />
          <circle cx={x + 6.6 * s} cy={shoY - 7.4 * s} r={1.1 * s} fill={SKIN_MID} />
        </g>
      ) : rightArm === 'reach' ? (
        <g className="login-work-arm" style={{ transformOrigin: `${x + 3 * s}px ${shoY + 1 * s}px` }}>
          <path d={`M ${x + 3.4 * s} ${shoY + 0.8 * s} L ${x + 8 * s} ${shoY + 4.6 * s} L ${x + 7.3 * s} ${shoY + 5.7 * s} L ${x + 2.6 * s} ${shoY + 1.8 * s} Z`} fill={suit} stroke={ESPRESSO_INK} strokeWidth={0.3 * s} />
          <circle cx={x + 8 * s} cy={shoY + 5.6 * s} r={1 * s} fill={SKIN_MID} />
        </g>
      ) : (
        <>
          <path d={`M ${x + 3.6 * s} ${shoY + 0.6 * s} L ${x + 4.6 * s} ${shoY + 7.6 * s} L ${x + 3.4 * s} ${shoY + 7.9 * s} L ${x + 2.5 * s} ${shoY + 1.2 * s} Z`} fill={suit} stroke={ESPRESSO_INK} strokeWidth={0.3 * s} />
          <circle cx={x + 4 * s} cy={shoY + 8.6 * s} r={1 * s} fill={SKIN_MID} />
        </>
      )}
      {/* neck + head */}
      <rect x={x - 1 * s} y={y - 23.4 * s} width={2 * s} height={1.8 * s} fill={SKIN_MID} />
      <circle cx={x} cy={headCY} r={2.5 * s} fill={SKIN_MID} />
      {/* helmet: dome + brim + band */}
      <path d={`M ${x - 3 * s} ${headCY - 0.8 * s} Q ${x} ${headCY - 5.6 * s} ${x + 3 * s} ${headCY - 0.8 * s} Z`} fill={helmet} stroke={ESPRESSO_INK} strokeWidth={0.35 * s} />
      <ellipse cx={x} cy={headCY - 0.7 * s} rx={3.9 * s} ry={0.8 * s} fill={helmet} stroke={ESPRESSO_INK} strokeWidth={0.3 * s} />
      <rect x={x - 3 * s} y={headCY - 1.7 * s} width={6 * s} height={0.7 * s} fill={ESPRESSO_INK} opacity="0.35" />
    </g>
  );
}

function Rigger({ x, y, scale = 1, armRaised = false }: { x: number; y: number; scale?: number; armRaised?: boolean }) {
  return (
    <g className="login-bob-slow">
      <SiteWorker x={x} y={y} scale={scale} suit={HI_VIS_ORANGE} helmet={HI_VIS_ORANGE} rightArm={armRaised ? 'raised' : 'down'} />
    </g>
  );
}

function Surveyor({ x, y, scale = 1 }: { x: number; y: number; scale?: number }) {
  const s = scale;
  return (
    <g className="login-bob">
      <SiteWorker x={x} y={y} scale={s} suit={YELLOW_WARNING} trousers={SLATE_DARK} helmet={IVORY_PAPER} rightArm="reach" />
      {/* total station on tripod */}
      <g transform={`translate(${x + 16 * s} ${y - 4 * s})`}>
        <line x1={-4 * s} y1={4 * s} x2={0} y2={-10 * s} stroke={ESPRESSO_INK} strokeWidth={0.6 * s} />
        <line x1={4 * s} y1={4 * s} x2={0} y2={-10 * s} stroke={ESPRESSO_INK} strokeWidth={0.6 * s} />
        <line x1={0} y1={4 * s} x2={0} y2={-10 * s} stroke={ESPRESSO_INK} strokeWidth={0.6 * s} />
        <rect x={-3 * s} y={-14 * s} width={6 * s} height={5 * s} fill={CHAMPAGNE} stroke={ESPRESSO_INK} strokeWidth={0.4 * s} />
        <circle cx={3 * s} cy={-12 * s} r={1 * s} fill={ESPRESSO_INK} />
      </g>
    </g>
  );
}

function Electrician({ x, y, scale = 1 }: { x: number; y: number; scale?: number }) {
  const s = scale;
  return (
    <g className="login-bob-slow">
      <SiteWorker x={x} y={y} scale={s} suit={RACING_GREEN} trousers={SLATE_DARK} helmet={CHAMPAGNE} rightArm="reach" />
      {/* cable coil at the feet */}
      <ellipse cx={x - 5 * s} cy={y - 2 * s} rx={2.2 * s} ry={1.1 * s} fill="none" stroke={ESPRESSO_INK} strokeWidth={0.4 * s} />
      <ellipse cx={x - 5 * s} cy={y - 0.8 * s} rx={2.2 * s} ry={1.1 * s} fill="none" stroke={ESPRESSO_INK} strokeWidth={0.4 * s} />
      {/* multimeter in the reaching hand */}
      <rect x={x + 7 * s} y={y - 16.5 * s} width={3 * s} height={4 * s} rx={0.4 * s} fill={CHAMPAGNE} stroke={ESPRESSO_INK} strokeWidth={0.3 * s} />
    </g>
  );
}

function GeneratorSkid({ x, y, scale = 1 }: { x: number; y: number; scale?: number }) {
  const s = scale;
  return (
    <g>
      <ellipse cx={x} cy={y + 1 * s} rx={18 * s} ry={1.4 * s} fill={ESPRESSO_INK} opacity="0.22" />
      <rect x={x - 16 * s} y={y - 14 * s} width={32 * s} height={14 * s} rx={1 * s} fill={YELLOW_WARNING} stroke={ESPRESSO_INK} strokeWidth={0.6 * s} />
      {Array.from({ length: 6 }).map((_, i) => (
        <line key={i} x1={x - 14 * s + i * 4.5 * s} y1={y - 12 * s} x2={x - 14 * s + i * 4.5 * s} y2={y - 4 * s} stroke={ESPRESSO_INK} strokeWidth={0.4 * s} />
      ))}
      <rect x={x + 12 * s} y={y - 22 * s} width={3 * s} height={8 * s} fill={SLATE_DARK} />
    </g>
  );
}

function FuelBowser({ x, y, scale = 1 }: { x: number; y: number; scale?: number }) {
  const s = scale;
  return (
    <g>
      <ellipse cx={x} cy={y + 1 * s} rx={26 * s} ry={2 * s} fill={ESPRESSO_INK} opacity="0.22" />
      <rect x={x - 22 * s} y={y - 14 * s} width={44 * s} height={10 * s} rx={4 * s} fill={IVORY_PAPER} stroke={ESPRESSO_INK} strokeWidth={0.6 * s} />
      <rect x={x - 26 * s} y={y - 16 * s} width={6 * s} height={12 * s} rx={1 * s} fill={CHAMPAGNE} stroke={ESPRESSO_INK} strokeWidth={0.5 * s} />
      <path d={`M ${x + 10 * s} ${y - 9 * s} L ${x + 13 * s} ${y - 12 * s} L ${x + 16 * s} ${y - 9 * s} L ${x + 13 * s} ${y - 6 * s} Z`} fill={RED_AVIATION} stroke={ESPRESSO_INK} strokeWidth={0.3 * s} />
      <circle cx={x - 16 * s} cy={y - 1 * s} r={2.4 * s} fill={SLATE_DARK} />
      <circle cx={x + 16 * s} cy={y - 1 * s} r={2.4 * s} fill={SLATE_DARK} />
    </g>
  );
}

function Telehandler({ x, y, scale = 1 }: { x: number; y: number; scale?: number }) {
  const s = scale;
  return (
    <g>
      <ellipse cx={x} cy={y + 1 * s} rx={18 * s} ry={1.4 * s} fill={ESPRESSO_INK} opacity="0.22" />
      <rect x={x - 14 * s} y={y - 12 * s} width={20 * s} height={10 * s} rx={1 * s} fill={YELLOW_WARNING} stroke={ESPRESSO_INK} strokeWidth={0.5 * s} />
      <rect x={x - 14 * s} y={y - 20 * s} width={10 * s} height={8 * s} rx={1 * s} fill={SLATE_DARK} />
      <rect x={x - 12 * s} y={y - 18 * s} width={6 * s} height={5 * s} fill={CHAMPAGNE} opacity="0.7" />
      <rect x={x + 4 * s} y={y - 18 * s} width={26 * s} height={3 * s} rx={0.5 * s} fill={YELLOW_WARNING} stroke={ESPRESSO_INK} strokeWidth={0.4 * s} transform={`rotate(-20 ${x + 4 * s} ${y - 18 * s})`} />
      <rect x={x + 24 * s} y={y - 26 * s} width={10 * s} height={4 * s} fill={IVORY_PAPER} stroke={ESPRESSO_INK} strokeWidth={0.4 * s} />
      <circle cx={x - 10 * s} cy={y - 1 * s} r={2.4 * s} fill={SLATE_DARK} />
      <circle cx={x + 4 * s} cy={y - 1 * s} r={2.4 * s} fill={SLATE_DARK} />
    </g>
  );
}

function BladeTransport({ x, y, scale = 1 }: { x: number; y: number; scale?: number }) {
  const s = scale;
  return (
    <g>
      <ellipse cx={x} cy={y + 1 * s} rx={60 * s} ry={2 * s} fill={ESPRESSO_INK} opacity="0.22" />
      <rect x={x - 56 * s} y={y - 14 * s} width={10 * s} height={12 * s} rx={1 * s} fill={RED_AVIATION} stroke={ESPRESSO_INK} strokeWidth={0.5 * s} />
      <rect x={x - 46 * s} y={y - 6 * s} width={100 * s} height={4 * s} fill={SLATE_DARK} />
      <path d={`M ${x - 44 * s} ${y - 8 * s} Q ${x} ${y - 10 * s} ${x + 50 * s} ${y - 8 * s} L ${x + 52 * s} ${y - 7 * s} Q ${x} ${y - 6 * s} ${x - 44 * s} ${y - 7 * s} Z`} fill="url(#intaglio-h)" stroke={ESPRESSO_INK} strokeWidth={0.4 * s} />
      <circle cx={x + 52 * s} cy={y - 10 * s} r={1.4 * s} fill={CHAMPAGNE} className="login-aviation-light" />
      <rect x={x + 50 * s} y={y - 14 * s} width={6 * s} height={3 * s} fill={RED_AVIATION} stroke={ESPRESSO_INK} strokeWidth={0.3 * s} />
      {[-50, -42, -8, 0, 8, 40, 48].map((dx) => (
        <circle key={dx} cx={x + dx * s} cy={y - 1 * s} r={1.6 * s} fill={SLATE_DARK} />
      ))}
    </g>
  );
}

function HerasFence({ x, y, width, scale = 1 }: { x: number; y: number; width: number; scale?: number }) {
  const s = scale;
  const posts = Math.ceil(width / (12 * s));
  return (
    <g stroke={SLATE_DARK} strokeWidth={0.5 * s}>
      <line x1={x} y1={y} x2={x + width} y2={y} strokeWidth={0.8 * s} />
      <line x1={x} y1={y - 14 * s} x2={x + width} y2={y - 14 * s} strokeWidth={0.8 * s} />
      {Array.from({ length: posts }).map((_, i) => (
        <line key={i} x1={x + (i + 1) * 12 * s} y1={y} x2={x + (i + 1) * 12 * s} y2={y - 14 * s} />
      ))}
      <rect x={x} y={y - 8 * s} width={width} height={8 * s} fill={HI_VIS_ORANGE} opacity="0.45" stroke="none" />
      {Array.from({ length: Math.floor(width / (2 * s)) }).map((_, i) => (
        <line key={i} x1={x + i * 2 * s} y1={y - 14 * s} x2={x + i * 2 * s + 4 * s} y2={y} stroke={SLATE_DARK} strokeOpacity="0.35" strokeWidth={0.3 * s} />
      ))}
    </g>
  );
}

function DangerSign({ x, y, scale = 1 }: { x: number; y: number; scale?: number }) {
  const s = scale;
  return (
    <g>
      <line x1={x} y1={y} x2={x} y2={y - 14 * s} stroke={ESPRESSO_INK} strokeWidth={0.7 * s} />
      <rect x={x - 7 * s} y={y - 22 * s} width={14 * s} height={8 * s} fill={RED_AVIATION} stroke={IVORY_PAPER} strokeWidth={0.4 * s} />
      <text x={x} y={y - 17 * s} textAnchor="middle" fontFamily="Inter, sans-serif" fontSize={3 * s} fontWeight="800" fill={IVORY_PAPER} letterSpacing="0.5">DANGER</text>
    </g>
  );
}

// ============================================================================
// DECORATIVE BACKGROUND (cream + blobs + halftone)
// ============================================================================

function DecorativeBackground() {
  return (
    <g>
      {/* Sky gradient base (warm cream) */}
      <rect width={VIEW_W} height={VIEW_H} fill="url(#login-sky-bg)" />

      {/* Dot pattern across whole canvas — premium dashboard texture */}
      <rect width={VIEW_W} height={VIEW_H} fill="url(#login-dot-pattern)" opacity="0.6" />

      {/* Ambient warmth in the upper-centre — non-directional so the sun
          can move freely across scenes without fighting the halo. */}
      <g className="login-blob-pulse">
        <circle cx={VIEW_W / 2} cy={120} r={360} fill="url(#login-blob-warm)" opacity="0.7" />
      </g>
    </g>
  );
}

// ============================================================================
// LANDSCAPE (sky tint, distant mountains, hills, ground)
// ============================================================================

function Landscape() {
  return (
    <g>
      {/* Sky tint near horizon */}
      <rect
        x={0}
        y={HORIZON - 100}
        width={VIEW_W}
        height={130}
        fill="url(#login-sky-haze)"
      />

      {/* Far mountains (3 layered) */}
      <path
        d={`M 0 ${HORIZON - 6}
            L 60 ${HORIZON - 60}
            L 120 ${HORIZON - 40}
            L 200 ${HORIZON - 80}
            L 280 ${HORIZON - 50}
            L 380 ${HORIZON - 90}
            L 460 ${HORIZON - 55}
            L 540 ${HORIZON - 85}
            L 620 ${HORIZON - 50}
            L 720 ${HORIZON - 75}
            L 820 ${HORIZON - 45}
            L ${VIEW_W} ${HORIZON - 65}
            L ${VIEW_W} ${HORIZON} L 0 ${HORIZON} Z`}
        fill={MOUNTAIN_DISTANT}
      />
      <path
        d={`M 0 ${HORIZON + 4}
            L 100 ${HORIZON - 40}
            L 220 ${HORIZON - 20}
            L 340 ${HORIZON - 60}
            L 460 ${HORIZON - 30}
            L 600 ${HORIZON - 50}
            L 740 ${HORIZON - 25}
            L 880 ${HORIZON - 45}
            L ${VIEW_W} ${HORIZON + 6}
            L ${VIEW_W} ${HORIZON + 8} L 0 ${HORIZON + 8} Z`}
        fill={MOUNTAIN_MID}
      />

      {/* Sage hills (mid layer) */}
      <path
        d={`M 0 ${HORIZON + 12}
            Q 140 ${HORIZON - 24} 300 ${HORIZON + 14}
            Q 460 ${HORIZON - 34} 620 ${HORIZON + 10}
            Q 780 ${HORIZON - 18} ${VIEW_W} ${HORIZON + 18}
            L ${VIEW_W} ${HORIZON + 26} L 0 ${HORIZON + 26} Z`}
        fill={HILL_SAGE}
      />
      <path
        d={`M 0 ${HORIZON + 24}
            Q 200 ${HORIZON} 420 ${HORIZON + 28}
            Q 640 ${HORIZON + 4} ${VIEW_W} ${HORIZON + 26}
            L ${VIEW_W} ${HORIZON + 34} L 0 ${HORIZON + 34} Z`}
        fill={HILL_SAGE_DEEP}
      />

      {/* Ground */}
      <rect x={0} y={HORIZON + 30} width={VIEW_W} height={VIEW_H - HORIZON - 30} fill={GROUND_TAUPE} />

      {/* Ground contour lines (topo) */}
      <g stroke={SLATE_DARK} strokeOpacity="0.06" strokeWidth="1" fill="none">
        <path d={`M 0 ${HORIZON + 70} Q 300 ${HORIZON + 60} 600 ${HORIZON + 80} T ${VIEW_W} ${HORIZON + 74}`} />
        <path d={`M 0 ${HORIZON + 120} Q 360 ${HORIZON + 110} 700 ${HORIZON + 132} T ${VIEW_W} ${HORIZON + 126}`} />
        <path d={`M 0 ${HORIZON + 168} Q 280 ${HORIZON + 158} 600 ${HORIZON + 178} T ${VIEW_W} ${HORIZON + 172}`} />
      </g>

      {/* Foreground hillock (where beacon sits) */}
      <path
        d={`M 130 ${HORIZON + 34}
            Q 220 ${HORIZON + 4} 320 ${HORIZON + 32}
            L 320 ${HORIZON + 34}
            Z`}
        fill={HILL_SAGE_DEEP}
        opacity="0.85"
      />
    </g>
  );
}

// ============================================================================
// CITY SKYLINE (right edge, distant)
// ============================================================================

function CitySkyline() {
  // x0 base
  const baseX = 760;
  const buildings = [
    { x: 0, w: 16, h: 70, fill: SLATE_DARK, op: 0.7 },
    { x: 20, w: 22, h: 100, fill: SLATE_DARK, op: 0.78 },
    { x: 46, w: 14, h: 82, fill: SLATE_DARK, op: 0.72 },
    { x: 64, w: 26, h: 120, fill: SLATE_DARK, op: 0.82 },
    { x: 94, w: 18, h: 92, fill: SLATE_DARK, op: 0.75 },
    { x: 116, w: 14, h: 68, fill: SLATE_DARK, op: 0.7 },
    { x: 134, w: 18, h: 88, fill: SLATE_DARK, op: 0.74 },
  ];

  return (
    <g transform={`translate(${baseX} 0)`}>
      {/* Buildings */}
      {buildings.map((b, i) => (
        <g key={`b-${i}`}>
          <rect x={b.x} y={HORIZON - b.h} width={b.w} height={b.h} fill={b.fill} opacity={b.op} />
          {/* Antenna on tallest */}
          {b.h > 110 && (
            <line x1={b.x + b.w / 2} y1={HORIZON - b.h} x2={b.x + b.w / 2} y2={HORIZON - b.h - 12} stroke={SLATE_DARK} strokeWidth="1" />
          )}
        </g>
      ))}

      {/* Window twinkles per building */}
      <g fill={SUN_ORANGE}>
        {/* Building 1 */}
        <rect x={4} y={HORIZON - 60} width={2.4} height={2.4} className="login-twinkle-1" />
        <rect x={10} y={HORIZON - 52} width={2.4} height={2.4} className="login-twinkle-2" />
        <rect x={4} y={HORIZON - 40} width={2.4} height={2.4} className="login-twinkle-3" />
        {/* Building 2 */}
        <rect x={24} y={HORIZON - 86} width={2.4} height={2.4} className="login-twinkle-4" />
        <rect x={32} y={HORIZON - 72} width={2.4} height={2.4} className="login-twinkle-1" />
        <rect x={26} y={HORIZON - 56} width={2.4} height={2.4} className="login-twinkle-2" />
        <rect x={36} y={HORIZON - 40} width={2.4} height={2.4} className="login-twinkle-3" />
        {/* Building 3 */}
        <rect x={50} y={HORIZON - 68} width={2.4} height={2.4} className="login-twinkle-4" />
        <rect x={56} y={HORIZON - 50} width={2.4} height={2.4} className="login-twinkle-1" />
        {/* Building 4 (tallest) */}
        <rect x={72} y={HORIZON - 106} width={2.4} height={2.4} className="login-twinkle-2" />
        <rect x={80} y={HORIZON - 90} width={2.4} height={2.4} className="login-twinkle-3" />
        <rect x={86} y={HORIZON - 74} width={2.4} height={2.4} className="login-twinkle-4" />
        <rect x={74} y={HORIZON - 58} width={2.4} height={2.4} className="login-twinkle-1" />
        <rect x={84} y={HORIZON - 42} width={2.4} height={2.4} className="login-twinkle-2" />
        {/* Building 5 */}
        <rect x={100} y={HORIZON - 74} width={2.4} height={2.4} className="login-twinkle-3" />
        <rect x={106} y={HORIZON - 54} width={2.4} height={2.4} className="login-twinkle-4" />
        {/* Building 6 */}
        <rect x={120} y={HORIZON - 50} width={2.4} height={2.4} className="login-twinkle-1" />
        {/* Building 7 */}
        <rect x={138} y={HORIZON - 70} width={2.4} height={2.4} className="login-twinkle-2" />
        <rect x={144} y={HORIZON - 50} width={2.4} height={2.4} className="login-twinkle-3" />
      </g>
    </g>
  );
}

// ============================================================================
// ROOT
// ============================================================================

/** Power-line helper for scenes 4 + 5 */
function PowerLineRun({
  poles,
  baseY,
  height,
  substationX,
  substationY,
}: {
  poles: number[];
  baseY: number;
  height: number;
  substationX: number;
  substationY: number;
}) {
  const wireY = baseY - height + 8;
  return (
    <g>
      {poles.map((px) => (
        <PowerPole key={`p-${px}`} x={px} baseY={baseY} height={height} />
      ))}
      {/* Catenary wires (3 per gap) */}
      <g fill="none" stroke={SLATE_DARK} strokeOpacity="0.7" strokeWidth="1.3">
        {poles.slice(0, -1).map((px, i) => {
          const nx = poles[i + 1];
          const sag = 10;
          return (
            <g key={`w-${i}`}>
              <path d={`M ${px - 18} ${wireY + 8} Q ${(px + nx) / 2} ${wireY + 8 + sag} ${nx - 18} ${wireY + 8}`} />
              <path d={`M ${px} ${wireY - 2} Q ${(px + nx) / 2} ${wireY - 2 + sag} ${nx} ${wireY - 2}`} />
              <path d={`M ${px + 18} ${wireY + 8} Q ${(px + nx) / 2} ${wireY + 8 + sag} ${nx + 18} ${wireY + 8}`} />
            </g>
          );
        })}
      </g>
      {/* Final arc into substation */}
      <path
        d={`M ${poles[poles.length - 1] + 18} ${wireY - 2}
            Q ${(poles[poles.length - 1] + substationX) / 2} ${wireY + 22}
            ${substationX - 40} ${substationY - 30}`}
        stroke={SLATE_DARK}
        strokeOpacity="0.7"
        strokeWidth="1.3"
        fill="none"
      />
      {/* Lime electricity sparks */}
      <g fill="none" stroke={BRAND_LIME} strokeWidth="3" strokeLinecap="round">
        {poles.slice(0, -1).map((px, i) => {
          const nx = poles[i + 1];
          const cls = i % 3 === 0 ? 'login-spark' : i % 3 === 1 ? 'login-spark-2' : 'login-spark-3';
          return (
            <path
              key={`sp-${i}`}
              className={cls}
              d={`M ${px} ${wireY - 2} Q ${(px + nx) / 2} ${wireY - 2 + 10} ${nx} ${wireY - 2}`}
            />
          );
        })}
      </g>
    </g>
  );
}


// ============================================================================
// WORLD CONSTANTS — panorama widths and section anchors
// ============================================================================
const WORLD_W = 2800;          // total panorama width
const SEC_DEPOT_X = 240;       // section anchor x (centre of depot scene)
const SEC_ROAD_A_X = 680;      // road between depot and Site 1
const SEC_SITE_1_X = 1200;     // Site 1 centre
const SEC_ROAD_B_X = 1720;     // road between Site 1 and Site 2
const SEC_SITE_2_X = 2200;     // Site 2 centre
const SEC_SUBSTATION_X = 2620; // substation + city centre

// ============================================================================
// EXTENDED LANDSCAPE — mountains + hills + ground across the full world width
// ============================================================================
function WorldLandscape() {
  return (
    <g>
      {/* Far mountains — peaks repeat across the world */}
      <path
        d={(() => {
          let d = `M 0 ${HORIZON}`;
          for (let x = 0; x <= WORLD_W; x += 100) {
            const dy = -45 - 30 * Math.abs(Math.sin(x / 120));
            d += ` L ${x} ${HORIZON + dy}`;
          }
          d += ` L ${WORLD_W} ${HORIZON} L 0 ${HORIZON} Z`;
          return d;
        })()}
        fill={MOUNTAIN_DISTANT}
      />
      {/* Mid mountains */}
      <path
        d={(() => {
          let d = `M 0 ${HORIZON + 6}`;
          for (let x = 0; x <= WORLD_W; x += 80) {
            const dy = -28 - 24 * Math.abs(Math.cos(x / 90));
            d += ` L ${x} ${HORIZON + 6 + dy}`;
          }
          d += ` L ${WORLD_W} ${HORIZON + 8} L 0 ${HORIZON + 8} Z`;
          return d;
        })()}
        fill={MOUNTAIN_MID}
      />
      {/* Sage hills */}
      <path
        d={(() => {
          let d = `M 0 ${HORIZON + 14}`;
          for (let x = 0; x <= WORLD_W; x += 60) {
            const dy = -14 - 12 * Math.abs(Math.sin(x / 70));
            d += ` L ${x} ${HORIZON + 14 + dy}`;
          }
          d += ` L ${WORLD_W} ${HORIZON + 16} L 0 ${HORIZON + 16} Z`;
          return d;
        })()}
        fill={HILL_SAGE}
      />
      {/* Mid hills front */}
      <path
        d={(() => {
          let d = `M 0 ${HORIZON + 24}`;
          for (let x = 0; x <= WORLD_W; x += 50) {
            const dy = -8 - 6 * Math.abs(Math.cos(x / 50));
            d += ` L ${x} ${HORIZON + 24 + dy}`;
          }
          d += ` L ${WORLD_W} ${HORIZON + 28} L 0 ${HORIZON + 28} Z`;
          return d;
        })()}
        fill={HILL_SAGE_DEEP}
      />
      {/* Ground */}
      <rect x={0} y={HORIZON + 28} width={WORLD_W} height={VIEW_H - HORIZON - 28} fill={GROUND_TAUPE} />
      {/* Subtle ground texture lines */}
      <g stroke={SLATE_DARK} strokeOpacity="0.05" strokeWidth="1" fill="none">
        <line x1={0} y1={HORIZON + 68} x2={WORLD_W} y2={HORIZON + 68} />
        <line x1={0} y1={HORIZON + 112} x2={WORLD_W} y2={HORIZON + 112} />
        <line x1={0} y1={HORIZON + 156} x2={WORLD_W} y2={HORIZON + 156} />
      </g>
    </g>
  );
}

// ============================================================================
// SORIGIN ENGRAVED WORDMARK — exact letterform paths from public/images/logo/
// logo.svg (viewBox 798×200), refilled as currency-note micro-line engraving
// in the brand greens.
// ============================================================================

const SORIGIN_LOGO_PATHS: string[] = [
  'M575.39 154.35V114.56C575.39 103.34 570.52 98.48 559.3 98.48H528.82C520.35 98.48 516.12 103.14 516.12 112.24C516.12 121.34 520.35 126.21 528.82 126.21H545.12C545.12 126.21 575.39 122.18 575.39 154.36V154.35Z',
  'M545.12 126.2V149.27C545.12 149.27 547.19 178.57 526.19 179.29C526.78 179.3 527.37 179.32 527.97 179.32C540.67 179.32 554.21 176.78 562.47 173.18C572.84 168.73 575.38 163.23 575.38 154.34C575.38 122.16 545.11 126.19 545.11 126.19L545.12 126.2Z',
  'M491.36 108.21C491.36 79.85 505.96 66.3 527.34 66.3C534.96 66.3 542.16 67.99 548.29 71.38C552.73 73.92 555.7 76.04 559.3 76.04C566.5 76.04 573.48 67.36 573.48 60.59C573.48 55.3 569.04 50.22 561.84 46.2C552.1 40.7 539.83 37.94 526.92 37.94C481.14 37.94 491.36 108.21 491.36 108.21Z',
  'M419.25 179.32C430.04 179.32 435.33 175.09 435.33 166.62V52.33C435.33 52.33 403.16 60.41 403.16 84.5V166.62C403.16 175.09 408.45 179.32 419.25 179.32Z',
  'M435.33 52.34C435.33 43.87 430.04 39.64 419.25 39.64C408.46 39.64 403.16 43.87 403.16 52.34V84.51C403.16 60.42 435.33 52.34 435.33 52.34Z',
  'M614.49 179.32C625.28 179.32 630.57 175.09 630.57 166.62V52.33C630.57 52.33 598.4 60.41 598.4 84.5V166.62C598.4 175.09 603.69 179.32 614.49 179.32Z',
  'M630.58 52.34C630.58 43.87 625.29 39.64 614.5 39.64C603.71 39.64 598.41 43.87 598.41 52.34V84.51C598.41 60.42 630.58 52.34 630.58 52.34Z',
  'M653.6 56.78V166.63C653.6 175.1 658.47 179.33 668.2 179.33C677.93 179.33 682.8 175.1 682.8 166.63V80.49C682.8 80.49 661.34 44.15 653.59 56.79L653.6 56.78Z',
  'M745.88 137L699.1 53.19C694.23 44.51 687.25 39.64 674.34 39.64C660.79 39.64 653.6 45.14 653.6 56.78C661.35 44.14 682.81 80.48 682.81 80.48L731.7 165.35C748.19 183.15 745.88 136.99 745.88 136.99V137Z',
  'M760.49 39.64C750.75 39.64 745.89 43.87 745.89 52.34V137C745.89 137 748.2 183.16 731.71 165.36C737 174.67 743.14 179.33 755.2 179.33C768.53 179.33 775.09 173.62 775.09 162.19V52.34C775.09 43.87 770.22 39.64 760.49 39.64Z',
  'M224.28 121.23C220.46 140.64 207.94 151.39 190.6 151.39C169.65 151.39 155.68 135.73 155.68 108C155.68 108 153.52 36.68 190.6 36.68C149.33 36.68 122.66 63.98 122.66 108C122.66 152.02 149.33 179.33 190.6 179.33C213.67 179.33 221.64 141.85 224.28 121.23Z',
  'M190.6 36.68C153.52 36.68 155.68 108 155.68 108C155.68 80.28 169.65 64.61 190.6 64.61C211.55 64.61 225.52 80.27 225.52 108C225.52 112.78 225.08 117.18 224.28 121.23C221.64 141.85 213.67 179.33 190.6 179.33C231.87 179.33 258.54 152.03 258.54 108C258.54 63.97 231.87 36.68 190.6 36.68Z',
  'M64.7404 89.61C74.1504 101.6 82.0904 109.79 80.9804 138.9C80.9804 146.31 73.7804 151.39 63.2004 151.39C53.8904 151.39 47.9604 147.58 41.4004 143.77C37.3804 141.44 34.6304 139.96 31.6604 139.96C24.6804 139.96 17.9004 149.06 17.9004 156.47C17.9004 161.34 20.8604 165.15 26.3704 168.96C35.6804 175.52 49.0204 179.33 62.9904 179.33C91.5604 179.33 113.76 163.65 112.73 137.21C111.35 101.97 82.2304 98.81 64.7504 89.62L64.7404 89.61Z',
  'M69.8495 91.67C57.7495 86.78 50.7695 83.03 50.7095 76.25C50.7095 76.25 39.9095 40.51 61.8995 37.28C36.4795 39.14 18.4595 54.91 18.9695 77.94C19.9695 123.47 87.9495 109.59 79.0395 145C82.9495 139.09 85.4695 133.78 86.9695 129.01C91.6695 114.07 88.3695 99.14 69.8495 91.66V91.67Z',
  'M52.7296 81.88C52.7296 81.88 46.4396 65.72 67.0096 65.03C74.1996 64.79 80.1296 67.57 86.9096 70.96C91.5696 73.29 93.8896 74.35 96.2196 74.35C103.63 74.35 109.77 65.25 109.77 58.05C109.77 53.18 107.23 50.01 101.52 46.41C92.4196 40.69 80.1396 37.1 67.0196 37.1C65.2796 37.1 63.5796 37.16 61.9096 37.29C39.9196 40.52 40.0396 65.15 52.7396 81.89L52.7296 81.88Z',
  'M545.12 149.27C539.62 150.75 534.33 151.39 529.46 151.39C507.02 151.39 491.36 137.42 491.36 108.21C491.36 108.21 481.13 37.94 526.92 37.94C488.19 37.94 458.35 62.7 458.35 108.84C458.35 154.98 487.91 178.57 526.2 179.28C547.19 178.56 545.13 149.26 545.13 149.26L545.12 149.27Z',
  'M375.96 172.21C375.96 174.26 375.3 175.86 373.56 177.08C371.56 178.54 368.14 179.32 362.72 179.32C351.21 179.32 347.28 175.77 341.7 167.63L332.14 153.85C331.83 153.41 331.47 152.83 330.96 152.1C330.35 151.17 329.58 150.05 328.66 148.79C328.35 148.35 328 147.86 327.64 147.38C321.55 139.15 315.58 130.91 307.03 133.65C307.03 123.91 313.42 121.63 325.29 120.59C340.16 119.29 350.25 134.61 350.25 134.61L366.67 156.23C373.37 165.14 375.98 168.5 375.98 172.2L375.96 172.21Z',
  'M322.97 108.16C356.84 105.26 361.11 57.43 347.03 45.01C371.22 54.44 379.49 79.05 370.37 104.08C354.27 148.24 293.87 113.65 288.37 155.23C287.01 147.46 287.35 142.02 287.35 136.3C287.35 111.9 308.39 109.19 322.98 108.16H322.97Z',
  'M336.26 104.08C345.49 99.93 348.9 77.24 331.64 69.83C323.23 66.22 307.15 66.81 300.25 68.02C293.26 65.5 290.82 59.14 293.64 51.31C295.55 46.01 299.19 43.42 306 41.45C316.83 38.33 329.83 38.6 342.21 43.06C343.85 43.65 345.43 44.3 346.96 45C367.04 59.39 356.22 94.17 336.26 104.07V104.08Z',
  'M306.63 67.41C306.63 67.41 305.21 39.54 316.77 39.54C307.69 39.54 296.08 40.6 289.74 42.5C276.65 46.3 276.44 54.96 276.44 63.19V166.66C276.44 175.11 281.51 179.33 291.85 179.33C302.19 179.33 307.26 175.11 307.26 166.66V134.35L306.63 108.38V67.42V67.41Z',
];

function SoriginEngravedLogo({ x, y, width = 190 }: { x: number; y: number; width?: number }) {
  const s = width / 798;
  return (
    <g transform={`translate(${x} ${y}) scale(${s})`}>
      <defs>
        <linearGradient id="sorigin-mark-grad" x1="0" y1="0" x2="798" y2="0" gradientUnits="userSpaceOnUse">
          <stop offset="0%" stopColor="#A6CE39" />
          <stop offset="45%" stopColor="#2E9777" />
          <stop offset="100%" stopColor="#076333" />
        </linearGradient>
        <clipPath id="sorigin-mark-clip">
          {SORIGIN_LOGO_PATHS.map((d, i) => (
            <path key={i} d={d} />
          ))}
        </clipPath>
        {/* engraving raster: two line weights, like intaglio banknote lettering */}
        <pattern id="sorigin-mark-lines" width="10" height="9" patternUnits="userSpaceOnUse">
          <line x1="0" y1="2" x2="10" y2="2" stroke="#ffffff" strokeWidth="2.6" />
          <line x1="0" y1="6.5" x2="10" y2="6.5" stroke="#ffffff" strokeWidth="1.3" />
        </pattern>
        <mask id="sorigin-mark-mask">
          <rect x="0" y="0" width="798" height="200" fill="url(#sorigin-mark-lines)" />
        </mask>
      </defs>
      {/* letterforms built from gradient hairlines */}
      <g clipPath="url(#sorigin-mark-clip)" mask="url(#sorigin-mark-mask)">
        <rect x="0" y="0" width="798" height="200" fill="url(#sorigin-mark-grad)" />
      </g>
      {/* fine contour keeps the letterforms crisp at small sizes */}
      {SORIGIN_LOGO_PATHS.map((d, i) => (
        <path key={i} d={d} fill="none" stroke="#076333" strokeWidth="2" strokeOpacity="0.5" />
      ))}
    </g>
  );
}

// ============================================================================
// CRAWLER CRANE — realistic LR-class wind-erection machine: crawler tracks,
// slewing superstructure, counterweight stack, A-frame backmast with pendant
// bars, long lattice main boom. Engraved flat style.
// ============================================================================

function CrawlerCrane({
  x, groundY, scale = 1, tipX, tipY,
}: {
  x: number; groundY: number; scale?: number; tipX: number; tipY: number;
}) {
  const s = scale;
  const gy = groundY;
  // undercarriage
  const trackW = 130 * s;
  const trackH = 24 * s;
  const tx0 = x - trackW / 2;
  const ty0 = gy - trackH;
  // superstructure
  const deckTop = ty0 - 26 * s;
  // boom pivot at the front of the deck
  const px = x + 42 * s;
  const py = deckTop + 6 * s;
  // backmast head
  const bmx = x - 14 * s;
  const bmy = deckTop - 92 * s;

  // lattice boom geometry
  const dx = tipX - px;
  const dy = tipY - py;
  const len = Math.hypot(dx, dy);
  const nx = -dy / len;
  const ny = dx / len;
  const wRoot = 6 * s;
  const wTip = 2.4 * s;
  const chordA = `M ${px + nx * wRoot} ${py + ny * wRoot} L ${tipX + nx * wTip} ${tipY + ny * wTip}`;
  const chordB = `M ${px - nx * wRoot} ${py - ny * wRoot} L ${tipX - nx * wTip} ${tipY - ny * wTip}`;
  const segs = 18;
  let lacing = '';
  for (let i = 0; i < segs; i++) {
    const f0 = i / segs;
    const f1 = (i + 1) / segs;
    const w0 = wRoot + (wTip - wRoot) * f0;
    const w1 = wRoot + (wTip - wRoot) * f1;
    const ax = px + dx * f0 + nx * w0;
    const ay = py + dy * f0 + ny * w0;
    const bx = px + dx * f1 - nx * w1;
    const by = py + dy * f1 - ny * w1;
    lacing += `M ${ax.toFixed(1)} ${ay.toFixed(1)} L ${bx.toFixed(1)} ${by.toFixed(1)} `;
  }

  return (
    <g>
      {/* crawler tracks */}
      <rect x={tx0} y={ty0} width={trackW} height={trackH} rx={trackH / 2} fill="url(#intaglio-cross)" stroke={ESPRESSO_INK} strokeWidth={0.9 * s} />
      <circle cx={tx0 + trackH / 2 + 1 * s} cy={ty0 + trackH / 2} r={7.5 * s} fill={IVORY_PAPER} stroke={ESPRESSO_INK} strokeWidth={0.7 * s} />
      <circle cx={tx0 + trackW - trackH / 2 - 1 * s} cy={ty0 + trackH / 2} r={7.5 * s} fill={IVORY_PAPER} stroke={ESPRESSO_INK} strokeWidth={0.7 * s} />
      {[0.28, 0.42, 0.56, 0.7].map((f) => (
        <circle key={f} cx={tx0 + trackW * f} cy={ty0 + trackH - 5.5 * s} r={3 * s} fill="none" stroke={ESPRESSO_INK} strokeWidth={0.5 * s} />
      ))}
      {Array.from({ length: 12 }, (_, i) => tx0 + 12 * s + i * 9 * s).map((gx) => (
        <line key={gx} x1={gx} y1={ty0 + 1.5 * s} x2={gx} y2={ty0 + 4 * s} stroke={ESPRESSO_INK} strokeWidth={0.45 * s} strokeOpacity="0.6" />
      ))}
      {/* car body + slew ring */}
      <rect x={x - 34 * s} y={ty0 - 9 * s} width={68 * s} height={9 * s} fill="url(#intaglio-h)" stroke={ESPRESSO_INK} strokeWidth={0.7 * s} />
      <ellipse cx={x} cy={ty0 - 9 * s} rx={20 * s} ry={2.4 * s} fill={IVORY_PAPER} stroke={ESPRESSO_INK} strokeWidth={0.6 * s} />
      {/* counterweight stack (rear) */}
      <rect x={x - 64 * s} y={deckTop - 4 * s} width={26 * s} height={28 * s} fill="url(#intaglio-v)" stroke={ESPRESSO_INK} strokeWidth={0.8 * s} />
      <line x1={x - 64 * s} y1={deckTop + 5 * s} x2={x - 38 * s} y2={deckTop + 5 * s} stroke={ESPRESSO_INK} strokeWidth={0.5 * s} />
      <line x1={x - 64 * s} y1={deckTop + 14 * s} x2={x - 38 * s} y2={deckTop + 14 * s} stroke={ESPRESSO_INK} strokeWidth={0.5 * s} />
      <line x1={x - 58 * s} y1={deckTop - 4 * s} x2={x - 58 * s} y2={deckTop + 24 * s} stroke={ESPRESSO_INK} strokeWidth={0.4 * s} strokeOpacity="0.6" />
      {/* superstructure deck */}
      <rect x={x - 40 * s} y={deckTop} width={84 * s} height={17 * s} rx={2 * s} fill="url(#intaglio-h)" stroke={ESPRESSO_INK} strokeWidth={0.9 * s} />
      {/* engine vents + exhaust */}
      {[0, 1, 2].map((i) => (
        <line key={i} x1={x - 30 * s + i * 6 * s} y1={deckTop + 3.5 * s} x2={x - 30 * s + i * 6 * s} y2={deckTop + 13 * s} stroke={ESPRESSO_INK} strokeWidth={0.5 * s} strokeOpacity="0.7" />
      ))}
      <rect x={x - 8 * s} y={deckTop - 7 * s} width={3.2 * s} height={7 * s} fill={SLATE_DARK} stroke={ESPRESSO_INK} strokeWidth={0.4 * s} />
      {/* handrail */}
      <line x1={x - 38 * s} y1={deckTop - 3.5 * s} x2={x + 10 * s} y2={deckTop - 3.5 * s} stroke={ESPRESSO_INK} strokeWidth={0.45 * s} />
      {[-34, -22, -10, 2].map((o) => (
        <line key={o} x1={x + o * s} y1={deckTop - 3.5 * s} x2={x + o * s} y2={deckTop} stroke={ESPRESSO_INK} strokeWidth={0.4 * s} />
      ))}
      {/* operator cab (front, by the boom foot) */}
      <path
        d={`M ${x + 26 * s} ${deckTop + 17 * s} L ${x + 26 * s} ${deckTop - 2 * s} Q ${x + 26 * s} ${deckTop - 7 * s} ${x + 31 * s} ${deckTop - 7 * s} L ${x + 44 * s} ${deckTop - 7 * s} L ${x + 44 * s} ${deckTop + 17 * s} Z`}
        fill={IVORY_PAPER} stroke={ESPRESSO_INK} strokeWidth={0.9 * s}
      />
      <path
        d={`M ${x + 28 * s} ${deckTop - 1 * s} Q ${x + 28 * s} ${deckTop - 5 * s} ${x + 32 * s} ${deckTop - 5 * s} L ${x + 42 * s} ${deckTop - 5 * s} L ${x + 42 * s} ${deckTop + 6 * s} L ${x + 28 * s} ${deckTop + 6 * s} Z`}
        fill={RACING_GREEN} fillOpacity="0.85" stroke={ESPRESSO_INK} strokeWidth={0.5 * s}
      />
      <line x1={x + 30 * s} y1={deckTop - 3.5 * s} x2={x + 40 * s} y2={deckTop - 3.5 * s} stroke={CANDLELIGHT} strokeWidth={0.7 * s} strokeOpacity="0.8" />
      {/* cab-roof beacon — amber working light while the crane is under load */}
      <line x1={x + 40 * s} y1={deckTop - 7 * s} x2={x + 40 * s} y2={deckTop - 8.6 * s} stroke={ESPRESSO_INK} strokeWidth={0.5 * s} />
      <circle cx={x + 40 * s} cy={deckTop - 9.6 * s} r={1.1 * s} fill={YELLOW_WARNING} stroke={ESPRESSO_INK} strokeWidth={0.4 * s} className="login-pulse-fast" />
      {/* A-frame backmast + sheave head */}
      <line x1={x + 6 * s} y1={deckTop + 2 * s} x2={bmx} y2={bmy} stroke={ESPRESSO_INK} strokeWidth={1.3 * s} />
      <line x1={x - 22 * s} y1={deckTop + 2 * s} x2={bmx} y2={bmy} stroke={ESPRESSO_INK} strokeWidth={1.3 * s} />
      <circle cx={bmx} cy={bmy} r={2.6 * s} fill={IVORY_PAPER} stroke={ESPRESSO_INK} strokeWidth={0.7 * s} />
      {/* pendant bar: backmast → counterweight (does not luff) */}
      <line x1={bmx} y1={bmy} x2={x - 56 * s} y2={deckTop - 2 * s} stroke={ESPRESSO_INK} strokeWidth={0.7 * s} />
      {/* Pendant bars: backmast head → boom tip. Pinned at the head and reeled
          in/out (rotate+scale keyframes synced to the boom luff) so the far end
          stays on the boom tip through the whole working arc. */}
      <g className="login-pendant-luff" style={{ transformOrigin: `${bmx}px ${bmy}px` }}>
        <line x1={bmx} y1={bmy} x2={tipX} y2={tipY + 4 * s} stroke={ESPRESSO_INK} strokeWidth={0.7 * s} />
        <line x1={bmx + 1.5 * s} y1={bmy + 2 * s} x2={tipX + 1.5 * s} y2={tipY + 6 * s} stroke={ESPRESSO_INK} strokeWidth={0.45 * s} strokeOpacity="0.6" />
      </g>
      {/* Luffing boom — works the lift about the boom-foot pin: luffed up to
          pick at the laydown, boomed down/out to land the section on the
          stack. The machine itself never moves. */}
      <g className="login-boom-luff" style={{ transformOrigin: `${px}px ${py}px` }}>
        {/* main lattice boom */}
        <path d={chordA} fill="none" stroke={ESPRESSO_INK} strokeWidth={1.5 * s} />
        <path d={chordB} fill="none" stroke={ESPRESSO_INK} strokeWidth={1.5 * s} />
        <path d={lacing} fill="none" stroke={ESPRESSO_INK} strokeWidth={0.5 * s} strokeOpacity="0.65" />
        {/* boom head: sheave block + champagne pennant + blinking aviation light */}
        <circle cx={tipX} cy={tipY} r={4 * s} fill={IVORY_PAPER} stroke={ESPRESSO_INK} strokeWidth={0.9 * s} />
        {/* sheave spokes — turn with the hoist winch as the rope reels in/out */}
        <g className="login-sheave-spin">
          <line x1={tipX - 3.1 * s} y1={tipY} x2={tipX + 3.1 * s} y2={tipY} stroke={ESPRESSO_INK} strokeWidth={0.5 * s} strokeOpacity="0.7" />
          <line x1={tipX} y1={tipY - 3.1 * s} x2={tipX} y2={tipY + 3.1 * s} stroke={ESPRESSO_INK} strokeWidth={0.5 * s} strokeOpacity="0.7" />
        </g>
        <circle cx={tipX} cy={tipY} r={1.4 * s} fill={ESPRESSO_INK} />
        <path d={`M ${tipX} ${tipY - 6 * s} L ${tipX + 9 * s} ${tipY - 3.5 * s} L ${tipX} ${tipY - 1 * s} Z`} fill={CHAMPAGNE} stroke={ESPRESSO_INK} strokeWidth={0.4 * s} />
        {/* Boom-head weather mast — raised above the hoist-rope convergence so
            the head stays readable: cross-arm with anemometer (left) + wind
            vane (right), aviation light on top */}
        <line x1={tipX} y1={tipY - 5 * s} x2={tipX} y2={tipY - 15 * s} stroke={ESPRESSO_INK} strokeWidth={0.8 * s} />
        <line x1={tipX - 8 * s} y1={tipY - 12 * s} x2={tipX + 8 * s} y2={tipY - 12 * s} stroke={ESPRESSO_INK} strokeWidth={0.7 * s} />
        <line x1={tipX - 8 * s} y1={tipY - 12 * s} x2={tipX - 8 * s} y2={tipY - 14 * s} stroke={ESPRESSO_INK} strokeWidth={0.6 * s} />
        {/* translate wrapper kept OUTSIDE the animated class — a CSS transform
            animation replaces the element's transform attribute, so combining
            them on one element throws the part to the viewBox origin */}
        <g transform={`translate(${tipX - 8 * s} ${tipY - 15.5 * s})`}>
          <g className="login-anemometer">
            <circle r={0.6 * s} fill="none" stroke="none" />
            <circle cx={0} cy={-1.8 * s} r={1 * s} fill={ESPRESSO_INK} />
            <circle cx={1.7 * s} cy={1 * s} r={1 * s} fill={ESPRESSO_INK} />
            <circle cx={-1.7 * s} cy={1 * s} r={1 * s} fill={ESPRESSO_INK} />
            <circle r={0.5 * s} fill={ESPRESSO_INK} />
          </g>
        </g>
        <line x1={tipX + 8 * s} y1={tipY - 12 * s} x2={tipX + 8 * s} y2={tipY - 13.5 * s} stroke={ESPRESSO_INK} strokeWidth={0.6 * s} />
        <g transform={`translate(${tipX + 8 * s} ${tipY - 13.5 * s})`}>
          <g className="login-vane">
            <rect x={-2 * s} y={-0.6 * s} width={4 * s} height={0.4 * s} fill="none" stroke="none" />
            <path d={`M 0 0 L ${4 * s} ${-1.2 * s} L ${4 * s} ${1.2 * s} Z`} fill={ESPRESSO_INK} />
          </g>
        </g>
        <circle cx={tipX} cy={tipY - 17 * s} r={1.8 * s} fill={RED_AVIATION} className="login-aviation-light" />
      </g>
      {/* boom foot bracket (pivot pin — stays put while the boom luffs) */}
      <circle cx={px} cy={py} r={3 * s} fill={IVORY_PAPER} stroke={ESPRESSO_INK} strokeWidth={0.8 * s} />
      {/* exhaust puffs rising from the stack */}
      <g fill={SLATE_LIGHT}>
        <circle cx={x - 6.4 * s} cy={deckTop - 10 * s} r={1.8 * s} className="login-exhaust-1" />
        <circle cx={x - 5.6 * s} cy={deckTop - 10 * s} r={1.3 * s} className="login-exhaust-2" />
        <circle cx={x - 6.8 * s} cy={deckTop - 10 * s} r={1 * s} className="login-exhaust-3" />
      </g>
    </g>
  );
}

// ============================================================================
// VALLEY INDUSTRY + GRID FEEDER
//   Small distant factory in the valley dip. A live transmission feeder
//   descends from the left ridge wind turbine and carries champagne "current"
//   into the plant — the wind makes the power, the industry runs on it.
// ============================================================================

/** Small engraved lattice transmission pylon. */
function LatticePylon({ x, baseY, h }: { x: number; baseY: number; h: number }) {
  const top = baseY - h;
  const rungs = [0.18, 0.42, 0.66, 0.86];
  const legAt = (t: number) => 4 - 2.6 * t; // half-width of the lattice at fraction t up
  return (
    <g
      stroke={ESPRESSO_INK}
      strokeWidth="0.5"
      fill="none"
      vectorEffect="non-scaling-stroke"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      {/* tapered legs */}
      <line x1={x - 4} y1={baseY} x2={x - 1.4} y2={top} />
      <line x1={x + 4} y1={baseY} x2={x + 1.4} y2={top} />
      {/* X-bracing between rungs */}
      {rungs.map((t, i) => {
        const yT = baseY - h * t;
        const tn = rungs[i + 1] ?? 1;
        const yN = baseY - h * tn;
        const wT = legAt(t);
        const wN = legAt(tn);
        return (
          <g key={`r-${i}`} strokeWidth="0.32">
            <line x1={x - wT} y1={yT} x2={x + wT} y2={yT} />
            <line x1={x - wT} y1={yT} x2={x + wN} y2={yN} />
            <line x1={x + wT} y1={yT} x2={x - wN} y2={yN} />
          </g>
        );
      })}
      {/* cross-arms + insulators */}
      <line x1={x - 7} y1={top + 3.5} x2={x + 7} y2={top + 3.5} />
      <line x1={x - 5.4} y1={top + 7.5} x2={x + 5.4} y2={top + 7.5} />
      <circle cx={x - 7} cy={top + 4.5} r={0.7} fill={ESPRESSO_INK} />
      <circle cx={x} cy={top + 1} r={0.7} fill={ESPRESSO_INK} />
      <circle cx={x + 7} cy={top + 4.5} r={0.7} fill={ESPRESSO_INK} />
    </g>
  );
}

// ============================================================================
// DOTTED METROPOLIS — far city skyline behind the horizon grid, built purely
// from window-dot matrices (halftone style) in the engraved palette. Dots
// thin out toward each building's base so the skyline dissolves into the
// horizon strip. Deterministic pseudo-random keeps renders stable.
// ============================================================================
const skylineRnd = (i: number, j: number, seed: number) => {
  const v = Math.sin(i * 127.1 + j * 311.7 + seed * 74.7) * 43758.5453;
  return v - Math.floor(v);
};

interface DottedBuilding {
  x: number;
  w: number;
  h: number;
  tone: number;
}

// Top sky band — farthest layer, behind the horizon grid.
const FAR_BAND_BUILDINGS: DottedBuilding[] = [
  { x: 64, w: 34, h: 30, tone: 0.28 },
    { x: 104, w: 26, h: 42, tone: 0.34 },
    { x: 136, w: 38, h: 34, tone: 0.3 },
    { x: 184, w: 30, h: 52, tone: 0.4 },
    { x: 220, w: 40, h: 40, tone: 0.34 },
    { x: 268, w: 30, h: 58, tone: 0.42 },
    { x: 304, w: 44, h: 48, tone: 0.38 },
    { x: 356, w: 36, h: 68, tone: 0.5 },
    { x: 398, w: 48, h: 82, tone: 0.56 },
    { x: 452, w: 40, h: 72, tone: 0.52 },
    { x: 498, w: 34, h: 86, tone: 0.56 },
    { x: 538, w: 44, h: 64, tone: 0.5 },
    { x: 590, w: 32, h: 54, tone: 0.42 },
    { x: 628, w: 42, h: 44, tone: 0.38 },
    { x: 676, w: 30, h: 56, tone: 0.4 },
    { x: 712, w: 38, h: 38, tone: 0.34 },
    { x: 756, w: 28, h: 48, tone: 0.36 },
  { x: 790, w: 36, h: 30, tone: 0.3 },
  { x: 832, w: 26, h: 40, tone: 0.3 },
];

// Mid-sky city — spans the full canvas left → right, standing just above the
// mountain ridgeline. Blurred + faint so it reads as very far away.
const MID_CITY_BUILDINGS: DottedBuilding[] = [
  { x: 40, w: 30, h: 46, tone: 0.3 },
  { x: 76, w: 24, h: 64, tone: 0.34 },
  { x: 106, w: 34, h: 50, tone: 0.32 },
  { x: 148, w: 28, h: 78, tone: 0.38 },
  { x: 182, w: 36, h: 60, tone: 0.34 },
  { x: 250, w: 30, h: 52, tone: 0.32 },
  { x: 286, w: 38, h: 70, tone: 0.36 },
  { x: 330, w: 30, h: 92, tone: 0.42 },
  { x: 366, w: 46, h: 72, tone: 0.4 },
  { x: 418, w: 38, h: 118, tone: 0.5 },
  // (462–512 slot is the SORIGIN tower — rendered crisp by <SoriginTower />)
  { x: 518, w: 36, h: 104, tone: 0.48 },
  { x: 560, w: 30, h: 78, tone: 0.4 },
  { x: 596, w: 26, h: 54, tone: 0.34 },
  { x: 640, w: 34, h: 66, tone: 0.36 },
  { x: 684, w: 28, h: 84, tone: 0.4 },
  { x: 718, w: 38, h: 58, tone: 0.34 },
  { x: 772, w: 30, h: 72, tone: 0.36 },
  { x: 808, w: 26, h: 48, tone: 0.3 },
  { x: 840, w: 30, h: 38, tone: 0.28 },
];

function DottedSkyline({
  baseY = 106,
  buildings = FAR_BAND_BUILDINGS,
  seed = 0,
  opacity = 0.5,
  blur = true,
}: {
  baseY?: number;
  buildings?: DottedBuilding[];
  seed?: number;
  opacity?: number;
  blur?: boolean;
}) {
  const pitchX = 5;
  const pitchY = 5;
  const dot = 2;
  return (
    <g opacity={opacity} filter={blur ? 'url(#login-far-blur)' : undefined}>
      {buildings.map((b, bi) => {
        const cols = Math.floor((b.w - 2) / pitchX);
        const rows = Math.floor((b.h - 2) / pitchY);
        const dots: ReactNode[] = [];
        for (let j = 0; j < rows; j++) {
          // bottom 35% of each building dissolves: dropout grows toward base
          const frac = j / rows;
          const dropout = frac > 0.65 ? 0.16 + ((frac - 0.65) / 0.35) * 0.62 : 0.16;
          for (let i = 0; i < cols; i++) {
            const r = skylineRnd(i, j, bi * 7 + 3 + seed);
            if (r < dropout) continue;
            const dx = b.x + 2 + i * pitchX;
            const dy = baseY - b.h + 2 + j * pitchY;
            const isLit = skylineRnd(i, j, bi * 7 + 99 + seed) > 0.985;
            if (isLit) {
              dots.push(
                <rect
                  key={`d-${bi}-${i}-${j}`}
                  x={dx}
                  y={dy}
                  width={dot}
                  height={dot}
                  fill={CHAMPAGNE}
                  className={`login-twinkle-${((i + j) % 4) + 1}`}
                />
              );
            } else {
              dots.push(
                <rect key={`d-${bi}-${i}-${j}`} x={dx} y={dy} width={dot} height={dot} fill={ESPRESSO_INK} opacity={b.tone} />
              );
            }
          }
        }
        return <g key={`bld-${bi}`}>{dots}</g>;
      })}
    </g>
  );
}

// ============================================================================
// SORIGIN TOWER — the headquarters in the far city: same dot-matrix style but
// crisp (no blur), denser lit windows, rooftop SORIGIN sign + aviation light.
// ============================================================================
function SoriginTower() {
  const baseY = 348;
  const bx = 462;
  const w = 50;
  const h = 140;
  const pitchX = 5;
  const pitchY = 5;
  const dot = 2;
  const cols = Math.floor((w - 2) / pitchX);
  const rows = Math.floor((h - 2) / pitchY);
  const dots: ReactNode[] = [];
  for (let j = 0; j < rows; j++) {
    const frac = j / rows;
    const dropout = frac > 0.7 ? 0.14 + ((frac - 0.7) / 0.3) * 0.5 : 0.14;
    for (let i = 0; i < cols; i++) {
      const r = skylineRnd(i, j, 211);
      if (r < dropout) continue;
      const dx = bx + 2 + i * pitchX;
      const dy = baseY - h + 2 + j * pitchY;
      const isLit = skylineRnd(i, j, 311) > 0.94;
      dots.push(
        isLit ? (
          <rect key={`st-${i}-${j}`} x={dx} y={dy} width={dot} height={dot} fill={CHAMPAGNE} className={`login-twinkle-${((i + j) % 4) + 1}`} />
        ) : (
          <rect key={`st-${i}-${j}`} x={dx} y={dy} width={dot} height={dot} fill={ESPRESSO_INK} opacity={0.5} />
        )
      );
    }
  }
  const cx = bx + w / 2;
  const roofY = baseY - h;
  return (
    <g opacity="0.72">
      {dots}
      {/* rooftop sign */}
      <line x1={bx + 8} y1={roofY + 2} x2={bx + 8} y2={roofY - 4} stroke={ESPRESSO_INK} strokeWidth="0.6" vectorEffect="non-scaling-stroke" />
      <line x1={bx + w - 8} y1={roofY + 2} x2={bx + w - 8} y2={roofY - 4} stroke={ESPRESSO_INK} strokeWidth="0.6" vectorEffect="non-scaling-stroke" />
      <rect x={bx + 2} y={roofY - 15} width={w - 4} height={11} rx={1} fill={IVORY_PAPER} stroke={ESPRESSO_INK} strokeWidth="0.6" vectorEffect="non-scaling-stroke" />
      <text
        x={cx}
        y={roofY - 6.8}
        textAnchor="middle"
        fontFamily="system-ui, sans-serif"
        fontSize="6.4"
        fontWeight="800"
        letterSpacing="1.1"
        fill={BRAND_GREEN}
      >
        SORIGIN
      </text>
      {/* antenna + aviation light */}
      <line x1={cx} y1={roofY - 15} x2={cx} y2={roofY - 24} stroke={ESPRESSO_INK} strokeWidth="0.7" vectorEffect="non-scaling-stroke" />
      <circle cx={cx} cy={roofY - 26} r={1.5} fill={RED_AVIATION} className="login-aviation-light" />
    </g>
  );
}

// ============================================================================
// LIFT SAFETY ZONE — windsock at the lift pad (the pre-hoist wind check) and
// an engraved chain-post exclusion ring where the hi-vis cones used to stand.
// The sock gusts around its swivel, its tail flutters faster, and the chain
// catenaries breathe in the same wind — all Sovereign Emerald, no orange.
// ============================================================================
function LiftSafetyZone() {
  const posts: Array<[number, number]> = [
    [505, 506],
    [552, 514],
    [628, 504],
  ];
  const POST_H = 14;
  return (
    <g>
      {/* windsock mast — wind check before every hoist */}
      <g transform="translate(472 543)">
        <ellipse cx={2} cy={1.8} rx={9} ry={1.4} fill={GROUND_SHADOW} opacity="0.5" />
        <rect x={-3} y={-1.2} width={6} height={2} rx={0.6} fill="url(#intaglio-h)" stroke={ESPRESSO_INK} strokeWidth="0.5" vectorEffect="non-scaling-stroke" />
        <line x1={0} y1={-1} x2={0} y2={-38} stroke={ESPRESSO_INK} strokeWidth="1.1" strokeLinecap="round" vectorEffect="non-scaling-stroke" />
        <line x1={0} y1={-26} x2={-6} y2={-1} stroke={ESPRESSO_INK} strokeWidth="0.5" opacity="0.55" vectorEffect="non-scaling-stroke" />
        <circle cx={0} cy={-38} r={1.4} fill="none" stroke={ESPRESSO_INK} strokeWidth="0.7" vectorEffect="non-scaling-stroke" />
        {/* sock — striped brand green / ivory, gust-lifted around the swivel */}
        <g className="login-windsock">
          <g transform="translate(1.2 -38)">
            <polygon points="0,-3.6 6.5,-3.2 6.5,3.2 0,3.6" fill={BRAND_GREEN} stroke={ESPRESSO_INK} strokeWidth="0.45" vectorEffect="non-scaling-stroke" />
            <polygon points="6.5,-3.2 12.5,-2.7 12.5,2.7 6.5,3.2" fill={IVORY_PAPER} stroke={ESPRESSO_INK} strokeWidth="0.45" vectorEffect="non-scaling-stroke" />
            <polygon points="12.5,-2.7 17.5,-2.3 17.5,2.3 12.5,2.7" fill={BRAND_GREEN} stroke={ESPRESSO_INK} strokeWidth="0.45" vectorEffect="non-scaling-stroke" />
            <g className="login-windsock-tail">
              <polygon points="17.5,-2.3 23,-1.8 23,1.8 17.5,2.3" fill={IVORY_PAPER} stroke={ESPRESSO_INK} strokeWidth="0.45" vectorEffect="non-scaling-stroke" />
              <ellipse cx={23} cy={0} rx={0.7} ry={1.8} fill="none" stroke={ESPRESSO_INK} strokeWidth="0.4" vectorEffect="non-scaling-stroke" />
            </g>
            {/* entry throat ring on the swivel */}
            <ellipse cx={0} cy={0} rx={0.9} ry={3.7} fill="none" stroke={ESPRESSO_INK} strokeWidth="0.6" vectorEffect="non-scaling-stroke" />
          </g>
        </g>
      </g>
      {/* exclusion ring — champagne-finial chain posts ringing the lift pad */}
      {posts.map(([px, py]) => (
        <g key={`post-${px}`}>
          <ellipse cx={px} cy={py + 1.4} rx={4.5} ry={0.9} fill={GROUND_SHADOW} opacity="0.5" />
          <rect x={px - 2.6} y={py - 1.2} width={5.2} height={1.8} rx={0.5} fill="url(#intaglio-h)" stroke={ESPRESSO_INK} strokeWidth="0.45" vectorEffect="non-scaling-stroke" />
          <line x1={px} y1={py} x2={px} y2={py - POST_H} stroke={ESPRESSO_INK} strokeWidth="1" strokeLinecap="round" vectorEffect="non-scaling-stroke" />
          <circle cx={px} cy={py - POST_H - 1.2} r={1.3} fill={CHAMPAGNE} stroke={ESPRESSO_INK} strokeWidth="0.45" vectorEffect="non-scaling-stroke" />
        </g>
      ))}
      {/* chain catenaries — sag breathes in the wind (endpoints stay pinned) */}
      <path className="login-chain-sway" d="M 505 492.8 Q 528.5 508 552 500.8" fill="none" stroke={ESPRESSO_INK} strokeWidth="0.8" strokeDasharray="1.7 1.1" strokeLinecap="round" vectorEffect="non-scaling-stroke" />
      <path className="login-chain-sway-2" d="M 552 500.8 Q 590 507 628 490.8" fill="none" stroke={ESPRESSO_INK} strokeWidth="0.8" strokeDasharray="1.7 1.1" strokeLinecap="round" vectorEffect="non-scaling-stroke" />
    </g>
  );
}

// ============================================================================
// CITY FEEDER PYLONS — transmission runs climbing from the ridge wind farm up
// to the far city, one from each side. Crisp hairlines over the blurred city
// reinforce the depth split.
// ============================================================================
function CityFeederPylons() {
  // Left run: ridge turbine (225, 377) → two pylons → city intake (332, 343).
  const left = 'M 227 372 Q 245 358 262 349 Q 281 349 300 342 Q 317 347 332 343';
  const leftLow = 'M 227 374.5 Q 245 360.5 262 351.5 Q 281 351.5 300 344.5 Q 317 349.5 332 345.5';
  // Right run: ridge turbine (682, 362) → two pylons → city intake (600, 344).
  const right = 'M 680 357 Q 666 346 652 341 Q 635 344 618 338 Q 608 342 600 344';
  const rightLow = 'M 680 359.5 Q 666 348.5 652 343.5 Q 635 346.5 618 340.5 Q 608 344.5 600 346.5';
  return (
    <g opacity="0.55">
      <LatticePylon x={262} baseY={366} h={17} />
      <LatticePylon x={300} baseY={357} h={15} />
      <LatticePylon x={652} baseY={356} h={15} />
      <LatticePylon x={618} baseY={352} h={14} />
      {[
        { hi: left, lo: leftLow },
        { hi: right, lo: rightLow },
      ].map((run, i) => (
        <g key={`feeder-${i}`}>
          <path d={run.hi} fill="none" stroke={ESPRESSO_INK} strokeWidth="0.55" strokeOpacity="0.7" vectorEffect="non-scaling-stroke" strokeLinecap="round" />
          <path d={run.lo} fill="none" stroke={ESPRESSO_INK} strokeWidth="0.45" strokeOpacity="0.5" vectorEffect="non-scaling-stroke" strokeLinecap="round" />
          <path d={run.hi} className={i === 0 ? 'login-powerflow' : 'login-spark-2'} fill="none" stroke={CHAMPAGNE} strokeWidth="1" strokeLinecap="round" vectorEffect="non-scaling-stroke" />
        </g>
      ))}
    </g>
  );
}

// ============================================================================
// FAR HORIZON GRID — a faint horizon strip floating in the empty sky band at
// the top of the scene: transmission pylons + a tiny industry cluster, far
// beyond the mountains. Drifting clouds pass in front of it for depth.
// ============================================================================
function FarHorizonGrid() {
  const baseY = 106; // horizon strip in the top empty sky
  const pylons = [
    { x: 200, baseY, h: 24 },
    { x: 290, baseY, h: 26 },
    { x: 380, baseY, h: 24 },
    { x: 470, baseY, h: 25 },
    { x: 560, baseY, h: 24 },
    { x: 650, baseY, h: 26 },
    { x: 740, baseY, h: 24 },
    { x: 830, baseY, h: 25 },
  ];
  // Catenary chain pylon-top → pylon-top, then drop into the plant intake.
  const conductor =
    'M 830 81 Q 785 88 740 82 Q 695 89 650 80 Q 605 88 560 82 Q 515 89 470 81 Q 425 88 380 82 Q 335 89 290 80 Q 245 88 200 82 Q 172 92 148 100';
  const conductorLow =
    'M 830 84 Q 785 91 740 85 Q 695 92 650 83 Q 605 91 560 85 Q 515 92 470 84 Q 425 91 380 85 Q 335 92 290 83 Q 245 91 200 85 Q 172 95 148 103';
  return (
    <g opacity="0.55">
      {/* hairline ground strip */}
      <line x1={36} y1={baseY} x2={872} y2={baseY} stroke={ESPRESSO_INK} strokeWidth="0.5" strokeOpacity="0.4" vectorEffect="non-scaling-stroke" strokeLinecap="round" />
      <line x1={60} y1={baseY + 3.5} x2={848} y2={baseY + 3.5} stroke={ESPRESSO_INK} strokeWidth="0.4" strokeOpacity="0.2" strokeDasharray="2 4" vectorEffect="non-scaling-stroke" strokeLinecap="round" />

      {pylons.map((p) => (
        <LatticePylon key={`rp-${p.x}`} x={p.x} baseY={p.baseY} h={p.h} />
      ))}
      <path d={conductor} fill="none" stroke={ESPRESSO_INK} strokeWidth="0.55" strokeOpacity="0.7" vectorEffect="non-scaling-stroke" strokeLinecap="round" />
      <path d={conductorLow} fill="none" stroke={ESPRESSO_INK} strokeWidth="0.45" strokeOpacity="0.5" vectorEffect="non-scaling-stroke" strokeLinecap="round" />
      {/* live current travelling along the horizon run */}
      <path d={conductor} className="login-powerflow" fill="none" stroke={CHAMPAGNE} strokeWidth="1" strokeLinecap="round" vectorEffect="non-scaling-stroke" />

      {/* tiny industry cluster at the end of the run */}
      <g transform="translate(116 106) scale(0.42)" opacity="0.9">
        <ellipse cx={4} cy={2} rx={40} ry={2.4} fill={GROUND_SHADOW} opacity="0.45" />
        {/* shed + sawtooth roof */}
        <rect x={-18} y={-14} width={36} height={14} fill="url(#intaglio-diag)" stroke={ESPRESSO_INK} strokeWidth="0.45" vectorEffect="non-scaling-stroke" />
        <path
          d="M -18 -14 L -18 -21 L -6 -14 L -6 -21 L 6 -14 L 6 -21 L 18 -14"
          fill={IVORY_PAPER}
          stroke={ESPRESSO_INK}
          strokeWidth="0.45"
          vectorEffect="non-scaling-stroke"
          strokeLinejoin="round"
        />
        <g fill={CHAMPAGNE}>
          <rect x={-13} y={-9} width={2} height={2.6} className="login-twinkle-2" />
          <rect x={-1} y={-9} width={2} height={2.6} className="login-twinkle-4" />
          <rect x={11} y={-9} width={2} height={2.6} className="login-twinkle-1" />
        </g>
        {/* chimneys + faint smoke */}
        <rect x={-4} y={-34} width={3.4} height={34} fill="url(#intaglio-diag)" stroke={ESPRESSO_INK} strokeWidth="0.45" vectorEffect="non-scaling-stroke" />
        <rect x={-4} y={-34} width={3.4} height={2} fill={CHAMPAGNE} />
        <rect x={3} y={-27} width={3} height={27} fill="url(#intaglio-diag)" stroke={ESPRESSO_INK} strokeWidth="0.45" vectorEffect="non-scaling-stroke" />
        <rect x={3} y={-27} width={3} height={1.8} fill={CHAMPAGNE} />
        <g fill={SLATE_LIGHT} opacity="0.25">
          <ellipse cx={-1} cy={-38} rx={2.6} ry={1.7} />
          <ellipse cx={1.5} cy={-43} rx={3.2} ry={2.1} />
        </g>
        {/* storage tank */}
        <rect x={22} y={-16} width={9} height={16} fill="url(#intaglio-diag)" stroke={ESPRESSO_INK} strokeWidth="0.45" vectorEffect="non-scaling-stroke" />
        <ellipse cx={26.5} cy={-16} rx={4.5} ry={1.5} fill={IVORY_PAPER} stroke={ESPRESSO_INK} strokeWidth="0.4" vectorEffect="non-scaling-stroke" />
      </g>
    </g>
  );
}

// ============================================================================
// BAY BUILDING — industrial multi-bay warehouse (gabled bays, roller doors,
// side office). Fills the foreground corners left + right of the composite.
// ============================================================================
function BayBuilding({
  x,
  y,
  scale = 1,
  bays = 3,
}: {
  x: number;
  y: number;
  scale?: number;
  bays?: number;
}) {
  const s = scale;
  const bayW = 26 * s;
  const wallH = 26 * s;
  const roofH = 10 * s;
  const w = bays * bayW;
  const officeW = 16 * s;
  const officeH = 15 * s;
  return (
    <g>
      <ellipse cx={x + (w + officeW) / 2} cy={y + 2 * s} rx={(w + officeW) * 0.62} ry={3.2 * s} fill={GROUND_SHADOW} opacity="0.5" />

      {/* main hall */}
      <rect x={x} y={y - wallH} width={w} height={wallH} fill="url(#intaglio-diag)" stroke={ESPRESSO_INK} strokeWidth="0.5" vectorEffect="non-scaling-stroke" />
      {/* gabled bay roofs */}
      {[...Array(bays)].map((_, i) => {
        const bx = x + i * bayW;
        return (
          <g key={`bay-${i}`}>
            <path
              d={`M ${bx} ${y - wallH} L ${bx + bayW / 2} ${y - wallH - roofH} L ${bx + bayW} ${y - wallH} Z`}
              fill={IVORY_PAPER}
              stroke={ESPRESSO_INK}
              strokeWidth="0.5"
              vectorEffect="non-scaling-stroke"
              strokeLinejoin="round"
            />
            {/* ridge vent */}
            <rect x={bx + bayW / 2 - 2.4 * s} y={y - wallH - roofH - 2.2 * s} width={4.8 * s} height={2.2 * s} fill={IVORY_PAPER} stroke={ESPRESSO_INK} strokeWidth="0.45" vectorEffect="non-scaling-stroke" />
            {/* roller shutter door */}
            <rect x={bx + bayW / 2 - 7 * s} y={y - 16 * s} width={14 * s} height={16 * s} fill="url(#intaglio-h)" stroke={ESPRESSO_INK} strokeWidth="0.5" vectorEffect="non-scaling-stroke" />
            <rect x={bx + bayW / 2 - 7 * s} y={y - 16 * s} width={14 * s} height={1.8 * s} fill={CHAMPAGNE} opacity="0.85" />
            {/* gable window */}
            <rect x={bx + bayW / 2 - 1.6 * s} y={y - wallH - 4.4 * s} width={3.2 * s} height={3 * s} fill={CHAMPAGNE} className={`login-twinkle-${(i % 4) + 1}`} />
          </g>
        );
      })}
      {/* brand fascia band along the eaves */}
      <rect x={x} y={y - wallH} width={w} height={2.2 * s} fill={BRAND_GREEN} opacity="0.85" />

      {/* side office annex */}
      <rect x={x + w} y={y - officeH} width={officeW} height={officeH} fill={IVORY_PAPER} stroke={ESPRESSO_INK} strokeWidth="0.5" vectorEffect="non-scaling-stroke" />
      <rect x={x + w} y={y - officeH} width={officeW} height={1.8 * s} fill={BRAND_GREEN} opacity="0.85" />
      <rect x={x + w + 3 * s} y={y - 10 * s} width={4 * s} height={4 * s} fill={CHAMPAGNE} className="login-twinkle-3" />
      <rect x={x + w + 9.5 * s} y={y - 7 * s} width={3.6 * s} height={7 * s} fill="url(#intaglio-v)" stroke={ESPRESSO_INK} strokeWidth="0.45" vectorEffect="non-scaling-stroke" />
    </g>
  );
}

// ============================================================================
// ROOT — single continuous panning world panorama
// ============================================================================

// Main ridge silhouette — the four ridge-farm turbines and the met mast are
// seated on these exact peak elevations; change a peak and they float.
const MAIN_RIDGE_D = `M 0 ${HORIZON}
  L 80 ${HORIZON - 60} L 140 ${HORIZON - 40} L 220 ${HORIZON - 95} L 320 ${HORIZON - 60}
  L 420 ${HORIZON - 100} L 540 ${HORIZON - 70} L 680 ${HORIZON - 110} L 800 ${HORIZON - 50}
  L ${VIEW_W} ${HORIZON - 75} L ${VIEW_W} ${HORIZON} Z`;

// Far range — peaks rise in the main ridge's saddles so every gap in the near
// silhouette opens onto a deeper plane.
const FAR_RIDGE_D = `M 0 ${HORIZON - 58}
  Q 75 ${HORIZON - 98} 150 ${HORIZON - 90} Q 200 ${HORIZON - 84} 235 ${HORIZON - 70}
  Q 285 ${HORIZON - 104} 330 ${HORIZON - 100} Q 380 ${HORIZON - 94} 430 ${HORIZON - 78}
  Q 475 ${HORIZON - 102} 520 ${HORIZON - 96} Q 575 ${HORIZON - 88} 620 ${HORIZON - 80}
  Q 690 ${HORIZON - 104} 760 ${HORIZON - 96} Q 805 ${HORIZON - 88} 835 ${HORIZON - 76}
  Q 870 ${HORIZON - 94} ${VIEW_W} ${HORIZON - 88}
  L ${VIEW_W} ${HORIZON} L 0 ${HORIZON} Z`;

export default function EpcLoginScene({ className }: EpcLoginSceneProps) {
  // Login-state reactions (requirement: scene responds to auth attempts).
  // Pure class toggle — all the reacting is CSS animation overrides.
  const authState = useAuthSceneStore((s) => s.state);
  const stateClass = authState === 'idle' ? '' : ` login-scene--${authState}`;
  return (
    <div className={`login-scene-root${stateClass}${className ? ` ${className}` : ''}`}>
      <svg
        viewBox={`0 0 ${VIEW_W} ${VIEW_H}`}
        preserveAspectRatio="xMidYMid slice"
        role="img"
        aria-hidden="true"
      >
        <defs>
          <linearGradient id="login-brand-grad" x1="0" y1="0" x2="1" y2="0">
            <stop offset="0%" stopColor={RACING_GREEN} />
            <stop offset="50%" stopColor={BRAND_GREEN} />
            <stop offset="100%" stopColor={CHAMPAGNE} />
          </linearGradient>
          <linearGradient id="login-gleam-grad" x1="0" y1="0" x2="1" y2="0">
            <stop offset="0" stopColor={CANDLELIGHT} stopOpacity="0" />
            <stop offset="0.5" stopColor={CANDLELIGHT} stopOpacity="0.55" />
            <stop offset="1" stopColor={CANDLELIGHT} stopOpacity="0" />
          </linearGradient>
          <pattern id="intaglio-h" x="0" y="0" width="4" height="4" patternUnits="userSpaceOnUse">
            <line x1="0" y1="0" x2="4" y2="0" stroke={ESPRESSO_INK} strokeWidth="0.4" strokeOpacity="0.65" />
          </pattern>
          <pattern id="intaglio-v" x="0" y="0" width="4" height="4" patternUnits="userSpaceOnUse">
            <line x1="0" y1="0" x2="0" y2="4" stroke={ESPRESSO_INK} strokeWidth="0.4" strokeOpacity="0.65" />
          </pattern>
          <pattern id="intaglio-diag" x="0" y="0" width="4" height="4" patternUnits="userSpaceOnUse" patternTransform="rotate(35)">
            <line x1="0" y1="0" x2="0" y2="4" stroke={ESPRESSO_INK} strokeWidth="0.4" strokeOpacity="0.65" />
          </pattern>
          <pattern id="intaglio-cross" x="0" y="0" width="4" height="4" patternUnits="userSpaceOnUse">
            <line x1="0" y1="0" x2="4" y2="0" stroke={ESPRESSO_INK} strokeWidth="0.3" strokeOpacity="0.55" />
            <line x1="0" y1="0" x2="0" y2="4" stroke={ESPRESSO_INK} strokeWidth="0.3" strokeOpacity="0.55" />
          </pattern>
          <pattern id="intaglio-green-diag" x="0" y="0" width="3.6" height="3.6" patternUnits="userSpaceOnUse" patternTransform="rotate(45)">
            <line x1="0" y1="0" x2="0" y2="3.6" stroke={BRAND_GREEN} strokeWidth="0.5" strokeOpacity="0.85" />
          </pattern>
          {/* solar-cell tone — dense racing-green weave that reads as a dark
              module from distance but resolves to pure linework up close */}
          <pattern id="intaglio-cell" x="0" y="0" width="2.2" height="2.2" patternUnits="userSpaceOnUse">
            <rect width="2.2" height="2.2" fill={BOTTLE_GREEN} fillOpacity="0.5" />
            <line x1="0" y1="0" x2="2.2" y2="0" stroke={RACING_GREEN} strokeWidth="0.8" strokeOpacity="0.9" />
            <line x1="0" y1="0" x2="0" y2="2.2" stroke={RACING_GREEN} strokeWidth="0.8" strokeOpacity="0.9" />
          </pattern>
          {/* atmospheric softening for the far dotted cities */}
          <filter id="login-far-blur" x="-5%" y="-5%" width="110%" height="110%">
            <feGaussianBlur stdDeviation="0.8" />
          </filter>
          {/* keeps facet shading + hachures inside the main ridge body */}
          <clipPath id="login-main-ridge-clip">
            <path d={MAIN_RIDGE_D} />
          </clipPath>
        </defs>

        {/* Flat ivory paper — premium banknote ground, no gradient */}
        <rect width={VIEW_W} height={VIEW_H} fill={IVORY_PAPER} />

        {/* === BACKGROUND — engraved mountain chain, receding in three planes === */}
        {/* Far range — pale sage body under a faint hatch, dissolving into haze */}
        <path d={FAR_RIDGE_D} fill={MOUNTAIN_DISTANT} opacity="0.5" />
        <path d={FAR_RIDGE_D} fill="url(#intaglio-diag)" stroke={ESPRESSO_INK} strokeWidth="0.3" opacity="0.4" />
        {/* Valley cloud — soft candlelight haze the far range dissolves into */}
        <path
          d={`M 0 ${HORIZON - 64} Q 120 ${HORIZON - 72} 240 ${HORIZON - 64} T 480 ${HORIZON - 62}
              T 720 ${HORIZON - 66} T ${VIEW_W} ${HORIZON - 62}
              L ${VIEW_W} ${HORIZON - 30} L 0 ${HORIZON - 30} Z`}
          fill={CANDLELIGHT}
          opacity="0.55"
          filter="url(#login-far-blur)"
        />
        {/* Main ridge — opaque ivory underpaint (masks the range behind), a sage
            body tint, the engraved hatch, then lit-left/shadow-right facets */}
        <path d={MAIN_RIDGE_D} fill={IVORY_PAPER} />
        <path d={MAIN_RIDGE_D} fill={MOUNTAIN_MID} opacity="0.28" />
        <path d={MAIN_RIDGE_D} fill="url(#intaglio-diag)" stroke={ESPRESSO_INK} strokeWidth="0.55" />
        <g clipPath="url(#login-main-ridge-clip)">
          {/* shadow facets falling down-right from each summit (light sits left) */}
          <path d={`M 80 ${HORIZON - 60} L 140 ${HORIZON - 40} L 140 ${HORIZON} L 96 ${HORIZON} Z`} fill="url(#intaglio-cross)" opacity="0.5" />
          <path d={`M 220 ${HORIZON - 95} L 320 ${HORIZON - 60} L 320 ${HORIZON} L 244 ${HORIZON} Z`} fill="url(#intaglio-cross)" opacity="0.5" />
          <path d={`M 420 ${HORIZON - 100} L 540 ${HORIZON - 70} L 540 ${HORIZON} L 448 ${HORIZON} Z`} fill="url(#intaglio-cross)" opacity="0.5" />
          <path d={`M 680 ${HORIZON - 110} L 800 ${HORIZON - 50} L 800 ${HORIZON} L 706 ${HORIZON} Z`} fill="url(#intaglio-cross)" opacity="0.5" />
          {/* arête hairlines — the fold each shadow facet breaks from */}
          <path d={`M 80 ${HORIZON - 60} Q 88 ${HORIZON - 30} 96 ${HORIZON}`} fill="none" stroke={ESPRESSO_INK} strokeWidth="0.3" opacity="0.45" vectorEffect="non-scaling-stroke" />
          <path d={`M 220 ${HORIZON - 95} Q 232 ${HORIZON - 48} 244 ${HORIZON}`} fill="none" stroke={ESPRESSO_INK} strokeWidth="0.3" opacity="0.45" vectorEffect="non-scaling-stroke" />
          <path d={`M 420 ${HORIZON - 100} Q 434 ${HORIZON - 50} 448 ${HORIZON}`} fill="none" stroke={ESPRESSO_INK} strokeWidth="0.3" opacity="0.45" vectorEffect="non-scaling-stroke" />
          <path d={`M 680 ${HORIZON - 110} Q 694 ${HORIZON - 55} 706 ${HORIZON}`} fill="none" stroke={ESPRESSO_INK} strokeWidth="0.3" opacity="0.45" vectorEffect="non-scaling-stroke" />
          {/* contour hachures terracing the lit western slopes */}
          <path d={`M 172 ${HORIZON - 52} Q 196 ${HORIZON - 70} 214 ${HORIZON - 86}`} fill="none" stroke={ESPRESSO_INK} strokeWidth="0.3" opacity="0.35" vectorEffect="non-scaling-stroke" />
          <path d={`M 348 ${HORIZON - 66} Q 384 ${HORIZON - 80} 412 ${HORIZON - 94}`} fill="none" stroke={ESPRESSO_INK} strokeWidth="0.3" opacity="0.35" vectorEffect="non-scaling-stroke" />
          <path d={`M 572 ${HORIZON - 76} Q 620 ${HORIZON - 90} 668 ${HORIZON - 104}`} fill="none" stroke={ESPRESSO_INK} strokeWidth="0.3" opacity="0.35" vectorEffect="non-scaling-stroke" />
          {/* switchback service track — how crews reach the ridge met mast */}
          <path
            d={`M 508 ${HORIZON - 6} L 462 ${HORIZON - 34} L 496 ${HORIZON - 58} L 444 ${HORIZON - 80} L 424 ${HORIZON - 95}`}
            fill="none"
            stroke={ESPRESSO_INK}
            strokeWidth="0.35"
            strokeDasharray="2.2 1.6"
            opacity="0.5"
            vectorEffect="non-scaling-stroke"
          />
        </g>
        <path
          d={`M 0 ${HORIZON + 8}
              Q 200 ${HORIZON - 30} 400 ${HORIZON + 12}
              Q 600 ${HORIZON - 20} ${VIEW_W} ${HORIZON + 10}
              L ${VIEW_W} ${HORIZON + 30} L 0 ${HORIZON + 30} Z`}
          fill="url(#intaglio-green-diag)"
        />
        {/* Small wind farm on the mountain ridges — all rotors turning */}
        <g opacity="0.7">
          <g transform="translate(80 -58)">
            <Turbine baseX={0} hubY={HORIZON - 34} scale={0.26} spinClass="login-blade-spin-slow" detailLevel="simple" />
          </g>
          <g transform="translate(225 -93)">
            <Turbine baseX={0} hubY={HORIZON - 38} scale={0.3} spinClass="login-blade-spin" detailLevel="simple" />
          </g>
          <g transform="translate(545 -68)">
            <Turbine baseX={0} hubY={HORIZON - 33} scale={0.25} spinClass="login-blade-spin" detailLevel="simple" />
          </g>
          <g transform="translate(682 -108)">
            <Turbine baseX={0} hubY={HORIZON - 38} scale={0.29} spinClass="login-blade-spin-slow" detailLevel="simple" />
          </g>
        </g>

        {/* Dotted metropolis — farthest layer in the top sky */}
        <DottedSkyline opacity={0.3} />

        {/* Mid-sky dotted city — full-width, blurred, far beyond the site;
            kept faint so the crane lattice stays crisp in front of it */}
        <DottedSkyline baseY={348} buildings={MID_CITY_BUILDINGS} seed={5} opacity={0.24} />

        {/* SORIGIN headquarters — the one crisp, branded tower in the city */}
        <SoriginTower />

        {/* Grid link: wind farm → far city, one feeder run per side */}
        <CityFeederPylons />

        {/* Far horizon strip in the top sky: pylon run + distant industry */}
        <FarHorizonGrid />

        {/* Met mast on the ridge summit right of the hero turbine — where wind
            measurement actually happens: up on the ridgeline it prospects,
            beside the farm it justified, served by the switchback track */}
        <g opacity="0.75">
          <MetMast x={420} y={HORIZON - 96} scale={0.5} />
        </g>

        {/* Hero turbine — full detail, full presence on the left */}
        <Turbine baseX={130} hubY={HORIZON - 165} scale={0.85} spinClass="login-blade-spin" detailLevel="full" />
        <CompletedTurbine x={330} y={HORIZON - 8} scale={0.6} />

        {/* === MIDGROUND — crawler crane hoisting the 3rd tower section onto the stack === */}
        {/* Solar farm — aligned 2×4 row/col array, gleam sweeps staggered */}
        {[0, 1].map((row) =>
          [0, 1, 2, 3].map((col) => {
            const gleam = ['login-gleam', 'login-gleam-2', 'login-gleam-3'][(row + col) % 3];
            return (
              <SolarPanel
                key={`farm-${row}-${col}`}
                cx={52 + col * 68}
                cy={HORIZON + 20 + row * 32}
                width={56}
                height={29}
                gleamClass={gleam}
              />
            );
          })
        )}
        {/* the machine stays parked — a real crane never tracks with a load
            slung; its boom does the reaching, so the shadow sits still too */}
        <g>
          <ellipse cx={440} cy={HORIZON + 12} rx={100} ry={6.5} fill={GROUND_SHADOW} opacity="0.6" />
          <CrawlerCrane x={400} groundY={HORIZON + 4} scale={1} tipX={583} tipY={178} />
        </g>
        {/* x=583 = the crane's plumb line (boom tip / hook centre), so every
            piece seats dead-centre on the growing stack */}
        <ErectionSite />
        {/* Lift safety: windsock wind-check + chain exclusion ring on the pad */}
        <LiftSafetyZone />
        {/* Ground rigger steering the load with a tag line (no one climbs or
            rides a suspended lift) — drawn before the rig so the hook block
            overlaps the rope's far end */}
        <TaglineRigger />
        {/* === THE ERECTION LIFT RIG — gravity-honest, the BOOM does the reach ===
            The machine stays parked; the horizontal travel is the boom LUFFING
            between the laydown (tip 493,146) and the stack (tip 583,178). The
            rig rides the boom-tip arc as a pure translation and is never
            rotated with the boom, so the ropes hang from the tip and the whole
            rope+hook+load assembly pendulums TOGETHER about the tip (±1.6°,
            login-load-sway) — cables stay taut and dead-straight to the load.
            The 40s erection clock (see CSS) flies section 1/3 → 2/3 → 3/3 →
            nacelle through this one rig; each load fades in at the pick and
            hands off to its static ErectionSite twin at the landing.
            Mechanics:
            • login-load-carry translate(x,y) keyframes ARE the boom-tip arc
              positions, so the rope top stays welded to the luffing boom head.
            • login-tower-hoist scaleY(k) + login-tower-lift translateY(242·(k−1))
              are the winch — locked pair, per-phase pick + seat depths, and a
              tiny settle bounce at every landing.
            • login-grip-s1/s2/s3/nac gate which load hangs on the hook. */}
        <g className="login-load-carry">
          <g className="login-load-sway" style={{ transformOrigin: '583px 178px' }}>
            {/* Hoist ropes (vertical) — shorten/lengthen as the winch hauls in/out */}
            <g className="login-tower-hoist" style={{ transformOrigin: '583px 178px' }}>
              <line x1={580} y1={178} x2={580} y2={420} stroke={ESPRESSO_INK} strokeWidth="0.7" />
              <line x1={586} y1={178} x2={586} y2={420} stroke={ESPRESSO_INK} strokeWidth="0.7" />
            </g>
            {/* Hook block + the phase's flying load — winch only (vertical) */}
            <g className="login-tower-lift">
              {/* hook block + twin slings (always visible — the empty hook luffs back) */}
              <rect x={578} y={420} width={10} height={8} rx={1} fill={SLATE_DARK} stroke={ESPRESSO_INK} strokeWidth="0.5" />
              <circle cx={580.5} cy={424} r={1} fill={SLATE_MID} />
              <circle cx={585.5} cy={424} r={1} fill={SLATE_MID} />
              <line x1={582} y1={428} x2={577.5} y2={432.5} stroke={ESPRESSO_INK} strokeWidth="0.6" />
              <line x1={584} y1={428} x2={588.5} y2={432.5} stroke={ESPRESSO_INK} strokeWidth="0.6" />
              <circle cx={583} cy={429.5} r={1.6} fill="none" stroke={ESPRESSO_INK} strokeWidth="0.6" />

              {/* — flying section 1/3 (phase 1): widest of the taper — */}
              <g className="login-grip-load login-grip-s1" opacity={0}>
                <ellipse cx={583} cy={480} rx={11.2} ry={2.2} fill={YELLOW_WARNING} stroke={ESPRESSO_INK} strokeWidth="0.5" />
                <polygon points="573.8,432 592.2,432 594,480 572,480" fill="url(#intaglio-v)" stroke={ESPRESSO_INK} strokeWidth="0.7" />
                <path d="M 588.9 432 L 590.9 480 L 594 480 L 592.2 432 Z" fill={GROUND_SHADOW} opacity="0.25" />
                <ellipse cx={583} cy={432} rx={9.4} ry={1.9} fill={SLATE_LIGHT} stroke={ESPRESSO_INK} strokeWidth="0.5" />
                <line x1={573.2} y1={448} x2={592.8} y2={448} stroke={ESPRESSO_INK} strokeWidth="0.4" strokeOpacity="0.6" />
                {/* shop-fitted access door rides up with the section */}
                <rect x={580.6} y={470} width={4.8} height={10} rx={2} fill={IVORY_PAPER} stroke={ESPRESSO_INK} strokeWidth="0.5" />
                <circle cx={584.2} cy={475} r={0.5} fill={ESPRESSO_INK} />
                <text x={583} y={462} textAnchor="middle" fontFamily="Inter, sans-serif" fontSize={4.5} fontWeight="700" fill={ESPRESSO_INK}>1/3</text>
                {[
                  [573.6, 480.98, 1],
                  [577.6, 481.5, 2],
                  [583.0, 481.7, 3],
                  [588.4, 481.5, 4],
                  [592.4, 480.98, 5],
                ].map(([bx, by, n]) => (
                  <g key={`s1-fit-${n}`} className={`login-bolt login-boltset-1 login-bolt-${n}`}>
                    <circle cx={bx} cy={by} r={0.85} fill={SLATE_DARK} stroke={ESPRESSO_INK} strokeWidth="0.3" />
                    <line x1={bx - 0.55} y1={by} x2={bx + 0.55} y2={by} stroke={IVORY_PAPER} strokeWidth="0.3" />
                  </g>
                ))}
              </g>

              {/* — flying section 2/3 (phase 2) — */}
              <g className="login-grip-load login-grip-s2" opacity={0}>
                <ellipse cx={583} cy={478} rx={9.4} ry={2} fill={YELLOW_WARNING} stroke={ESPRESSO_INK} strokeWidth="0.5" />
                <polygon points="575.5,432 590.5,432 592.2,478 573.8,478" fill="url(#intaglio-v)" stroke={ESPRESSO_INK} strokeWidth="0.7" />
                <path d="M 587.6 432 L 589.3 478 L 592.2 478 L 590.5 432 Z" fill={GROUND_SHADOW} opacity="0.25" />
                <ellipse cx={583} cy={432} rx={7.7} ry={1.7} fill={SLATE_LIGHT} stroke={ESPRESSO_INK} strokeWidth="0.5" />
                <line x1={574.7} y1={450} x2={591.3} y2={450} stroke={ESPRESSO_INK} strokeWidth="0.4" strokeOpacity="0.6" />
                <text x={583} y={458} textAnchor="middle" fontFamily="Inter, sans-serif" fontSize={4.5} fontWeight="700" fill={ESPRESSO_INK}>2/3</text>
                {[
                  [575.6, 478.98, 1],
                  [579.0, 479.5, 2],
                  [583.0, 479.7, 3],
                  [587.0, 479.5, 4],
                  [590.4, 478.98, 5],
                ].map(([bx, by, n]) => (
                  <g key={`s2-fit-${n}`} className={`login-bolt login-boltset-2 login-bolt-${n}`}>
                    <circle cx={bx} cy={by} r={0.85} fill={SLATE_DARK} stroke={ESPRESSO_INK} strokeWidth="0.3" />
                    <line x1={bx - 0.55} y1={by} x2={bx + 0.55} y2={by} stroke={IVORY_PAPER} strokeWidth="0.3" />
                  </g>
                ))}
              </g>

              {/* — flying section 3/3 (phase 3): top of the taper — */}
              <g className="login-grip-load login-grip-s3" opacity={0}>
                <ellipse cx={583} cy={474} rx={8.6} ry={2} fill={YELLOW_WARNING} stroke={ESPRESSO_INK} strokeWidth="0.5" />
                <polygon points="577,432 589,432 590.5,474 575.5,474" fill="url(#intaglio-v)" stroke={ESPRESSO_INK} strokeWidth="0.7" />
                <path d="M 586.6 432 L 587.9 474 L 590.5 474 L 589 432 Z" fill={GROUND_SHADOW} opacity="0.25" />
                <ellipse cx={583} cy={432} rx={7} ry={1.7} fill={SLATE_LIGHT} stroke={ESPRESSO_INK} strokeWidth="0.5" />
                <line x1={576.4} y1={453} x2={589.6} y2={453} stroke={ESPRESSO_INK} strokeWidth="0.4" strokeOpacity="0.6" />
                <text x={583} y={460} textAnchor="middle" fontFamily="Inter, sans-serif" fontSize={4.5} fontWeight="700" fill={ESPRESSO_INK}>3/3</text>
                {[
                  [577.0, 474.98, 1],
                  [579.6, 475.5, 2],
                  [583.0, 475.7, 3],
                  [586.4, 475.5, 4],
                  [589.0, 474.98, 5],
                ].map(([bx, by, n]) => (
                  <g key={`s3-fit-${n}`} className={`login-bolt login-boltset-3 login-bolt-${n}`}>
                    <circle cx={bx} cy={by} r={0.85} fill={SLATE_DARK} stroke={ESPRESSO_INK} strokeWidth="0.3" />
                    <line x1={bx - 0.55} y1={by} x2={bx + 0.55} y2={by} stroke={IVORY_PAPER} strokeWidth="0.3" />
                  </g>
                ))}
              </g>

              {/* — flying nacelle (phase 4): long spreader slings — */}
              <g className="login-grip-load login-grip-nac" opacity={0}>
                <line x1={581} y1={428} x2={574.5} y2={460} stroke={ESPRESSO_INK} strokeWidth="0.6" />
                <line x1={585} y1={428} x2={591.5} y2={460} stroke={ESPRESSO_INK} strokeWidth="0.6" />
                <rect x={572} y={460} width={22} height={8} rx={2} fill="url(#intaglio-h)" stroke={ESPRESSO_INK} strokeWidth="0.7" />
                <line x1={573.5} y1={461.6} x2={592.5} y2={461.6} stroke={CANDLELIGHT} strokeWidth="0.6" strokeOpacity="0.8" />
                <circle cx={571.5} cy={464} r={2.6} fill={SLATE_DARK} stroke={ESPRESSO_INK} strokeWidth="0.6" />
                <circle cx={571.5} cy={464} r={0.8} fill={ESPRESSO_INK} />
              </g>
            </g>
          </g>
        </g>
        {/* === FOREGROUND — bay buildings fill the empty corners; the right
            one sits below the auth card's bottom edge so it stays visible === */}
        <BayBuilding x={8} y={HORIZON + 168} scale={1.2} bays={3} />
        <BayBuilding x={688} y={HORIZON + 176} scale={1.3} bays={3} />

        {/* Signature blade on cradles */}
        <BladeOnCradles x={250} y={HORIZON + 88} scale={1.1} />

        {/* Clouds + birds + dust motes */}
        <g className="login-cloud-drift"><Cloud x={350} y={128} scale={0.65} opacity={0.85} /></g>
        <g className="login-cloud-drift-2"><Cloud x={520} y={90} scale={0.55} opacity={0.7} /></g>
        <g className="login-cloud-drift-3"><Cloud x={760} y={200} scale={0.5} opacity={0.6} /></g>
        <Bird x={180} y={210} scale={0.85} className="login-bird" />
        <Bird x={260} y={245} scale={0.75} className="login-bird-2" />
        <DustMotes />

        {/* SORIGIN wordmark — exact logo letterforms rebuilt from engraved
            micro-lines in the brand greens (currency-note style) */}
        <SoriginEngravedLogo x={52} y={170} width={172} />
      </svg>
    </div>
  );
}
